import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { digitalApi as api } from './api';
import { calculatePlanReach, computeEffectiveFrequency, MAX_FREQ_DEFAULT } from './reach';
import { C } from '../../theme';
import { overrideKeyToFieldKey } from './CatalogueTable';
import PlanCatalogueView from './PlanCatalogueView';
import './dpe.css';
import { Lock, Unlock, Users, Globe, Building2, ClipboardList, Settings, TrendingUp, Plane, Calendar, Upload, FolderOpen, Zap, Target, BarChart2, AlignLeft, Table2, Activity, Info, AlertTriangle, Download } from 'lucide-react';

const YEAR=2026, API='/api';
const MN=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTHS=['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
export const DEF_UNIVERSE=1887226; // Moldova All Adults 18+ (GfK/Nielsen)
const FLIGHT_COLORS=['#4472C4','#ED7D31','#70AD47','#FF0066','#7030A0','#00B0F0','#FFC000','#C00000','#1B5E8C','#44546A','#9C27B0','#009688'];
// smartAllocate's platform-count rule (redesigned 2026-10-03 — see CLAUDE.md "smartAllocate maxP
// cliff"): the smallest k whose real calcFlight outcome is within this fraction (relative) of the
// best outcome across every k=1..N wins — replaces the old static budget-tier maxP lookup, which
// could cross a budget-tier boundary and admit a pricier platform that LOWERED total reach despite
// spending more (confirmed: 44.4%→41.8% vRCH from a single €1 increment at the €20,000 boundary).
const SMARTALLOCATE_OUTCOME_TOLERANCE=0.01;
// Guardrails on the k search itself (added 2026-10-03, same diagnosis as above): the external
// reach/click formulas in calcFlight scale ~linearly with budget with no real per-platform
// saturation below their asymptotic caps, so an unconstrained outcome-driven search can
// legitimately conclude "put it all in one platform" is optimal. MAX_PLATFORM_SHARE keeps any
// single line from absorbing the whole budget regardless of what the math says is "best", and
// MIN_PLATFORM_BUDGET keeps the search from fragmenting a small budget across lines too thin to
// be real buys. Real impression caps (effectiveImpressionCap) are independent and still apply
// on top of this.
const MAX_PLATFORM_SHARE=0.40;
const MIN_PLATFORM_BUDGET=500;

// ─── DATE UTILS ───────────────────────────────────────────────────────────────
// ISO 8601 week functions — weeks start Monday, W1 contains first Thursday of year
// All arithmetic in UTC to avoid timezone shifts
function _utcDate(y,m,d){return new Date(Date.UTC(y,m,d));}
function _utcDow(d){return(d.getUTCDay()+6)%7;} // Mon=0..Sun=6
function dateToWeek(s){
  if(!s)return null;
  const [y,m,d]=s.split('-').map(Number);
  const dt=_utcDate(y,m-1,d);
  // Shift to nearest Thursday
  const thu=new Date(dt);thu.setUTCDate(dt.getUTCDate()+3-_utcDow(dt));
  // W1 = week containing Jan 4
  const w1=_utcDate(thu.getUTCFullYear(),0,4);
  const w1thu=new Date(w1);w1thu.setUTCDate(w1.getUTCDate()+3-_utcDow(w1));
  const wn=1+Math.round((thu-w1thu)/604800000);
  return Math.max(1,Math.min(53,wn));
}
// ISO week-year of a date string — the calendar year of that date's "nearest Thursday"
// (can differ from the date's own literal year, e.g. 2027-01-01 belongs to ISO week-year 2026).
function isoWeekYear(s){
  const [y,m,d]=s.split('-').map(Number);
  const dt=_utcDate(y,m-1,d);
  const thu=new Date(dt);thu.setUTCDate(dt.getUTCDate()+3-_utcDow(dt));
  return thu.getUTCFullYear();
}
// year defaults to the module-level YEAR constant so every pre-existing call site that
// doesn't have a specific flight's real date in scope (the Flowchart's own fixed 52-column
// grid, MONTH_WEEKS init, etc.) keeps behaving exactly as before. Call sites that DO have a
// real flight/week year available should pass it explicitly — see calcFlight.
function weekStartDate(w,year=YEAR){
  // W1 Mon = Jan 4 minus its weekday offset
  const jan4=_utcDate(year,0,4);
  const w1mon=new Date(jan4);w1mon.setUTCDate(jan4.getUTCDate()-_utcDow(jan4));
  const mon=new Date(w1mon);mon.setUTCDate(w1mon.getUTCDate()+(w-1)*7);
  return mon.toISOString().slice(0,10);
}
function weekEndDate(w,year=YEAR){
  const s=weekStartDate(w,year);const [y,m,d]=s.split('-').map(Number);
  return _utcDate(y,m-1,d+6).toISOString().slice(0,10);
}
function daysInWeek(w,s,e,year=YEAR){const wS=new Date(weekStartDate(w,year)),wE=new Date(weekEndDate(w,year)),fS=new Date(s),fE=new Date(e);const a=fS>wS?fS:wS,b=fE<wE?fE:wE;return Math.max(0,Math.min(7,Math.floor((b-a)/86400000)+1));}
function weekMonth(w,year=YEAR){const d=new Date(weekStartDate(w,year));return d.getMonth();}
function fdate(s){if(!s)return'—';const d=new Date(s);return d.getDate()+' '+MN[d.getMonth()];}
// ISO 8601: most years have 52 weeks, but a year has 53 whenever Jan 1 falls on a
// Thursday (or, in a leap year, a Wednesday) — e.g. 2020, 2026, 2032, roughly every
// 5-6 years, a real standard calendar property, not an edge case to special-case away.
// Dec 28 always falls in a year's final ISO week (ISO weeks are Mon-Sun and Dec 28 is
// always within December's last 4 days), so that week's number IS the year's total
// week count. Grid sizing below derives from this per displayYear rather than assuming
// a fixed 52 (or a fixed 53), so both cases render correctly for whichever year is
// actually selected.
function weeksInYear(year=YEAR){return dateToWeek(`${year}-12-28`);}
// year-parameterized so callers with a real displayYear in scope (AnnualFlowchart,
// Details' monthly rollup) can compute the correct month/week grouping for that year —
// week-number-to-month boundaries can shift by a week depending on which day Jan 4
// falls on, so this isn't purely cosmetic. MONTH_WEEKS below stays the 2026 default for
// every pre-existing call site that has no specific year in scope (unchanged behavior,
// though 2026 itself has 53 weeks, so MONTH_WEEKS now correctly includes week 53 under
// December for any such call site too — a correctness improvement, not a new gap).
function monthWeeksFor(year){const mw=Array(12).fill(0).map(()=>[]);const n=weeksInYear(year);for(let w=1;w<=n;w++)mw[weekMonth(w,year)].push(w);return mw;}
const MONTH_WEEKS=monthWeeksFor(YEAR);

const OBJECTIVES=[{id:'awareness',label:'Awareness',color:'#4472C4'},{id:'consideration',label:'Consideration',color:'#ED7D31'},{id:'traffic',label:'Traffic',color:'#70AD47'},{id:'conversion',label:'Conversion',color:'#FF0066'}];
const AD_TYPES=[{key:'video',label:'Video',color:'#4472C4'},{key:'banner',label:'Banner',color:'#70AD47'},{key:'branding',label:'Branding',color:'#ED7D31'},{key:'nonstandard',label:'Non-Standard',color:'#FF0066'},{key:'social',label:'Social',color:'#7030A0'},{key:'sem',label:'SEM',color:'#00B0F0'}];
const SUMMARY_GROUPS=[{key:'video',label:'Video',types:['video']},{key:'display_std',label:'Display Standard',types:['banner']},{key:'display_ns',label:'Display Non-Standard',types:['branding','nonstandard']},{key:'social',label:'Social',types:['social']},{key:'sem',label:'Search',types:['sem']}];

// ─── DEFAULT PLATFORMS ────────────────────────────────────────────────────────
const DEFAULT_PLATFORMS=[
  {id:'dv360_yt',        seller:'DV 360',     label:'DV 360 YouTube',           type:'video',      base_cpm:1.10,viewability:0.85,audience_comp:0.75, ctr:0.0010,vtr:0.70,weekly_frq:3, monthly_frq:12,real_users_coef:1.8,reach_pool:'yt',     priority:{awareness:10,views:10,traffic:6,conversion:3}},
  {id:'eskimi_video',    seller:'Eskimi',     label:'Eskimi In-Stream',         type:'video',      base_cpm:3.06,viewability:0.75,audience_comp:0.614,ctr:0.0012,vtr:0.70,weekly_frq:4, monthly_frq:16,real_users_coef:2.1,reach_pool:'video',  priority:{awareness:9, views:10,traffic:5,conversion:2}},
  {id:'teads_video',     seller:'Teads',      label:'Teads Out-Stream',         type:'video',      base_cpm:2.90,viewability:0.75,audience_comp:0.724,ctr:0.0008,vtr:0.70,weekly_frq:4, monthly_frq:16,real_users_coef:2.0,reach_pool:'video',  priority:{awareness:9, views:10,traffic:4,conversion:2}},
  {id:'dv360_video',     seller:'DV 360',     label:'DV 360 Out-Stream',        type:'video',      base_cpm:0.60,viewability:0.70,audience_comp:0.65, ctr:0.0008,vtr:0.50,weekly_frq:5, monthly_frq:20,real_users_coef:2.5,reach_pool:'video',  priority:{awareness:7, views:7, traffic:4,conversion:2}},
  {id:'megogo_video',    seller:'Megogo',     label:'Megogo In-Stream',         type:'video',      base_cpm:0,   viewability:0.90,audience_comp:0.70, ctr:0.0010,vtr:0.80,weekly_frq:3, monthly_frq:12,real_users_coef:1.6,reach_pool:'video',  priority:{awareness:8, views:9, traffic:4,conversion:2}},
  {id:'simpals_video',   seller:'Simpals',    label:'Simpals In-Stream',        type:'video',      base_cpm:0,   viewability:0.75,audience_comp:0.65, ctr:0.0010,vtr:0.65,weekly_frq:4, monthly_frq:16,real_users_coef:2.0,reach_pool:'video',  priority:{awareness:6, views:7, traffic:3,conversion:1}},
  {id:'dv360_display',   seller:'DV 360',     label:'DV 360 Display',           type:'banner',     base_cpm:0.40,viewability:0.70,audience_comp:0.64, ctr:0.0008,vtr:null,weekly_frq:7, monthly_frq:28,real_users_coef:3.5,reach_pool:'display',priority:{awareness:6, views:1, traffic:8,conversion:5}},
  {id:'eskimi_display',  seller:'Eskimi',     label:'Eskimi Display',           type:'banner',     base_cpm:0.66,viewability:0.70,audience_comp:0.65, ctr:0.0008,vtr:null,weekly_frq:7, monthly_frq:28,real_users_coef:3.2,reach_pool:'display',priority:{awareness:5, views:1, traffic:7,conversion:4}},
  {id:'simpals_display', seller:'Simpals',    label:'Simpals Display',          type:'banner',     base_cpm:0.45,viewability:0.65,audience_comp:0.65, ctr:0.0008,vtr:null,weekly_frq:7, monthly_frq:28,real_users_coef:2.8,reach_pool:'display',priority:{awareness:5, views:1, traffic:7,conversion:4}},
  {id:'meteofor_display',seller:'Meteofor.md',label:'Meteofor.md Display',      type:'banner',     base_cpm:0.58,viewability:0.65,audience_comp:0.60, ctr:0.0008,vtr:null,weekly_frq:6, monthly_frq:24,real_users_coef:2.5,reach_pool:'display',priority:{awareness:4, views:1, traffic:6,conversion:3}},
  {id:'simpals_bill',    seller:'Simpals',    label:'Simpals Billboard',        type:'banner',     base_cpm:0.87,viewability:0.65,audience_comp:0.65, ctr:0.0010,vtr:null,weekly_frq:6, monthly_frq:24,real_users_coef:2.5,reach_pool:'display',priority:{awareness:5, views:1, traffic:7,conversion:4}},
  {id:'simpals_skin',    seller:'Simpals',    label:'Simpals Fluid Skin',       type:'branding',   base_cpm:3.80,viewability:0.75,audience_comp:0.65, ctr:0.0030,vtr:null,weekly_frq:4, monthly_frq:16,real_users_coef:2.0,reach_pool:'display',priority:{awareness:8, views:1, traffic:5,conversion:2}},
  {id:'meteofor_brand',  seller:'Meteofor.md',label:'Meteofor.md Branding',     type:'branding',   base_cpm:1.67,viewability:0.95,audience_comp:0.664,ctr:0.0025,vtr:null,weekly_frq:3, monthly_frq:12,real_users_coef:1.5,reach_pool:'display',priority:{awareness:8, views:1, traffic:5,conversion:2}},
  {id:'eskimi_rm',       seller:'Eskimi',     label:'Eskimi Rich Media',        type:'nonstandard',base_cpm:0.66,viewability:0.70,audience_comp:0.70, ctr:0.0010,vtr:null,weekly_frq:5, monthly_frq:20,real_users_coef:2.8,reach_pool:'display',priority:{awareness:7, views:1, traffic:6,conversion:3}},
  {id:'simpals_rm',      seller:'Simpals',    label:'Simpals Rich Media',       type:'nonstandard',base_cpm:0.45,viewability:0.65,audience_comp:0.738,ctr:0.0007,vtr:null,weekly_frq:5, monthly_frq:20,real_users_coef:2.5,reach_pool:'display',priority:{awareness:7, views:1, traffic:6,conversion:3}},
  {id:'teads_rm',        seller:'Teads',      label:'Teads Rich Media',         type:'nonstandard',base_cpm:2.90,viewability:0.75,audience_comp:0.72, ctr:0.0008,vtr:null,weekly_frq:5, monthly_frq:20,real_users_coef:2.2,reach_pool:'display',priority:{awareness:6, views:1, traffic:5,conversion:3}},
  {id:'meta_multi',      seller:'Meta',       label:'Meta (FB+IG) Multiformat', type:'social',     base_cpm:0.90,viewability:0.40,audience_comp:0.75, ctr:0.0015,vtr:null,weekly_frq:10,monthly_frq:40,real_users_coef:1.2,reach_pool:'social', priority:{awareness:8, views:3, traffic:9,conversion:9}},
  {id:'ig_reels',        seller:'Instagram',  label:'Instagram Reels',          type:'social',     base_cpm:0.90,viewability:0.40,audience_comp:0.65, ctr:0.0020,vtr:null,weekly_frq:7, monthly_frq:28,real_users_coef:1.2,reach_pool:'social', priority:{awareness:7, views:4, traffic:8,conversion:8}},
  {id:'fb_feed',         seller:'Facebook',   label:'Facebook Feed',            type:'social',     base_cpm:0.90,viewability:0.40,audience_comp:0.70, ctr:0.0015,vtr:null,weekly_frq:8, monthly_frq:32,real_users_coef:1.3,reach_pool:'social', priority:{awareness:7, views:3, traffic:8,conversion:8}},
  {id:'linkedin',        seller:'LinkedIn',   label:'LinkedIn Newsfeed',        type:'social',     base_cpm:0,   viewability:0.50,audience_comp:0.40, ctr:0.0010,vtr:null,weekly_frq:5, monthly_frq:20,real_users_coef:1.5,reach_pool:'social', priority:{awareness:4, views:1, traffic:6,conversion:5}},
  {id:'gads_search',     seller:'Google Ads', label:'Google Ads Search',        type:'sem',        base_cpm:null,viewability:1.00,audience_comp:0.80, ctr:0.0200,vtr:null,weekly_frq:null,monthly_frq:null,real_users_coef:1.0,reach_pool:'display',priority:{awareness:1,views:1,traffic:10,conversion:10},cpc:0.20},
  {id:'gads_perfmax',    seller:'Google Ads', label:'Google Ads Perf. Max',     type:'sem',        base_cpm:null,viewability:1.00,audience_comp:0.75, ctr:0.0100,vtr:null,weekly_frq:null,monthly_frq:null,real_users_coef:1.0,reach_pool:'display',priority:{awareness:2,views:1,traffic:9,conversion:9},cpc:0.15},
];

// Default audiences — now with per-platform audience_comp overrides
const DEFAULT_AUDIENCES=[
  {id:'aud_all',   name:'All Adults 18+',        universe:1887226, platComp:{}},
  {id:'aud_women', name:'Women | Urban | 18-45',  universe:280000, platComp:{dv360_yt:0.72,eskimi_video:0.58,meta_multi:0.78,ig_reels:0.70,fb_feed:0.72}},
  {id:'aud_young', name:'All | 18-35',            universe:310000, platComp:{dv360_yt:0.68,meta_multi:0.72,ig_reels:0.72,fb_feed:0.65}},
  {id:'aud_men',   name:'Men | 18-54',            universe:251000, platComp:{dv360_yt:0.78,eskimi_video:0.65,meta_multi:0.71,ig_reels:0.58}},
];
// platComp = {platformId: audience_comp_override} — specific to this audience

// ─── REACH / CALC ─────────────────────────────────────────────────────────────
// Consideration objective: eligibility/ranking keyed on format_group, not type.
// format_group==='video' -> ranked by VTR%; every other format_group -> ranked by
// Engagement Rate. A platform with no real value for its applicable metric is not
// eligible at all (excluded, not deprioritized) — shared by smartAllocate and the
// Summary-tab display so both agree on which metric drove the decision.
function considerationMetric(p){
  const isVideo=(p.format_group||'')==='video';
  const val=isVideo?p.vtr:p.engagement_rate;
  return{isVideo,val,eligible:val!=null&&val>0};
}
// Effective impression cap for a platform over a flight's real duration (`weeks`).
// Weekly/monthly caps are the only volume limits (max_impressions retired 2026-10-04 — see
// CLAUDE.md — it had zero real data at any layer and duplicated what weekly/monthly already
// cover). Whichever of weekly_impression_cap/monthly_impression_cap is TIGHTER, once both are
// expressed as a weekly-equivalent rate (monthly/4, same "monthly = weekly×4" project convention
// checkVolumeCap uses), scaled to the flight's actual week count. No caps set -> null (blank cap
// means no cap) — never silently treated as zero.
function effectiveImpressionCap(p,weeks){
  const weeklyCap=p.weekly_impression_cap||null,monthlyCap=p.monthly_impression_cap||null;
  if(!weeklyCap&&!monthlyCap)return null;
  const maxWeekly=Math.min(weeklyCap||Infinity,monthlyCap?monthlyCap/4:Infinity);
  return maxWeekly*Math.max(weeks||1,1);
}
// Fills each ranked platform's share up to its own effective cap; whatever a capped platform
// can't absorb cascades to the next platform in `rankedPool` not already in the mix (which may
// reach beyond the chosen top-k, mirroring the old capByInventory's behavior of pulling overflow
// extras from the full `avail` pool, not just the winning top-N). Replaces the old
// capByInventory(shRaw,top,budget,avail,maxP,o) — same role (a post-processing pass over the
// already-decided mix), new source of truth for what "capped" means.
function applyImpressionCaps(shRaw,rankedPool,budget,weeks){
  let overflow=0;
  let mix=shRaw.map(s=>{
    const plat=rankedPool.find(p=>p.id===s.platformId);
    const cap=plat?effectiveImpressionCap(plat,weeks):null;
    if(!plat||cap==null||!plat.base_cpm||plat.base_cpm<=0)return s;
    const impliedBud=(s.shr/100)*budget;
    const impliedImpr=(impliedBud/plat.base_cpm)*1000;
    if(impliedImpr>cap){
      const maxBud=(cap/1000)*plat.base_cpm;
      const maxShr=Math.max(0,Math.floor(maxBud/budget*100));
      overflow+=s.shr-maxShr;
      return{...s,shr:maxShr};
    }
    return s;
  });
  if(overflow>0){
    const used=new Set(mix.map(s=>s.platformId));
    for(const p of rankedPool){
      if(overflow<=0)break;
      if(used.has(p.id))continue;
      const cap=effectiveImpressionCap(p,weeks);
      const maxShr=(cap!=null&&p.base_cpm>0)?Math.floor(((cap/1000)*p.base_cpm)/budget*100):overflow;
      const add=Math.min(overflow,Math.max(0,maxShr));
      if(add<=0)continue;
      mix.push({platformId:p.id,shr:add,discount:0,frq_override:null});
      used.add(p.id);
      overflow-=add;
    }
    if(overflow>0&&mix.length>0)mix[0].shr+=overflow; // nowhere left to put it — same last-resort fallback as before
  }
  const sum=mix.reduce((s,x)=>s+x.shr,0);if(sum!==100&&mix.length>0)mix[0].shr+=(100-sum);
  return mix;
}
// Caps any single line at MAX_PLATFORM_SHARE (40%) of the budget; the excess spills to the next
// ranked platform not yet at its own 40% ceiling — same cascade shape as applyImpressionCaps
// (can reach beyond the k platforms already in the mix). Applied to every candidate mix the
// k-sweep evaluates, so the outcome driving k's choice already reflects the real, share-capped
// allocation — not an unconstrained one the final mix would then have to deviate from.
function applyMaxShare(shRaw,rankedPool){
  const maxShr=Math.floor(MAX_PLATFORM_SHARE*100);
  let overflow=0;
  let mix=shRaw.map(s=>{
    if(s.shr>maxShr){overflow+=s.shr-maxShr;return{...s,shr:maxShr};}
    return s;
  });
  if(overflow>0){
    for(const p of rankedPool){
      if(overflow<=0)break;
      const existing=mix.find(s=>s.platformId===p.id);
      if(existing){
        const room=maxShr-existing.shr;
        if(room<=0)continue;
        const add=Math.min(overflow,room);
        existing.shr+=add;overflow-=add;
        continue;
      }
      const add=Math.min(overflow,maxShr);
      if(add<=0)continue;
      mix.push({platformId:p.id,shr:add,discount:0,frq_override:null});
      overflow-=add;
    }
    if(overflow>0&&mix.length>0)mix[0].shr+=overflow; // nowhere left to put it under the cap — same last-resort fallback as caps
  }
  const sum=mix.reduce((s,x)=>s+x.shr,0);if(sum!==100&&mix.length>0)mix[0].shr+=(100-sum);
  return mix;
}
// Evaluates one candidate mix through the REAL calcFlight path (agency fee + tracker costs
// included), returning the single scalar the k-sweep compares: vRCH% for Awareness/Consideration,
// clicks for Traffic/Conversion (calcFlight computes no `conversions` field, so Conversion falls
// back to clicks exactly as the brief specifies).
function evalMixOutcome(mix,o,budget,cat,ctx){
  if(!mix.length)return 0;
  const fl={budget,objective:o,platforms:mix,startDate:ctx.startDate,endDate:ctx.endDate,startWeek:ctx.startWeek,weeks:ctx.weeks,weeklyOverrides:{},agency_fee:ctx.agencyFee,agency_on_top:ctx.agencyOnTop,trackerSettings:ctx.trackerSettings};
  const res=calcFlight(fl,cat,ctx.universe,ctx.audience,ctx.sitesMap,ctx.siteOverlaps,ctx.trackersCatalogue,ctx.trackerEligibility);
  return(o==='awareness'||o==='consideration')?res.tot.reach:res.tot.clicks;
}
// For k in the allowed range, builds the mix via `buildMixForK` (the objective's own existing
// distribution rule, just parameterized by k instead of the old fixed maxP), clips it to
// MAX_PLATFORM_SHARE, evaluates it, and returns the mix for the SMALLEST allowed k within
// SMARTALLOCATE_OUTCOME_TOLERANCE (relative) of the best outcome seen across the allowed range —
// never an average, never just "where it plateaus". The allowed range excludes any k whose
// naive equal split would need a line over 40% (k < ceil(1/MAX_PLATFORM_SHARE)) and any k whose
// average line would fall under MIN_PLATFORM_BUDGET (k*MIN_PLATFORM_BUDGET>budget). If the two
// bounds conflict (a budget too small for both at once), the largest k the budget can afford
// wins and the degenerate case is logged, not silently ignored.
function sweepBestK(buildMixForK,maxK,o,budget,cat,ctx,rankedPool){
  if(maxK<=0)return[];
  const kMinShare=Math.ceil(1/MAX_PLATFORM_SHARE);
  const kMaxBudget=Math.floor(budget/MIN_PLATFORM_BUDGET);
  let kLo=Math.min(kMinShare,maxK),kHi=Math.min(maxK,kMaxBudget);
  if(kLo>kHi){
    console.warn(`smartAllocate: budget €${budget} too small to satisfy both MAX_PLATFORM_SHARE (needs k>=${kMinShare}) and MIN_PLATFORM_BUDGET (allows k<=${kMaxBudget}) — using the largest budget-affordable k=${Math.max(1,kHi)}.`);
    kLo=kHi=Math.max(1,kHi);
  }
  const evaluated=[];
  let best=-Infinity;
  for(let k=kLo;k<=kHi;k++){
    const mix=applyMaxShare(buildMixForK(k),rankedPool);
    const val=evalMixOutcome(mix,o,budget,cat,ctx);
    evaluated.push({k,mix,val});
    if(val>best)best=val;
  }
  const threshold=best*(1-SMARTALLOCATE_OUTCOME_TOLERANCE);
  const chosen=evaluated.find(e=>e.val>=threshold)||evaluated[evaluated.length-1];
  return chosen.mix;
}
function smartAllocate(obj,budget,cat,sitesMap,ctxIn){
  const c=cat||DEFAULT_PLATFORMS,o=obj||'awareness';
  sitesMap=sitesMap||{};
  function cpru(p){const cpm=p.base_cpm||0;if(cpm<=0)return Infinity;return cpm/((p.viewability||0.7)*(p.audience_comp||0.65)*(p.real_users_coef||2.0));}
  // Effective cost-per-click — Traffic-only ranking metric (2026-10-04). SEM uses its own cpc
  // (already client-override-resolved by the catalogue merge — p.cpc here is never the raw global
  // value when a client override exists); display/video/social derive an implied CPC from
  // CPM÷(1000×CTR). A platform with no real CTR (or, for non-SEM, no real CPM) can't produce a
  // real cost-per-click — ranks last (Infinity), not excluded.
  function effCpc(p){
    if(p.type==='sem')return p.cpc>0?p.cpc:Infinity;
    if(!p.ctr||p.ctr<=0)return Infinity;
    if(!p.base_cpm||p.base_cpm<=0)return Infinity;
    return p.base_cpm/(1000*p.ctr);
  }

  // Publisher branch: RON semantics — device weight from site data, then site weight within device.
  // Confirmed: this branch never reads maxP (no top-N slicing — exactly one platform per
  // site+device pair via a CPRU/considerationMetric reduce), so the maxP redesign below does not
  // apply here and this branch is intentionally left untouched.
  const pubPlats=c.filter(p=>p.active!==false&&p.site_id&&sitesMap[p.site_id]&&(p.base_cpm>0||(p.cpc&&p.cpc>0)));
  if(pubPlats.length){
    const DKEYS={desktop:'monthly_real_users_desktop',mobile:'monthly_real_users_mobile',app:'monthly_real_users_app'};
    const DEVS=['desktop','mobile','app'];
    const siteIds=[...new Set(pubPlats.map(p=>p.site_id))];
    const devPool={};
    DEVS.forEach(d=>{devPool[d]=siteIds.reduce((s,sid)=>s+((sitesMap[sid]&&sitesMap[sid][DKEYS[d]])||0),0);});
    const activeDev=DEVS.filter(d=>devPool[d]>0&&pubPlats.some(p=>p.device===d));
    if(activeDev.length){
      const totalPool=activeDev.reduce((s,d)=>s+devPool[d],0)||1;
      const rawAllocs=[];
      activeDev.forEach(dev=>{
        const devBudget=budget*(devPool[dev]/totalPool);
        const devKey=DKEYS[dev];
        const sitePools=siteIds.map(sid=>({sid,pool:(sitesMap[sid]&&sitesMap[sid][devKey])||0})).filter(s=>s.pool>0);
        const totalSitePool=sitePools.reduce((s,si)=>s+si.pool,0)||1;
        sitePools.forEach(({sid,pool})=>{
          const candidates=pubPlats.filter(p=>p.site_id===sid&&p.device===dev);
          if(!candidates.length)return;
          let eligible,byConsideration=false;
          if(o==='consideration'){
            eligible=candidates.filter(p=>considerationMetric(p).eligible);
            byConsideration=true;
            if(!eligible.length)return; // no eligible format here — excluded, not a fallback to all candidates
          }
          else if(o==='awareness'){const banners=candidates.filter(p=>p.type==='banner');eligible=banners.length?banners:candidates;}
          else if(o==='conversion'){eligible=candidates;}
          else{const click=candidates.filter(p=>p.type!=='video');eligible=click.length?click:candidates;}
          const chosen=byConsideration
            ?eligible.reduce((best,p)=>considerationMetric(p).val>considerationMetric(best).val?p:best)
            :eligible.reduce((best,p)=>cpru(p)<cpru(best)?p:best);
          rawAllocs.push({platformId:chosen.id,bud:devBudget*(pool/totalSitePool)});
        });
      });
      if(rawAllocs.length){
        const totBud=rawAllocs.reduce((s,r)=>s+r.bud,0)||1;
        const result=rawAllocs.map(r=>({platformId:r.platformId,shr:Math.round(r.bud/totBud*100),discount:0,frq_override:null}));
        const sum=result.reduce((s,x)=>s+x.shr,0);if(sum!==100&&result.length)result[0].shr+=(100-sum);
        return result;
      }
    }
  }

  // External / non-publisher path.
  const avail=c.filter(p=>p.active!==false&&(p.base_cpm>0||(p.cpc&&p.cpc>0))).sort((a,b)=>(b.priority[o]||0)-(a.priority[o]||0));
  if(!avail.length)return[];
  // SEM (type==='sem') is only a real funnel fit for Traffic (search intent) and Conversion
  // (bottom-funnel retargeting mix below) — Awareness and Consideration exclude it structurally
  // (2026-10-04), not just incidentally via the nonSem/considerationMetric derivations that used
  // to be the only thing keeping it out (a real risk once SEM platforms can carry a real `cpc` and
  // so start passing the `avail` filter above). A SEM platform with no CPC still fails `avail`
  // itself, same as before this change.
  const nonSemAvail=avail.filter(p=>p.type!=='sem');

  // Context needed to run the REAL calcFlight path per candidate k (agency fee + tracker costs
  // included, per spec). Every field has a safe default so call sites that can't supply full
  // context (a brand-new flight has no trackerSettings yet, etc.) still get a correct — not just
  // approximate — evaluation; see call-site comments for which fields each site can really supply.
  const ctx=ctxIn||{};
  const weeks=Math.max(ctx.weeks||4,1);
  const startDate=ctx.startDate||weekStartDate(Math.max(1,Math.min(49,dateToWeek(new Date().toISOString().slice(0,10))||1)));
  const startWeek=dateToWeek(startDate)||1;
  const endDate=ctx.endDate||weekEndDate(startWeek+weeks-1);
  const fullCtx={
    weeks,startDate,endDate,startWeek,
    universe:ctx.universe||DEF_UNIVERSE,
    audience:ctx.audience||null,
    sitesMap,
    siteOverlaps:ctx.siteOverlaps||[],
    trackersCatalogue:ctx.trackersCatalogue||[],
    trackerEligibility:ctx.trackerEligibility||[],
    agencyFee:ctx.agencyFee||0,
    agencyOnTop:ctx.agencyOnTop||false,
    trackerSettings:ctx.trackerSettings||{},
  };

  if(o==='awareness'){
    const nonSem=nonSemAvail.filter(p=>(p.viewability||0)>=0.40);
    if(!nonSem.length){
      // Degenerate pool (no non-SEM platform meets the viewability floor) — fall back to the
      // same priority-ranked sweep the generic/Traffic branch used to share, still k-swept (not a
      // fixed slice). Uses nonSemAvail, not avail — SEM must stay excluded even in this fallback.
      const mix=sweepBestK(k=>buildPriorityMix(nonSemAvail,o,k),nonSemAvail.length,o,budget,c,fullCtx,nonSemAvail);
      return applyImpressionCaps(mix,nonSemAvail,budget,weeks);
    }
    const buildAwarenessMix=k=>{
      const nVideoSlots=Math.min(2,k);
      const videos=nonSem.filter(p=>p.type==='video').sort((a,b)=>cpru(a)-cpru(b)).slice(0,nVideoSlots);
      const nonVideo=nonSem.filter(p=>p.type!=='video').sort((a,b)=>cpru(a)-cpru(b)).slice(0,Math.max(0,k-videos.length));
      const top=[...videos,...nonVideo];
      const scores=top.map(p=>1/(cpru(p)||0.001));
      const videoIdxs=top.map((p,i)=>p.type==='video'?i:-1).filter(i=>i>=0);
      const totalScore=scores.reduce((s,v)=>s+v,0)||1;
      let rawShares=scores.map(s=>s/totalScore*100);
      const videoTotal=videoIdxs.reduce((s,i)=>s+rawShares[i],0);
      if(videoTotal>50){
        const scale=50/videoTotal;let freed=0;
        videoIdxs.forEach(i=>{const orig=rawShares[i];rawShares[i]=orig*scale;freed+=orig-rawShares[i];});
        const nonVideoIdxs=top.map((_,i)=>i).filter(i=>!videoIdxs.includes(i));
        const nvTotal=nonVideoIdxs.reduce((s,i)=>s+rawShares[i],0)||1;
        nonVideoIdxs.forEach(i=>{rawShares[i]+=freed*(rawShares[i]/nvTotal);});
      }
      const shRaw=top.map((p,i)=>({platformId:p.id,shr:Math.round(rawShares[i]),discount:0,frq_override:null}));
      const sum=shRaw.reduce((s,x)=>s+x.shr,0);if(sum!==100&&shRaw.length>0)shRaw[0].shr+=(100-sum);
      return shRaw;
    };
    const maxK=nonSem.length;
    const mix=sweepBestK(buildAwarenessMix,maxK,o,budget,c,fullCtx,nonSemAvail);
    return applyImpressionCaps(mix,nonSemAvail,budget,weeks);
  }

  if(o==='consideration'){
    // Eligibility + ranking keyed on format_group (see considerationMetric): video ->
    // VTR%, everything else -> Engagement Rate. No fallback to priority order — a
    // platform with neither real metric is excluded outright, not just deprioritized.
    // Also explicitly type-excludes SEM (nonSemAvail, not avail) — see the nonSemAvail note above.
    const eligible=nonSemAvail.filter(p=>considerationMetric(p).eligible).sort((a,b)=>considerationMetric(b).val-considerationMetric(a).val);
    if(!eligible.length)return[];
    const buildConsiderationMix=k=>{
      const top=eligible.slice(0,k);
      const scores=top.map(p=>considerationMetric(p).val);
      const totalScore=scores.reduce((s,v)=>s+v,0)||1;
      const shRaw=top.map((p,i)=>({platformId:p.id,shr:Math.round(scores[i]/totalScore*100),discount:0,frq_override:null}));
      const sum=shRaw.reduce((s,x)=>s+x.shr,0);if(sum!==100&&shRaw.length>0)shRaw[0].shr+=(100-sum);
      return shRaw;
    };
    const mix=sweepBestK(buildConsiderationMix,eligible.length,o,budget,c,fullCtx,eligible);
    return applyImpressionCaps(mix,eligible,budget,weeks);
  }

  if(o==='conversion'){
    // 2 SEM (intent) + 2 Social (retargeting/pixel) + 1 Video (brand-recall sequencing) once k is
    // large enough to afford it — slot quotas now scale with k instead of the old fixed maxP tiers,
    // same type-quota + within-type-CPRU logic, unchanged.
    const buildConversionMix=k=>{
      const nSem=k<=3?1:2, nVid=k>=6?2:1, nSoc=Math.max(0,k-nSem-nVid);
      const sem=avail.filter(p=>p.type==='sem').sort((a,b)=>cpru(a)-cpru(b)).slice(0,nSem);
      const soc=avail.filter(p=>p.type==='social').sort((a,b)=>cpru(a)-cpru(b)).slice(0,nSoc);
      const vid=avail.filter(p=>p.type==='video').sort((a,b)=>cpru(a)-cpru(b)).slice(0,nVid);
      let top=[...sem,...soc,...vid];
      if(top.length<k){const used=new Set(top.map(p=>p.id));top=[...top,...avail.filter(p=>!used.has(p.id)).slice(0,k-top.length)];}
      const tot=top.reduce((s,p)=>s+(p.priority[o]||0),0)||1;
      const shRaw=top.map(p=>({platformId:p.id,shr:Math.round((p.priority[o]||0)/tot*100),discount:0,frq_override:null}));
      const shSum=shRaw.reduce((s,x)=>s+x.shr,0);if(shSum!==100&&shRaw.length>0)shRaw[0].shr+=(100-shSum);
      return shRaw;
    };
    const mix=sweepBestK(buildConversionMix,avail.length,o,budget,c,fullCtx,avail);
    return applyImpressionCaps(mix,avail,budget,weeks);
  }

  if(o==='traffic'){
    // Ranked by lowest effective cost-per-click (effCpc), not the static priority number — a real
    // efficiency metric replacing the old priority-order-only ranking (2026-10-04). SEM is eligible
    // here (avail, not nonSemAvail): search intent is exactly what Traffic optimises for. Same
    // k-search/40%-share-cap/€500-min machinery as every other objective, just a different
    // ranking+weighting input.
    const ranked=[...avail].sort((a,b)=>effCpc(a)-effCpc(b));
    const buildTrafficMix=k=>{
      const top=ranked.slice(0,k);
      const scores=top.map(p=>{const e=effCpc(p);return(e>0&&isFinite(e))?1/e:0;});
      const totalScore=scores.reduce((s,v)=>s+v,0)||1;
      const shRaw=top.map((p,i)=>({platformId:p.id,shr:Math.round(scores[i]/totalScore*100),discount:0,frq_override:null}));
      const sum=shRaw.reduce((s,x)=>s+x.shr,0);if(sum!==100&&shRaw.length>0)shRaw[0].shr+=(100-sum);
      return shRaw;
    };
    const mix=sweepBestK(buildTrafficMix,ranked.length,o,budget,c,fullCtx,ranked);
    return applyImpressionCaps(mix,ranked,budget,weeks);
  }

  // Any other/unrecognized objective: unchanged priority-order ranking, now k-swept. No real
  // objective falls through here any more (awareness/consideration/traffic/conversion all have
  // their own branch above) — kept as a safety net for an unknown objective id.
  const mix=sweepBestK(k=>buildPriorityMix(avail,o,k),avail.length,o,budget,c,fullCtx,avail);
  return applyImpressionCaps(mix,avail,budget,weeks);
}
// Shared by the Traffic/default branch and the Awareness degenerate-pool fallback — identical
// priority-ranked top-k distribution, now parameterized by k instead of the old fixed maxP.
function buildPriorityMix(avail,o,k){
  const top=avail.slice(0,k);
  const tot=top.reduce((s,p)=>s+(p.priority[o]||0),0)||1;
  const shRaw=top.map(p=>({platformId:p.id,shr:Math.round((p.priority[o]||0)/tot*100),discount:0,frq_override:null}));
  const shSum=shRaw.reduce((s,x)=>s+x.shr,0);if(shSum!==100&&shRaw.length>0)shRaw[0].shr+=(100-shSum);
  return shRaw;
}
// ─── SOLVE FOR TARGET ────────────────────────────────────────────────────────
// Binary search: find minimum budget to hit a target metric for given objective
function solveForTarget(targetVal,metric,obj,cat,univ,audience,flTemplate,sitesMap,siteOverlaps){
  // metric: 'reach'(RCH fraction)|'views'|'clicks'|'conversions'
  // Returns {budget, platforms, result} or null
  if(!targetVal||targetVal<=0)return null;
  sitesMap=sitesMap||{};siteOverlaps=siteOverlaps||[];
  let lo=200,hi=2000000,best=null;
  for(let i=0;i<28;i++){
    const mid=Math.round((lo+hi)/2/100)*100;
    const plats=smartAllocate(obj,mid,cat,sitesMap,{weeks:flTemplate.weeks,startDate:flTemplate.startDate,endDate:flTemplate.endDate,universe:univ,audience,siteOverlaps,agencyFee:flTemplate.agency_fee,agencyOnTop:flTemplate.agency_on_top,trackerSettings:flTemplate.trackerSettings});
    const fl={...flTemplate,budget:mid,objective:obj,platforms:plats};
    const res=calcFlight(fl,cat,univ,audience,sitesMap,siteOverlaps);
    const val=metric==='reach'?res.tot.reach:metric==='views'?res.tot.views:metric==='clicks'?res.tot.clicks:res.tot.clicks*(fl.platforms.reduce((s,pe)=>{const p=cat.find(x=>x.id===pe.platformId);return s+(p?.cvr||0)*(pe.shr/100);},0)||0.02);
    if(val>=targetVal){best={budget:mid,platforms:plats,result:res,achievedVal:val};hi=mid;}
    else lo=mid;
    if(hi-lo<100)break;
  }
  return best;
}

function sb2(a,b){return a+b-a*b;}
function sbN(arr){return arr.reduce((a,b)=>sb2(a,b),0);}
// Folds a list of per-flight calcFlight results into one combined total the same way
// plan-level totals do — reused for any flight subset (a plan, or one group within it).
export function combineTot(resultList){
  const t={budget:0,impressions:0,vimpressions:0,views:0,clicks:0,reach:0};
  resultList.forEach(r=>{t.budget+=r.tot.budget;t.impressions+=r.tot.impressions;t.vimpressions+=r.tot.vimpressions;t.views+=r.tot.views;t.clicks+=r.tot.clicks;t.reach=sb2(t.reach,r.tot.reach);});
  return t;
}
function pReach(impr,view,ac,frq,ruc,univ){if(!impr||!frq||frq<=0)return 0;return Math.min(0.98,(impr*view*ac/(ruc||1)/frq)/univ);}
// Gemius-based pool reach: Sainsbury combination of per-platform real user reach
function poolReach(platforms,univ){
  if(!platforms.length||!univ)return 0;
  // Step 1: real users per platform = cookies / RUC × audComp
  // Step 2: per-platform reach = ru_i / universe
  // Step 3: Sainsbury combination across platforms in pool
  const reaches=platforms.map(p=>{
    const ru=(p.cookies||0)/(p.ruc||1)*(p.audComp||0.65);
    return Math.min(0.999,ru/univ);
  });
  return reaches.reduce((acc,r)=>acc+r-acc*r,0);
}
// Pool reach in absolutes (real users) using same Sainsbury logic × universe
function poolRealUsers(platforms,univ){
  return poolReach(platforms,univ)*univ;
}

export function calcFlight(fl,cat,univ,audience,sitesMap,siteOverlaps,trackersCatalogue,trackerEligibility){
  sitesMap=sitesMap||{};siteOverlaps=siteOverlaps||[];trackersCatalogue=trackersCatalogue||[];trackerEligibility=trackerEligibility||[];
  const c=cat||DEFAULT_PLATFORMS,u=univ||DEF_UNIVERSE;
  const empty={platforms:[],tot:{budget:0,impressions:0,vimpressions:0,views:0,clicks:0,reach:0,ecpm:0,vcpm:0,frequency:0,trackerCost:0,agencyFee:0},weekly:[],nWeeks:0,activeWeeks:[],startW:1,endW:1};
  if(!fl.platforms||!fl.platforms.length)return empty;
  const sd=fl.startDate||weekStartDate(fl.startWeek||1);
  const ed=fl.endDate||weekEndDate((fl.startWeek||1)+(fl.weeks||4)-1);
  const sw=dateToWeek(sd),ew=dateToWeek(ed);
  // Date-anchored walk, not a [sw,ew] week-number range: a flight can span an ISO
  // week-year boundary where bare week numbers aren't comparable (e.g. a 20-week flight
  // from week 40 of one year to week 7 of the next gives sw=40,ew=7 — "for(w=sw;w<=ew;w++)"
  // would silently iterate zero times). Walking forward by real 7-day steps from sd until
  // the cursor passes ed guarantees forward progress every iteration regardless of year
  // boundaries — same reasoning as DiagnosticPage.js's weeksBetween(), which avoids
  // subtracting two dateToWeek() results for exactly this reason. Each step's own (week,
  // year) pair is re-derived from its real date, so daysInWeek always compares against the
  // correct year's calendar — not a single hardcoded YEAR, and not even a single anchor
  // year for the whole flight (a flight can genuinely straddle two ISO week-years).
  const wf=[];
  {
    const sdYear=isoWeekYear(sd);
    let cursor=new Date(weekStartDate(sw,sdYear)+'T00:00:00Z');
    const edLimit=new Date(ed+'T00:00:00Z');
    let guard=0;
    while(cursor<=edLimit&&guard<600){
      guard++;
      const cursorStr=cursor.toISOString().slice(0,10);
      const w=dateToWeek(cursorStr);
      const wYear=isoWeekYear(cursorStr);
      const frac=daysInWeek(w,sd,ed,wYear)/7;
      if(frac>0.14)wf.push({week:w,year:wYear,frac});
      cursor=new Date(cursor);cursor.setUTCDate(cursor.getUTCDate()+7);
    }
  }
  const totFrac=wf.reduce((s,x)=>s+x.frac,0)||1;
  const tb=fl.budget||0;
  // Resolve weekly budgets respecting weeklyOverrides
  // Overridden weeks get their fixed budget; remaining budget distributed proportionally to free weeks
  const wOvr=fl.weeklyOverrides||{};
  const fixedTotal=wf.reduce((s,_,i)=>s+(wOvr[i]?.budget!=null?wOvr[i].budget:0),0);
  const freeFrac=wf.reduce((s,x,i)=>s+(wOvr[i]?.budget!=null?0:x.frac),0)||1;
  const freePool=Math.max(0,tb-fixedTotal);
  const wkBudgets=wf.map((x,i)=>wOvr[i]?.budget!=null?wOvr[i].budget:(x.frac/freeFrac)*freePool);
  const res=(fl.platforms||[]).map(pe=>{
    const p=c.find(x=>x.id===pe.platformId);if(!p)return null;
    // Per-audience audience_comp override
    const audComp=audience?.platComp?.[p._db_id??p.id]??p.audience_comp??0.65;
    const bud=tb*((pe.shr||0)/100),disc=Math.max(0,Math.min(0.90,pe.discount||0));
    // Catalogue-driven trackers: fl.trackerSettings holds one master on/off toggle per tracker
    // for the whole flight, plus a per-tracker excludedPlatformIds exception list. A tracker
    // applies to this platform-line only if its master toggle is on, this platform is eligible
    // per the admin matrix, AND this platform isn't in that tracker's exclusion list — see
    // activeTrackersForLine, the single shared resolver (also used by the UI and the Gemius hook).
    // Each active tracker contributes its resolved (cascade-merged) CPM as a flat add-on cost —
    // the same mechanical shape as the old free-text cpmTrackers term, just sourced from real data.
    const tCpm=activeTrackersForLine(p._db_id,fl.trackerSettings,trackersCatalogue,trackerEligibility).reduce((s,t)=>s+(t.base_cpm||0),0);
    const af=(pe.agency_fee_override!=null?pe.agency_fee_override:(fl.agency_fee||0))/100;
    const netB=af>0&&!fl.agency_on_top?bud/(1+af):bud;
    const afAmt=af>0?(fl.agency_on_top?bud*af:bud-netB):0;
    const tCost=netB>0?(netB/(p.base_cpm||1))*tCpm:0;
    const mBud=netB-tCost;
    if(p.type==='sem'){
      const cpc=(pe.cpc_override||p.cpc||0.20)*(1-disc);
      const clicks=cpc>0?mBud/cpc:0,impr=p.ctr>0?clicks/p.ctr:0,frq=pe.frq_override!=null?pe.frq_override:1;
      return{p,pe,bud,mBud,tCost,afAmt,cpm:null,cpc,impr,vimpr:impr,views:0,clicks,reach:pReach(impr,1,audComp,frq,1,u),frq,audComp};
    }
    const flightMonth=new Date(sd+'T00:00:00Z').getUTCMonth();
    const seasonMult=(p.seasonal_cpm&&p.seasonal_cpm[flightMonth])||1;
    const cpm=(p.base_cpm||0)*(1-disc)*seasonMult;
    if(!cpm)return{p,pe,bud,mBud,tCost,afAmt,cpm:0,impr:0,vimpr:0,views:0,clicks:0,reach:0,frq:0,audComp};
    const impr=(mBud/cpm)*1000,vimpr=impr*(p.viewability||0.7);
    const views=p.vtr?impr*p.vtr:0,clicks=impr*(p.ctr||0);
    const frq=pe.frq_override!=null?pe.frq_override:(p.weekly_frq||3);
    const nWks=wf.length||1;const uniqueCookies=frq>0?(impr/nWks)/frq:0; // weekly unique cookies = (total_impr/nWeeks)/frq
    const reach=pReach(impr,p.viewability||0.7,audComp,frq,p.real_users_coef||2.0,u);
    return{p,pe,bud,mBud,tCost,afAmt,cpm,impr,vimpr,views,clicks,reach,frq,audComp,uniqueCookies};
  }).filter(Boolean);
  // Site-aware reach: routes site-bound platforms through 4-tier hierarchy, external through legacy Sainsbury
  const reachEntries=res.map(r=>({
    platformId:r.p.id,siteId:r.p.site_id||null,device:r.p.device||null,
    overlapGroup:r.p.format_overlap_group||null,reach_pool:r.p.reach_pool||'other',
    cookies:((r.impr&&r.frq>0)?(r.impr/(wf.length||1))/r.frq:0)*(r.p.viewability||0.7),
    ruc:r.p.real_users_coef||2.0,audComp:r.audComp||r.p.audience_comp||0.65,
    viewability:r.p.viewability||0.7,
  }));
  const wkRes_reach=calculatePlanReach({entries:reachEntries,sitesMap,siteOverlaps,universe:u});
  const weeklyPubReach=wkRes_reach.publisher;
  const weeklyExtReach=wkRes_reach.external;
  const pubCap=wkRes_reach.publisherCap;
  const nWksTotal=wf.length||1;
  let cumPub=0,cumExt=0;
  for(let i=0;i<nWksTotal;i++){cumPub=cumPub+weeklyPubReach-cumPub*weeklyPubReach;cumExt=cumExt+weeklyExtReach-cumExt*weeklyExtReach;}
  if(pubCap!=null)cumPub=Math.min(cumPub,pubCap);
  const reach=Math.min(0.98,sb2(cumPub,cumExt));
  const tot={budget:res.reduce((s,r)=>s+r.bud,0),impressions:res.reduce((s,r)=>s+r.impr,0),vimpressions:res.reduce((s,r)=>s+r.vimpr,0),views:res.reduce((s,r)=>s+r.views,0),clicks:res.reduce((s,r)=>s+r.clicks,0),trackerCost:res.reduce((s,r)=>s+r.tCost,0),agencyFee:res.reduce((s,r)=>s+r.afAmt,0),reach};
  tot.ecpm=tot.impressions>0?(tot.budget/tot.impressions)*1000:0;
  tot.vcpm=tot.vimpressions>0?(tot.budget/tot.vimpressions)*1000:0;
  tot.frequency=res.reduce((s,r)=>s+r.frq*(r.impr/(tot.impressions||1)),0);
  tot.uniqueCookies=res.reduce((s,r)=>s+(r.uniqueCookies||0),0);
  tot.taRealUsers=res.reduce((s,r)=>s+(r.uniqueCookies||0)/(r.p.real_users_coef||2.0)*(r.audComp||r.p.audience_comp||0.65),0);
  const weekly=wf.map((x,i)=>{
    const wb=wkBudgets[i];
    // Use per-week platform mix override if set, otherwise use flight-level mix
    const wkPlatforms=wOvr[i]?.platforms||null;
    // Build effective platform entries for this week
    const wkRes=wkPlatforms
      ? wkPlatforms.map(pe=>{
          const p=c.find(x=>x.id===pe.platformId);if(!p)return null;
          const audComp=audience?.platComp?.[p._db_id??p.id]??p.audience_comp??0.65;
          return{p,pe,frq:pe.frq_override!=null?pe.frq_override:(p.weekly_frq||3),audComp};
        }).filter(Boolean)
      : res;
    const wkEntries=wkRes.map(r=>{
      const platBud=wb*((r.pe.shr||0)/100);
      const disc=Math.max(0,Math.min(0.90,r.pe.discount||0));
      const cpm=(r.p.base_cpm||0)*(1-disc);
      const wi=cpm>0?(platBud/cpm)*1000:0;
      const wCookies=(wi&&r.frq>0)?wi/r.frq:0;
      return{platformId:r.p.id,siteId:r.p.site_id||null,device:r.p.device||null,
        overlapGroup:r.p.format_overlap_group||null,reach_pool:r.p.reach_pool||'other',
        cookies:wCookies*(r.p.viewability||0.7),
        ruc:r.p.real_users_coef||2.0,audComp:r.audComp||r.p.audience_comp||0.65,
        viewability:r.p.viewability||0.7};
    });
    const wkRch=calculatePlanReach({entries:wkEntries,sitesMap,siteOverlaps,universe:u});
    const wkPubReach=wkRch.publisher;const wkExtReach=wkRch.external;
    const wReach=wkRch.combined;
    const wImpr=wkRes.reduce((s,r)=>{const platBud=wb*((r.pe.shr||0)/100);const disc=Math.max(0,Math.min(0.90,r.pe.discount||0));const cpm=(r.p.base_cpm||0)*(1-disc);return s+(cpm>0?(platBud/cpm)*1000:0);},0);
    const wVimpr=wkRes.reduce((s,r)=>{const platBud=wb*((r.pe.shr||0)/100);const disc=Math.max(0,Math.min(0.90,r.pe.discount||0));const cpm=(r.p.base_cpm||0)*(1-disc);const wi=cpm>0?(platBud/cpm)*1000:0;return s+wi*(r.p.viewability||0.7);},0);
    const wViews=wkRes.reduce((s,r)=>{const platBud=wb*((r.pe.shr||0)/100);const disc=Math.max(0,Math.min(0.90,r.pe.discount||0));const cpm=(r.p.base_cpm||0)*(1-disc);const wi=cpm>0?(platBud/cpm)*1000:0;return s+(r.p.vtr?wi*r.p.vtr:0);},0);
    const wClicks=wkRes.reduce((s,r)=>{const platBud=wb*((r.pe.shr||0)/100);const disc=Math.max(0,Math.min(0.90,r.pe.discount||0));const cpm=(r.p.base_cpm||0)*(1-disc);const wi=cpm>0?(platBud/cpm)*1000:0;return s+wi*(r.p.ctr||0);},0);
    return{week:x.week,year:x.year,frac:x.frac,budget:wb,impressions:wImpr,vimpressions:wVimpr,views:wViews,clicks:wClicks,reach:wReach,publisherReach:wkPubReach,externalReach:wkExtReach,isOverridden:wOvr[i]?.budget!=null,hasMixOverride:!!wkPlatforms};
  });
  return{platforms:res,tot,weekly,wkBudgets,nWeeks:wf.length,activeWeeks:wf.map(x=>x.week),startW:sw,endW:ew,startDate:sd,endDate:ed,universe:u,publisherCap:pubCap};
}

// ─── FORMATTERS ───────────────────────────────────────────────────────────────
const fe  =n=>(!isFinite(n)||isNaN(n)||n===0)?'—':'€'+Math.round(n).toLocaleString();
const fe2 =n=>(!isFinite(n)||isNaN(n))?'—':'€'+n.toFixed(2);
const fmil=n=>{if(!n||!isFinite(n))return'—';if(n>=1e6)return(n/1e6).toFixed(1)+'M';if(n>=1000)return Math.round(n/1000)+'K';return Math.round(n)+''};
const fn  =n=>{if(!n||!isFinite(n))return'—';if(n>=1e6)return(n/1e6).toFixed(1)+'M';if(n>=1000)return(n/1000).toFixed(0)+'K';return Math.round(n).toLocaleString()+''};
const fp  =n=>(!isFinite(n)||isNaN(n))?'—':(n*100).toFixed(1)+'%';
const fp2 =n=>(!isFinite(n)||isNaN(n))?'—':(n*100).toFixed(2)+'%';
const ff  =n=>(!isFinite(n)||isNaN(n)||n===0)?'—':n.toFixed(1);

let _uid=1;function mkId(){return 'f'+(++_uid)+'_'+Date.now();}
function mkFlight(idx,brand,cat,sitesMap){const obj='awareness',budget=10000;const todayStr=new Date().toISOString().slice(0,10);const sw=Math.max(1,Math.min(49,dateToWeek(todayStr)||1));const startDate=weekStartDate(sw);const endDate=weekEndDate(sw+3);return{id:mkId(),label:'Flight '+(idx+1),brand:brand||'Activation 1',color:FLIGHT_COLORS[idx%FLIGHT_COLORS.length],startDate,endDate,startWeek:sw,weeks:4,budget,objective:obj,audienceId:null,platforms:smartAllocate(obj,budget,cat,sitesMap,{weeks:4,startDate,endDate}),weeklyOverrides:{},agency_fee:0,agency_on_top:false};}

const TH={padding:'4px 6px',color:'var(--text3)',fontWeight:600,fontSize:9,border:'1px solid var(--border)',textAlign:'center',whiteSpace:'nowrap',background:'var(--gold-bg)'};
const TD={padding:'3px 6px',border:'1px solid var(--border)',verticalAlign:'middle'};
const TDr={...TD,textAlign:'right'};
const TDc={...TD,textAlign:'center'};

// ─── BRAND HEADER ─────────────────────────────────────────────────────────────
function BrandHeader({brand,onRename,onAddFlight,groupTot}){
  const[editing,setEditing]=useState(false),[val,setVal]=useState(brand);
  function commit(){setEditing(false);onRename(val);}
  return(<div style={{padding:'6px 10px 2px',display:'flex',flexDirection:'column',gap:2,borderTop:'2px solid var(--border)'}}>
    <div style={{fontSize:9,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'.07em',display:'flex',alignItems:'center',gap:4}}>
      {editing?<input autoFocus value={val} className="inp" style={{fontSize:9,fontWeight:700,textTransform:'uppercase',flex:1,padding:'1px 4px'}} onChange={e=>setVal(e.target.value)} onBlur={commit} onKeyDown={e=>{if(e.key==='Enter')commit();if(e.key==='Escape'){setVal(brand);setEditing(false);}}}/>
        :<span style={{flex:1,cursor:'pointer'}} onClick={()=>{setVal(brand);setEditing(true);}}>{brand} ✎</span>}
      <button className="btn btn-xs btn-ghost" onClick={onAddFlight}>+ Flight</button>
    </div>
    {groupTot&&<div style={{fontSize:9,color:'var(--text3)',display:'flex',gap:8}}>
      <span>{fe(groupTot.budget)}</span>
      <span style={{color:'var(--gold-dark)',fontWeight:700}}>vRCH% {fp(groupTot.reach)}</span>
    </div>}
  </div>);
}

// ─── PLATFORM MIX TABLE ───────────────────────────────────────────────────────
// Volume-cap check for a proposed platform-line share change. Returns null if within caps
// (or no caps set on this platform), otherwise the info needed to drive the warning modal.
function checkVolumeCap(p,disc,tb,newShr,nWeeks){
  if(!p||(!p.weekly_impression_cap&&!p.monthly_impression_cap))return null;
  const cpm=p.base_cpm?p.base_cpm*(1-(disc||0)):0;
  if(!cpm||cpm<=0)return null;
  const bud=tb*(newShr/100);
  const totalImpr=(bud/cpm)*1000;
  const wk=Math.max(nWeeks||1,1);
  const estWeekly=totalImpr/wk;
  const estMonthly=estWeekly*4; // project convention: monthly = weekly × 4 (see CLAUDE.md frequency rule)
  const weeklyCap=p.weekly_impression_cap||null;
  const monthlyCap=p.monthly_impression_cap||null;
  let violated=null;
  if(weeklyCap&&estWeekly>weeklyCap)violated='weekly';
  else if(monthlyCap&&estMonthly>monthlyCap)violated='monthly';
  if(!violated)return null;
  // Max share that stays within BOTH caps (weekly cap and monthly cap expressed as weekly-equivalent)
  const maxWeeklyImpr=Math.min(weeklyCap||Infinity,monthlyCap?monthlyCap/4:Infinity);
  const maxTotalImpr=maxWeeklyImpr*wk;
  const maxBud=(maxTotalImpr/1000)*cpm;
  const maxShr=Math.max(0,Math.min(newShr,(maxBud/tb)*100));
  return{violated,capValue:violated==='weekly'?weeklyCap:monthlyCap,estValue:violated==='weekly'?estWeekly:estMonthly,maxShr};
}

function PlatformMixTable({fl,result,onUpdate,catalogue,mixLocked,setMixLocked,showAgencyFee,sitesMap,siteOverlaps,trackersCatalogue,trackerEligibility}){
  const[freeMode,setFreeMode]=useState(false); // false=auto-rebalance, true=free edit
  const[capWarning,setCapWarning]=useState(null); // {p, violated, capValue, estValue, apply(shr)}
  function rebal(pls,ci,nv){const a=pls.map((p,i)=>i===ci?{...p,shr:nv}:p),rest=a.filter((_,i)=>i!==ci),rs=rest.reduce((s,p)=>s+(p.shr||0),0)||1,rem=Math.max(0,100-nv);const rb=a.map((p,i)=>i===ci?p:{...p,shr:Math.max(1,Math.round((p.shr||0)/rs*rem))});const sum=rb.reduce((s,p)=>s+p.shr,0);if(sum!==100&&rb.length>1){const fi=rb.findIndex((_,i)=>i!==ci);if(fi>=0)rb[fi].shr+=(100-sum);}return rb;}
  function freeShr(pls,ci,nv){return pls.map((p,i)=>i===ci?{...p,shr:Math.max(0,nv)}:p);}
  function applyShrChange(pi,nv){
    const pe=fl.platforms[pi];
    const p=catalogue.find(x=>x.id===pe.platformId)||DEFAULT_PLATFORMS.find(x=>x.id===pe.platformId);
    const nWeeks=result?.nWeeks||1;
    const cap=checkVolumeCap(p,pe.discount,fl.budget,nv,nWeeks);
    const commit=shr=>onUpdate({platforms:freeMode?freeShr(fl.platforms,pi,shr):rebal(fl.platforms,pi,Math.max(0,Math.min(freeMode?999:100,shr)))});
    if(!cap){commit(nv);return;}
    setCapWarning({
      p,violated:cap.violated,capValue:cap.capValue,estValue:cap.estValue,
      onReduce:()=>{commit(cap.maxShr);setCapWarning(null);},
      onContinue:()=>{commit(nv);setCapWarning(null);},
    });
  }
  function removePl(pi){const pls=fl.platforms.filter((_,j)=>j!==pi);if(!pls.length){onUpdate({platforms:[]});return;}const t=pls.reduce((s,p)=>s+(p.shr||0),0)||1;const rb=pls.map(p=>({...p,shr:Math.round((p.shr||0)/t*100)}));const s=rb.reduce((s,p)=>s+p.shr,0);if(s!==100)rb[0].shr+=(100-s);onUpdate({platforms:rb});}
  // Trackers currently toggled ON for this flight — only these get a per-platform exception column.
  const onTrackers=(trackersCatalogue||[]).filter(t=>fl.trackerSettings?.[t.id]?.enabled);
  function toggleTrackerExclusion(trackerId,platformDbId,nowChecked){
    const cur=fl.trackerSettings?.[trackerId]||{enabled:false,excludedPlatformIds:[]};
    const excluded=cur.excludedPlatformIds||[];
    const nextExcluded=nowChecked?excluded.filter(id=>id!==platformDbId):[...excluded,platformDbId];
    onUpdate({trackerSettings:{...(fl.trackerSettings||{}),[trackerId]:{...cur,excludedPlatformIds:nextExcluded}}});
  }
  function addPl(id){if(fl.platforms.some(p=>p.platformId===id))return;const n=fl.platforms.length,pls=fl.platforms.map(p=>({...p,shr:Math.round((p.shr||0)*(n/(n+1)))}));const sum=pls.reduce((s,p)=>s+(p.shr||0),0);pls.push({platformId:id,shr:100-sum,discount:0,frq_override:null});onUpdate({platforms:pls});}
  const totalShr=(fl.platforms||[]).reduce((s,p)=>s+(p.shr||0),0);
  const shrOk=Math.abs(totalShr-100)<1;
  return(<div>
    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
      <div style={{display:'flex',alignItems:'center',gap:4,fontSize:9,background:freeMode?'var(--gold-bg)':'var(--surface3)',border:'1px solid '+(freeMode?'var(--gold-border)':'var(--border)'),borderRadius:12,padding:'2px 8px',cursor:'pointer'}} onClick={()=>setFreeMode(f=>!f)}>
        <span style={{fontWeight:700,color:freeMode?'var(--gold-dark)':'var(--text3)'}}>{freeMode?'✎ Free edit':'⟳ Auto-balance'}</span>
        <span style={{fontSize:8,color:'var(--text3)'}}>{freeMode?'(shares independent)':'(others rebalance)'}</span>
      </div>
      {setMixLocked&&<div title={mixLocked?'Mix locked — budget changes keep current shares':'Mix unlocked — budget changes recalculate mix'} onClick={()=>setMixLocked(l=>!l)} style={{display:'flex',alignItems:'center',gap:3,fontSize:9,padding:'2px 8px',borderRadius:12,border:'1px solid '+(mixLocked?'var(--red)':'var(--border)'),background:mixLocked?'#ff000015':'var(--surface3)',color:mixLocked?'var(--red)':'var(--text3)',cursor:'pointer',fontWeight:mixLocked?700:400}}>
        {mixLocked?<><Lock size={11} style={{verticalAlign:'middle',marginRight:3}}/>Mix locked</>:<><Unlock size={11} style={{verticalAlign:'middle',marginRight:3}}/>Mix unlocked</>}
      </div>}
      <div style={{fontSize:10,fontWeight:700,color:shrOk?'var(--text3)':totalShr>100?'var(--red)':'var(--amber)',marginLeft:4}}>
        Total: {totalShr}%{!shrOk&&<span style={{fontWeight:400,fontSize:9,marginLeft:4}}>{totalShr>100?'⚠ Over 100%':'⚠ Under 100% — remaining budget unallocated'}</span>}
      </div>
    </div>
    <div style={{overflowX:'auto'}}><table style={{width:'100%',borderCollapse:'collapse',fontSize:11,minWidth:480}}>
      <thead><tr>
        <th style={{...TH,textAlign:'left'}}>Platform</th>
        <th style={{...TH,width:54}}>Shr%</th><th style={{...TH,width:58}}>CPM €</th>
        <th style={{...TH,width:46}}>Disc%</th><th style={{...TH,width:52}}>Avg FRQ/wk</th>{showAgencyFee&&<th style={{...TH,width:36}} title='Agency fee % per platform'>AF%</th>}
        {onTrackers.map(t=><th key={t.id} style={{...TH,width:70}} title={t.name+' — checked = included'}>{t.name}</th>)}
        <th style={{...TH,width:64}}>Budget</th><th style={{...TH,width:52}}>Impr.</th><th style={{...TH,width:18}}/>
      </tr></thead>
      <tbody>{(()=>{
        // Deduplicate: remove entries where platformId resolves to same label as an earlier entry
        const seen=new Set();
        return (fl.platforms||[]).filter(pe=>{
          const p=catalogue.find(x=>x.id===pe.platformId)||DEFAULT_PLATFORMS.find(x=>x.id===pe.platformId);
          if(!p)return false;
          const key=p.label.toLowerCase().trim();
          if(seen.has(key))return false;
          seen.add(key);return true;
        });
      })().map((pe,pi)=>{
        const p=catalogue.find(x=>x.id===pe.platformId)||DEFAULT_PLATFORMS.find(x=>x.id===pe.platformId);if(!p)return null;
        const pr=result?.platforms?.find(r=>r.p?.id===pe.platformId);
        const typ=AD_TYPES.find(t=>t.key===p.type);
        const eff=p.base_cpm?p.base_cpm*(1-(pe.discount||0)):null;
        const frqV=pe.frq_override!=null?pe.frq_override:(p.weekly_frq||3);
        const freqCapWarn=(()=>{
          if(!p.site_id||!sitesMap)return null;
          const site=sitesMap[p.site_id];if(!site)return null;
          const nWeeks=result?.nWeeks||1;
          const cookies=pr&&pr.frq>0?(pr.impr/nWeeks)/pr.frq*(p.viewability||0.7):0;
          const effFreq=computeEffectiveFrequency({cookies,frq:pr?.frq||frqV,ruc:p.real_users_coef||2.0,audComp:pr?.audComp||p.audience_comp||0.65,device:p.device,site});
          const cap=p.max_weekly_frequency??MAX_FREQ_DEFAULT;
          return effFreq!==null&&effFreq>cap?{eff:effFreq,cap}:null;
        })();
        return(<tr key={pe.platformId}>
          <td style={TD}><div style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:7,height:7,borderRadius:'50%',background:typ?.color||'#ccc',flexShrink:0,display:'inline-block'}}/><span style={{fontSize:10,fontWeight:500}}>{p.label}</span></div>{freqCapWarn&&<div style={{fontSize:10,color:'#d08000',marginTop:1}}>⚠ Freq {freqCapWarn.eff.toFixed(1)}/wk (cap {freqCapWarn.cap})</div>}</td>
          <td style={TDc}><input className="mono-inp" type="number" min={0} max={freeMode?999:100} value={pe.shr||0} style={{width:'100%',textAlign:'center',background:freeMode?'var(--gold-bg)':'transparent'}} onFocus={e=>e.target.select()} onChange={e=>applyShrChange(pi,Math.max(0,freeMode?+e.target.value:Math.min(100,+e.target.value)))}/></td>
          <td style={{...TDc,color:'var(--gold-dark)',fontWeight:600,fontSize:10}}>{p.type==='sem'?'CPC':eff?eff.toFixed(2):'manual'}</td>
          <td style={TDc}><input className="mono-inp" type="number" min={0} max={90} step={5} value={Math.round((pe.discount||0)*100)} style={{width:'100%',textAlign:'center'}} onChange={e=>onUpdate({platforms:fl.platforms.map((p2,j)=>j!==pi?p2:{...p2,discount:+e.target.value/100})})}/></td>
          <td style={TDc}>{p.type==='sem'?<span style={{color:'var(--text3)',fontSize:9}}>—</span>:<input className="mono-inp" type="number" min={1} max={30} step={0.5} value={frqV} style={{width:'100%',textAlign:'center',background:pe.frq_override!=null?'#FBF7F2':'transparent'}} title={pe.frq_override!=null?'Override':'Default'} onFocus={e=>e.target.select()} onChange={e=>onUpdate({platforms:fl.platforms.map((p2,j)=>j!==pi?p2:{...p2,frq_override:+e.target.value})})}/>}</td>
          {showAgencyFee&&<td style={{...TDc,minWidth:40}} title="Agency fee % for this platform (blank=flight default)"><input className="mono-inp" type="number" inputMode="decimal" step={0.1} min={0} max={100} placeholder={String(fl.agency_fee||0)} key={'af_'+pe.platformId} defaultValue={pe.agency_fee_override!=null?pe.agency_fee_override:''} style={{width:'100%',textAlign:'center',background:pe.agency_fee_override!=null?'var(--gold-bg)':'transparent',fontSize:9}} onFocus={e=>e.target.select()} onBlur={e=>onUpdate({platforms:fl.platforms.map((p2,j)=>j!==pi?p2:{...p2,agency_fee_override:e.target.value===''?null:parseFloat(e.target.value)??null})})}/></td>}
          {onTrackers.map(t=>{
            const eligible=isTrackerEligible(p._db_id,t.id,trackerEligibility);
            if(!eligible)return <td key={t.id} style={{...TDc,color:'var(--text3)'}}>—</td>;
            const excluded=(fl.trackerSettings?.[t.id]?.excludedPlatformIds||[]).includes(p._db_id);
            const checked=!excluded; // default included — only exceptions are stored
            return(<td key={t.id} style={TDc}>
              <input type="checkbox" checked={checked} onChange={e=>toggleTrackerExclusion(t.id,p._db_id,e.target.checked)}/>
            </td>);
          })}
          <td style={{...TDc,color:'var(--gold-dark)',fontWeight:600,fontSize:10}}>{fe(pr?.bud)}</td>
          <td style={{...TDc,fontSize:10}}>{fmil(pr?.impr)}</td>
          <td style={{...TDc,padding:2}}><button style={{color:'var(--red)',border:'none',background:'transparent',cursor:'pointer',padding:'0 4px',fontSize:14,fontWeight:700}} onClick={()=>removePl(pi)}>×</button></td>
        </tr>);
      })}</tbody>
    </table></div>
    <div style={{display:'flex',gap:6,marginTop:8}}>
      <select className="sel" style={{flex:1}} value="" onChange={e=>{if(e.target.value)addPl(e.target.value);e.target.value='';}}>
        <option value="">+ Add platform…</option>
        {AD_TYPES.map(t=>({type:t,plats:(()=>{const seen=new Set();return catalogue.filter(p=>p.type===t.key&&p.active!==false).filter(p=>{const k=p.label.toLowerCase().trim();if(seen.has(k))return false;seen.add(k);return true;});})()})).filter(({plats})=>plats.length>0).map(({type:t,plats})=>(<optgroup key={t.key} label={t.label}>{plats.map(p=>(<option key={p.id} value={p.id} disabled={fl.platforms.some(x=>x.platformId===p.id)}>{p.label}{p.base_cpm?' (€'+p.base_cpm+')':p.cpc?' (CPC €'+p.cpc+')':' (manual)'}</option>))}</optgroup>))}
      </select>
      <button className="btn btn-ghost btn-sm" onClick={()=>onUpdate({platforms:smartAllocate(fl.objective,fl.budget,catalogue,sitesMap,{weeks:result?.nWeeks||fl.weeks,startDate:fl.startDate,endDate:fl.endDate,universe:result?.universe,siteOverlaps,trackersCatalogue,trackerEligibility,agencyFee:fl.agency_fee,agencyOnTop:fl.agency_on_top,trackerSettings:fl.trackerSettings})})}>↺ Re-opt.</button>
    </div>
    {capWarning&&<div className="modal-overlay" onClick={ev=>{if(ev.target===ev.currentTarget)setCapWarning(null);}}>
      <div className="modal-box" style={{maxWidth:420}} onClick={e=>e.stopPropagation()}>
        <div className="modal-hdr">
          <span style={{fontWeight:700,fontSize:13,color:'var(--amber)',display:'inline-flex',alignItems:'center',gap:5}}><AlertTriangle size={13}/>Volume cap exceeded</span>
          <button className="btn btn-ghost btn-sm" onClick={()=>setCapWarning(null)}>✕</button>
        </div>
        <div style={{padding:'12px 16px',fontSize:12,lineHeight:1.5}}>
          This exceeds <b>{capWarning.p.label}</b>'s cap of <b>{capWarning.capValue.toLocaleString()} impressions/{capWarning.violated==='weekly'?'week':'month'}</b> — estimated at <b>{Math.round(capWarning.estValue).toLocaleString()}</b>. Reduce to the maximum available, or continue anyway?
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={()=>setCapWarning(null)}>Cancel</button>
          <button className="btn btn-secondary btn-sm" onClick={capWarning.onContinue}>Continue anyway</button>
          <button className="btn btn-primary btn-sm" onClick={capWarning.onReduce}>Reduce to maximum</button>
        </div>
      </div>
    </div>}
  </div>);
}

// ─── FLIGHT MODAL ─────────────────────────────────────────────────────────────
// ─── FLIGHT BUDGET / TARGET TOGGLE ──────────────────────────────────────────
function FlightBudgetTarget({fl,onUpdate,catalogue,result,universe,audiences,mixLocked,sitesMap,siteOverlaps,trackersCatalogue,trackerEligibility}){
  const[mode,setMode]=React.useState('budget'); // 'budget' | 'target'
  const[targetInput,setTargetInput]=React.useState('');
  const[solving,setSolving]=React.useState(false);
  const[solveResult,setSolveResult]=React.useState(null);

  const OBJ_META={
    awareness:{metric:'reach',label:'Target RCH%',unit:'%',min:1,max:95,step:1,toVal:v=>v/100,fromVal:v=>Math.round(v*100)},
    views:    {metric:'views',label:'Target Views',unit:'views',min:1000,max:10000000,step:1000,toVal:v=>v,fromVal:v=>Math.round(v)},
    traffic:  {metric:'clicks',label:'Target Clicks',unit:'clicks',min:100,max:1000000,step:100,toVal:v=>v,fromVal:v=>Math.round(v)},
    conversion:{metric:'conversions',label:'Target Conversions',unit:'conv.',min:10,max:100000,step:10,toVal:v=>v,fromVal:v=>Math.round(v)},
  };
  const meta=OBJ_META[fl.objective]||OBJ_META.awareness;
  const univ=universe?.universe||1887226;
  const audObj=universe||null;

  function runSolve(){
    const raw=parseFloat(targetInput);
    if(!raw||raw<=0)return;
    setSolving(true);setSolveResult(null);
    setTimeout(()=>{
      const targetVal=meta.toVal(raw);
      const sol=solveForTarget(targetVal,meta.metric,fl.objective,catalogue,univ,audObj,fl,sitesMap||{},siteOverlaps||[]);
      setSolving(false);
      if(sol){setSolveResult({budget:sol.budget,achievedVal:meta.fromVal(sol.achievedVal)});}
      else{setSolveResult({error:'Cannot reach target with available platforms'});}
    },30);
  }

  function applyResult(){
    if(!solveResult||solveResult.error)return;
    const plats=smartAllocate(fl.objective,solveResult.budget,catalogue,sitesMap,{weeks:fl.weeks,startDate:fl.startDate,endDate:fl.endDate,universe:univ,audience:audObj,siteOverlaps,trackersCatalogue,trackerEligibility,agencyFee:fl.agency_fee,agencyOnTop:fl.agency_on_top,trackerSettings:fl.trackerSettings});
    onUpdate({budget:solveResult.budget,platforms:plats});
    setSolveResult(null);setTargetInput('');setMode('budget');
  }

  if(mode==='budget') return(
    <div style={{marginBottom:12}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:4}}>
        <div className="lbl" style={{margin:0}}>Budget (EUR net media)</div>
        <button className="btn btn-ghost btn-xs" style={{fontSize:9,padding:'1px 6px',color:'var(--gold-dark)',border:'1px solid var(--gold-border)'}} onClick={()=>{setMode('target');setSolveResult(null);}}>⇄ Plan by {meta.label.replace('Target ','')}</button>
      </div>
      <input className="inn" type="text" inputMode="decimal" key={'bgt-'+fl.id} defaultValue={fl.budget||0} style={{width:'100%'}} onFocus={e=>{if(parseFloat(e.target.value)===0)e.target.select();}} onBlur={e=>{const val=parseFloat(e.target.value)||0;e.target.value=val;onUpdate(mixLocked?{budget:val}:{budget:val,platforms:smartAllocate(fl.objective,val,catalogue,sitesMap,{weeks:fl.weeks,startDate:fl.startDate,endDate:fl.endDate,universe:univ,audience:audObj,siteOverlaps,trackersCatalogue,trackerEligibility,agencyFee:fl.agency_fee,agencyOnTop:fl.agency_on_top,trackerSettings:fl.trackerSettings})});}}/>
      <input type="range" min={500} max={200000} step={500} value={fl.budget} style={{width:'100%',marginTop:4}} onChange={e=>onUpdate(mixLocked?{budget:+e.target.value}:{budget:+e.target.value,platforms:smartAllocate(fl.objective,+e.target.value,catalogue,sitesMap,{weeks:fl.weeks,startDate:fl.startDate,endDate:fl.endDate,universe:univ,audience:audObj,siteOverlaps,trackersCatalogue,trackerEligibility,agencyFee:fl.agency_fee,agencyOnTop:fl.agency_on_top,trackerSettings:fl.trackerSettings})})}/>
    </div>
  );

  return(
    <div style={{marginBottom:12,padding:'10px 12px',background:'var(--gold-bg)',border:'1px solid var(--gold-border)',borderRadius:6}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
        <span style={{fontWeight:700,fontSize:11,color:'var(--gold-dark)',display:'inline-flex',alignItems:'center',gap:4}}><Target size={11}/>  {meta.label}</span>
        <button className="btn btn-ghost btn-xs" style={{fontSize:9,padding:'1px 6px'}} onClick={()=>{setMode('budget');setSolveResult(null);}}>⇄ Plan by Budget</button>
      </div>
      <div style={{display:'flex',gap:6,alignItems:'center',marginBottom:8}}>
        <input className="inn" type="number" min={meta.min} max={meta.max} step={meta.step} value={targetInput} placeholder={meta.unit==='%'?'e.g. 30':'e.g. '+(meta.min*10)} style={{flex:1}} onChange={e=>setTargetInput(e.target.value)}
          onKeyDown={e=>{if(e.key==='Enter')runSolve();}}/>
        <span style={{fontSize:11,color:'var(--text3)',minWidth:30}}>{meta.unit}</span>
        <button className="btn btn-primary btn-sm" onClick={runSolve} disabled={solving||!targetInput} style={{whiteSpace:'nowrap'}}>{solving?'⏳ Solving…':'Calculate'}</button>
      </div>
      {solveResult&&!solveResult.error&&(
        <div style={{background:'var(--surface2)',borderRadius:6,padding:'8px 10px',border:'1px solid var(--gold-border)'}}>
          <div style={{fontSize:10,color:'var(--text3)',marginBottom:4}}>Optimal plan found:</div>
          <div style={{display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
            <div><span style={{fontSize:9,color:'var(--text3)'}}>Min. Budget </span><span style={{fontWeight:800,fontSize:13,color:'var(--gold-dark)'}}>€{solveResult.budget.toLocaleString()}</span></div>
            <div><span style={{fontSize:9,color:'var(--text3)'}}>Achieved </span><span style={{fontWeight:800,fontSize:13,color:'var(--gold-dark)'}}>{solveResult.achievedVal}{meta.unit}</span></div>
            <button className="btn btn-primary btn-sm" style={{marginLeft:'auto'}} onClick={applyResult}>Apply →</button>
          </div>
        </div>
      )}
      {solveResult?.error&&<div style={{fontSize:10,color:'var(--red)',padding:'6px 0'}}>{solveResult.error}</div>}
      <div style={{fontSize:9,color:'var(--text3)',marginTop:6}}>Current budget: <b>€{fl.budget.toLocaleString()}</b> · Current result: <b>{result?.tot?meta.fromVal(result.tot[meta.metric]==='reach'?result.tot.reach:result.tot[meta.metric]):0}{meta.unit}</b></div>
    </div>
  );
}

// ─── WEEKLY MIX MODAL ────────────────────────────────────────────────────────
function WeeklyMixModal({weekIdx,fl,result,onUpdate,catalogue,onClose}){
  const rw=result?.weekly?.[weekIdx];
  const wkOvr=fl.weeklyOverrides?.[weekIdx]||{};
  const basePlatforms=wkOvr.platforms||fl.platforms;
  const[platforms,setPlatforms]=useState(()=>basePlatforms.map(p=>({...p})));
  const[freeMode,setFreeMode]=useState(false);

  function rebal(pls,ci,nv){const a=pls.map((p,i)=>i===ci?{...p,shr:nv}:p),rest=a.filter((_,i)=>i!==ci),rs=rest.reduce((s,p)=>s+(p.shr||0),0)||1,rem=Math.max(0,100-nv);const rb=a.map((p,i)=>i===ci?p:{...p,shr:Math.max(1,Math.round((p.shr||0)/rs*rem))});const sum=rb.reduce((s,p)=>s+p.shr,0);if(sum!==100&&rb.length>1){const fi=rb.findIndex((_,i)=>i!==ci);if(fi>=0)rb[fi].shr+=(100-sum);}return rb;}
  function freeShr(pls,ci,nv){return pls.map((p,i)=>i===ci?{...p,shr:Math.max(0,nv)}:p);}

  function apply(){
    onUpdate({weeklyOverrides:{...(fl.weeklyOverrides||{}),[weekIdx]:{...wkOvr,platforms}}});
    onClose();
  }
  function reset(){
    const ovr={...(fl.weeklyOverrides||{})};
    if(ovr[weekIdx]){delete ovr[weekIdx].platforms;if(!ovr[weekIdx].budget)delete ovr[weekIdx];}
    onUpdate({weeklyOverrides:ovr});
    onClose();
  }

  const totalShr=platforms.reduce((s,p)=>s+(p.shr||0),0);
  const shrOk=Math.abs(totalShr-100)<1;
  const wb=rw?.budget||0;

  return(<div className="modal-overlay" onClick={ev=>{if(ev.target===ev.currentTarget)onClose();}}>
    <div className="modal-box" style={{maxWidth:520}} onClick={e=>e.stopPropagation()}>
      <div className="modal-hdr">
        <span style={{fontWeight:700,fontSize:13}}>✎ Platform Mix — W{rw?.week} {rw?'('+fdate(weekStartDate(rw.week))+')':''}</span>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
      </div>
      <div style={{padding:'12px 16px'}}>
        <div style={{fontSize:10,color:'var(--text3)',marginBottom:10}}>Week budget: <b>{fe(wb)}</b> · Override the platform split for this week only. Other weeks are unaffected.</div>
        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
          <div style={{display:'flex',alignItems:'center',gap:4,fontSize:9,background:freeMode?'var(--gold-bg)':'var(--surface3)',border:'1px solid '+(freeMode?'var(--gold-border)':'var(--border)'),borderRadius:12,padding:'2px 8px',cursor:'pointer'}} onClick={()=>setFreeMode(f=>!f)}>
            <span style={{fontWeight:700,color:freeMode?'var(--gold-dark)':'var(--text3)'}}>{freeMode?'✎ Free edit':'⟳ Auto-balance'}</span>
          </div>
          <div style={{fontSize:10,fontWeight:700,color:shrOk?'var(--text3)':totalShr>100?'var(--red)':'var(--amber)'}}>Total: {totalShr}%</div>
        </div>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
          <thead><tr><th style={TH}>Platform</th><th style={{...TH,width:60}}>Shr%</th><th style={{...TH,width:80}}>Budget €</th><th style={{...TH,width:60}}>Impr.</th><th style={{...TH,width:64}}>FRQ/wk</th></tr></thead>
          <tbody>{platforms.map((pe,pi)=>{
            const p=catalogue.find(x=>x.id===pe.platformId)||DEFAULT_PLATFORMS.find(x=>x.id===pe.platformId);if(!p)return null;
            const typ=AD_TYPES.find(t=>t.key===p.type);
            const platBud=wb*(pe.shr||0)/100;
            const disc=pe.discount||0;
            const cpm=(p.base_cpm||0)*(1-disc);
            const impr=cpm>0?(platBud/cpm)*1000:0;
            const frqVal=pe.frq_override!=null?pe.frq_override:(p.weekly_frq||3);
            const hasfrqOvr=pe.frq_override!=null;
            return(<tr key={pe.platformId}>
              <td style={TD}><div style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:6,height:6,borderRadius:'50%',background:typ?.color||'#ccc',display:'inline-block'}}/><span style={{fontSize:10}}>{p.label}</span></div></td>
              <td style={TDc}><input className="mono-inp" type="number" min={0} max={freeMode?999:100} value={pe.shr||0} style={{width:'100%',textAlign:'center',background:freeMode?'var(--gold-bg)':'transparent'}} onFocus={e=>e.target.select()} onChange={e=>setPlatforms(freeMode?freeShr(platforms,pi,+e.target.value):rebal(platforms,pi,Math.max(0,Math.min(100,+e.target.value))))}/></td>
              <td style={{...TDc,color:'var(--gold-dark)',fontSize:10,fontWeight:600}}>{fe(platBud)}</td>
              <td style={{...TDc,fontSize:10}}>{fmil(impr)}</td>
              <td style={TDc}>{p.type!=='sem'?<input className="mono-inp" type="number" min={1} max={30} step={0.5} value={frqVal} style={{width:'100%',textAlign:'center',background:hasfrqOvr?'var(--gold-bg)':'transparent',fontSize:10}} title={hasfrqOvr?'FRQ override':'Default FRQ'} onFocus={e=>e.target.select()} onChange={e=>setPlatforms(platforms.map((p2,j)=>j!==pi?p2:{...p2,frq_override:+e.target.value}))} onDoubleClick={()=>setPlatforms(platforms.map((p2,j)=>j!==pi?p2:{...p2,frq_override:null}))}/>:<span style={{color:'var(--text3)',fontSize:9}}>—</span>}</td>
            </tr>);
          })}</tbody>
        </table>
        <div style={{fontSize:9,color:'var(--text3)',marginTop:4,display:'flex',alignItems:'center',gap:4}}><Info size={9}/>Double-click FRQ to reset to platform default</div>
      </div>
      <div className="modal-footer">
        {wkOvr.platforms&&<button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={reset}>✕ Reset to flight default</button>}
        <div style={{flex:1}}/>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={apply}>Apply to W{rw?.week}</button>
      </div>
    </div>
  </div>);
}

// Pill-style on/off switch — for the flight-level tracker master toggle only (Step 2). Deliberately
// distinct from a checkbox at a glance: a sliding track+thumb, not a plain box/tick. The per-platform
// exception checkboxes (Step 3) stay real checkboxes per the confirmed design — don't reuse this there.
function TrackerSwitch({checked,onChange,title}){
  return(
    <button type="button" role="switch" aria-checked={checked} title={title} onClick={()=>onChange(!checked)} style={{
      width:32,height:18,borderRadius:999,border:'1px solid '+(checked?'var(--gold-border)':'var(--border)'),
      background:checked?'var(--gold)':'var(--surface3)',position:'relative',cursor:'pointer',padding:0,flexShrink:0,
      transition:'background .15s, border-color .15s',
    }}>
      <span style={{
        position:'absolute',top:1,left:checked?15:1,width:14,height:14,borderRadius:'50%',
        background:'#fff',boxShadow:'0 1px 2px rgba(0,0,0,0.25)',transition:'left .15s',
      }}/>
    </button>
  );
}

function FlightModal({fl,result,onClose,onUpdate,onClone,onRemove,canRemove,catalogue,audiences,showAgencyFee,sitesMap,siteOverlaps,trackersCatalogue,trackerEligibility}){
  const tot=result&&result.tot;
  const obj=OBJECTIVES.find(o=>o.id===fl.objective)||OBJECTIVES[0];
  const[wkMixModal,setWkMixModal]=useState(null);
  const[mixLocked,setMixLocked]=useState(false);
  const[mouseDownOnOverlay,setMouseDownOnOverlay]=useState(false);
  function setDates(s,e){
    // Guard: only process valid ISO dates (YYYY-MM-DD)
    if(!s||!e||!/^\d{4}-\d{2}-\d{2}$/.test(s)||!/^\d{4}-\d{2}-\d{2}$/.test(e))return;
    const sw=dateToWeek(s),ew=dateToWeek(e);
    const safeE=ew>=sw?e:weekEndDate(sw);
    const safeEw=ew>=sw?ew:sw;
    onUpdate({startDate:s,endDate:safeE,startWeek:sw,weeks:Math.max(1,safeEw-sw+1)});
  }
  return(<div className="modal-overlay" onMouseDown={ev=>{if(ev.target===ev.currentTarget)setMouseDownOnOverlay(true);}} onMouseUp={ev=>{if(ev.target===ev.currentTarget&&mouseDownOnOverlay)onClose();setMouseDownOnOverlay(false);}}>
    <div className="modal-box" onClick={e=>e.stopPropagation()}>
      <div className="modal-hdr">
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <div style={{width:10,height:10,borderRadius:'50%',background:fl.color,flexShrink:0}}/>
          <input value={fl.label} onChange={e=>onUpdate({label:e.target.value})} style={{fontWeight:700,fontSize:13,background:'transparent',border:'none',borderBottom:'1px solid var(--border)',outline:'none',color:'var(--text1)',width:160,padding:'0 2px'}} placeholder="Flight name"/>
          <span style={{fontSize:9,color:'var(--text3)'}}>{fl.brand}</span>
          <span style={{fontSize:9,padding:'2px 7px',borderRadius:8,background:obj.color+'22',color:obj.color,fontWeight:700}}>{obj.label}</span>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>✕ Close</button>
      </div>
      <div className="modal-body">
        <div className="modal-col modal-col-left">
          <div style={{marginBottom:12}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:6}}>
              <div>
                <div className="lbl">Start date</div>
                <input className="inp" type="date" key={'sd-'+fl.id} defaultValue={fl.startDate||''}
                  onBlur={e=>{var sd=e.target.value;if(!sd||sd.length!==10)return;var sw=dateToWeek(sd)||fl.startWeek;var ew=fl.endDate?dateToWeek(fl.endDate):null;var wks=ew&&ew>=sw?ew-sw+1:fl.weeks;onUpdate({startDate:sd,startWeek:sw,weeks:wks});}}/>
              </div>
              <div>
                <div className="lbl">End date</div>
                <input className="inp" type="date" key={'ed-'+fl.id} defaultValue={fl.endDate||''}
                  onBlur={e=>{var ed=e.target.value;if(!ed||ed.length!==10)return;var ew=dateToWeek(ed)||fl.startWeek+fl.weeks-1;var wks=Math.max(1,ew-fl.startWeek+1);onUpdate({endDate:ed,weeks:wks});}}/>
              </div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
              <div>
                <div className="lbl">Start week (override)</div>
                <input className="inn" type="number" min={1} max={52} value={fl.startWeek} style={{width:'100%'}} onChange={e=>onUpdate({startWeek:Math.min(52,Math.max(1,+e.target.value)),startDate:''})}/>
              </div>
              <div>
                <div className="lbl">Duration (weeks)</div>
                <input className="inn" type="number" min={1} max={52} value={fl.weeks} style={{width:'100%'}} onChange={e=>onUpdate({weeks:Math.min(52,Math.max(1,+e.target.value)),endDate:''})}/>
              </div>
            </div>
          </div>
          <div style={{marginBottom:12}}>
            <div className="lbl">Target Audience</div>
            <select className="sel" style={{width:'100%'}} value={fl.audienceId||''} onChange={e=>onUpdate({audienceId:e.target.value||null})}>
              <option value="">— plan default (All Adults 18+) —</option>
              {(audiences||[]).filter(a=>a._db_id!=null||a.scope==='plan').map(a=><option key={a.id} value={a.id}>{a.name} ({a.universe.toLocaleString()})</option>)}
            </select>
            {fl.audienceId&&audiences&&(()=>{const a=audiences.find(x=>x.id===fl.audienceId);const ovrCount=Object.keys(a?.platComp||{}).length;return ovrCount>0&&<div style={{fontSize:9,color:'var(--gold-dark)',marginTop:2,display:'flex',alignItems:'center',gap:3}}><Zap size={9}/>{ovrCount} platform audience_comp overrides active</div>;})()}
          </div>
          <div style={{marginBottom:12}}>
            <div className="lbl">Objective</div>
            <div style={{display:'flex',gap:3}}>{OBJECTIVES.map(o=>(<button key={o.id} className="btn btn-sm" onClick={()=>{const aud=fl.audienceId&&audiences?audiences.find(a=>a.id===fl.audienceId):null;onUpdate(mixLocked?{objective:o.id}:{objective:o.id,platforms:smartAllocate(o.id,fl.budget,catalogue,sitesMap,{weeks:fl.weeks,startDate:fl.startDate,endDate:fl.endDate,universe:aud?.universe,audience:aud,siteOverlaps,trackersCatalogue,trackerEligibility,agencyFee:fl.agency_fee,agencyOnTop:fl.agency_on_top,trackerSettings:fl.trackerSettings})});}} style={{flex:1,background:fl.objective===o.id?o.color:'#eef4f8',color:fl.objective===o.id?'#fff':'#4a6a8a',border:'1px solid '+(fl.objective===o.id?o.color:'#dce8f0'),fontSize:10}}>{o.label}</button>))}</div>
          </div>
          <FlightBudgetTarget fl={fl} onUpdate={onUpdate} catalogue={catalogue} result={result} universe={fl.audienceId&&audiences?audiences.find(a=>a.id===fl.audienceId):null} audiences={audiences} mixLocked={mixLocked} sitesMap={sitesMap} siteOverlaps={siteOverlaps} trackersCatalogue={trackersCatalogue} trackerEligibility={trackerEligibility}/>
          <div style={{marginBottom:12,padding:'10px 12px',background:'#f4f8fc',borderRadius:6,border:'1px solid #dce8f0'}}>
            {showAgencyFee&&<div style={{display:'flex',gap:8,alignItems:'center',marginBottom:8}}>
              <div style={{flex:1}}><div className="lbl">Agency fee %</div><input className="inn" type="text" inputMode="decimal" defaultValue={fl.agency_fee||0} key={'af_'+fl.id} style={{width:'100%'}} onFocus={e=>e.target.select()} onBlur={e=>{const v=parseFloat(e.target.value);onUpdate({agency_fee:isNaN(v)?0:v});}}/></div>
              <div style={{paddingTop:16}}><label style={{display:'flex',alignItems:'center',gap:4,fontSize:10,cursor:'pointer',whiteSpace:'nowrap'}}><input type="checkbox" checked={!!fl.agency_on_top} onChange={e=>onUpdate({agency_on_top:e.target.checked})}/>On top</label></div>
            </div>}
          </div>
          {(()=>{
            // Only list a tracker here if it's eligible for at least one platform currently in
            // this flight — no clutter for trackers with nothing relevant to toggle.
            const relevantTrackers=(trackersCatalogue||[]).filter(t=>fl.platforms.some(pe=>{
              const p=catalogue.find(x=>x.id===pe.platformId);
              return p&&isTrackerEligible(p._db_id,t.id,trackerEligibility);
            }));
            if(!relevantTrackers.length)return null;
            return(<div style={{marginBottom:12,padding:'10px 12px',background:'#f4f8fc',borderRadius:6,border:'1px solid #dce8f0'}}>
              <div className="lbl" style={{marginBottom:6}}>Tracking</div>
              {relevantTrackers.map(t=>{
                const enabled=!!fl.trackerSettings?.[t.id]?.enabled;
                return(<div key={t.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:4,fontSize:11}}>
                  <span>{t.name}</span>
                  <TrackerSwitch checked={enabled} title={enabled?'On for this flight':'Off for this flight'} onChange={next=>{
                    // Master toggle only ever touches `enabled` — excludedPlatformIds is untouched here.
                    const cur=fl.trackerSettings?.[t.id]||{enabled:false,excludedPlatformIds:[]};
                    onUpdate({trackerSettings:{...(fl.trackerSettings||{}),[t.id]:{...cur,enabled:next}}});
                  }}/>
                </div>);
              })}
            </div>);
          })()}
          {tot&&tot.impressions>0&&(<div style={{display:'flex',gap:4,flexWrap:'wrap',paddingTop:8,borderTop:'1px solid var(--border)'}}>
            {[{l:'Budget',v:fe(tot.budget)},{l:'Impr.',v:fmil(tot.impressions)},{l:'vImp.',v:fmil(tot.vimpressions)},{l:'eCPM',v:fe2(tot.ecpm)},{l:'vCPM',v:fe2(tot.vcpm)},{l:'vRCH%',v:fp(tot.reach),hi:true},{l:'Avg FRQ/wk',v:ff(tot.frequency)},...(tot.views>0?[{l:'Views',v:fmil(tot.views)}]:[]),{l:'Clicks',v:fmil(tot.clicks)},...(tot.agencyFee>0?[{l:'Agency',v:fe(tot.agencyFee)}]:[]),...(tot.trackerCost>0?[{l:'Trackers',v:fe(tot.trackerCost)}]:[])].map(k=>(
              <div key={k.l} style={{background:k.hi?'rgba(0,144,208,0.08)':'#eef4f8',border:'1px solid '+(k.hi?'rgba(0,144,208,0.3)':'#dce8f0'),borderRadius:6,padding:'4px 8px',minWidth:52,textAlign:'center'}}>
                <div style={{fontSize:9,color:'#8aa0b8',textTransform:'uppercase',letterSpacing:'.04em',marginBottom:1}}>{k.l}</div>
                <div style={{fontSize:12,fontWeight:800,color:k.hi?'#0060a0':'#1a2a3a'}}>{k.v}</div>
              </div>
            ))}
          </div>)}
        </div>
        <div className="modal-col modal-col-right">
          <div className="lbl" style={{marginBottom:6}}>Platform mix</div>
          <PlatformMixTable fl={fl} result={result} onUpdate={onUpdate} catalogue={catalogue} mixLocked={mixLocked} setMixLocked={setMixLocked} showAgencyFee={showAgencyFee} sitesMap={sitesMap||{}} siteOverlaps={siteOverlaps} trackersCatalogue={trackersCatalogue} trackerEligibility={trackerEligibility}/>
          {result&&result.nWeeks>0&&result.nWeeks<=26&&(<div style={{marginTop:16}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
              <div className="lbl" style={{marginBottom:0}}>Weekly distribution</div>
              <button className="btn btn-xs btn-ghost" onClick={()=>onUpdate({weeklyOverrides:{}})}>Reset all</button>
            </div>
            {wkMixModal!=null&&<WeeklyMixModal weekIdx={wkMixModal} fl={fl} result={result} onUpdate={onUpdate} catalogue={catalogue} onClose={()=>setWkMixModal(null)}/>}
            <div style={{overflowX:'auto'}}><table style={{width:'100%',borderCollapse:'collapse',fontSize:10,minWidth:380}}>
              <thead><tr style={{background:'var(--gold-bg)'}}>
                <th style={{...TH,textAlign:'left'}}>Period</th>
                <th style={{...TH,width:34}}>Days</th>
                <th style={{...TH,width:88}}>Budget €</th>
                <th style={{...TH,width:52}}>Impr.</th>
                <th style={{...TH,width:52}}>Clicks</th>
                <th style={{...TH,width:52}}>vRCH%</th>
                <th style={{...TH,width:28}} title="Weekly platform mix override">Mix</th>
              </tr></thead>
              <tbody>{(result.weekly||[]).map((rw,i)=>{
                const ov=fl.weeklyOverrides&&fl.weeklyOverrides[i];
                const isOvr=ov&&ov.budget!=null;
                const isRedist=!isOvr&&Object.keys(fl.weeklyOverrides||{}).length>0;
                const days=Math.round(rw.frac*7);
                return(<tr key={i} style={{background:isOvr?'var(--gold-bg)':isRedist?'var(--surface3)':'transparent'}}>
                  <td style={{...TD,fontSize:9,color:'var(--text3)',fontWeight:600,whiteSpace:'nowrap'}}>
                    {isOvr&&<span style={{color:'var(--gold-dark)',marginRight:3}}>●</span>}
                    {isRedist&&<span style={{color:'var(--text3)',marginRight:3,fontSize:8}}>↺</span>}
                    W{rw.week} · {fdate(weekStartDate(rw.week))}{days<7&&<span style={{color:'#E07000',marginLeft:3}}>({days}d)</span>}
                  </td>
                  <td style={{...TDc,fontSize:9,color:days<7?'#E07000':'var(--text3)'}}>{days}</td>
                  <td style={TDc}><input className="inn" type="number" min={0} step={100} value={Math.round(rw.budget)} style={{width:'100%',fontSize:10,background:isOvr?'var(--gold-bg)':'transparent',fontWeight:isOvr?700:400}}
                    onChange={e=>{const v=+e.target.value;onUpdate({weeklyOverrides:{...(fl.weeklyOverrides||{}),[i]:{...(fl.weeklyOverrides?.[i]||{}),budget:v}}});}}
                    onBlur={e=>{const v=+e.target.value;if(v===0)onUpdate({weeklyOverrides:{...(fl.weeklyOverrides||{}),[i]:{...(fl.weeklyOverrides?.[i]||{}),budget:undefined}}});}}
                  /></td>
                  <td style={{...TDc,fontSize:9,color:isOvr?'var(--gold-dark)':'var(--text2)',fontWeight:isOvr?700:400}}>{fmil(rw.impressions)}</td>
                  <td style={{...TDc,fontSize:9,color:isOvr?'var(--gold-dark)':'var(--text2)',fontWeight:isOvr?700:400}}>{fmil(rw.clicks)}</td>
                  <td style={{...TDc,fontSize:10,color:'var(--gold-dark)',fontWeight:700}}>{fp(rw.reach)}</td>
                  <td style={{...TDc,width:28}}>
                    <button title="Override platform mix for this week" onClick={()=>setWkMixModal(i)} style={{fontSize:11,background:rw.hasMixOverride?'var(--gold-dark)':'transparent',color:rw.hasMixOverride?'#fff':'var(--text3)',border:'1px solid '+(rw.hasMixOverride?'var(--gold-dark)':'var(--border)'),borderRadius:4,cursor:'pointer',padding:'1px 4px',lineHeight:1}}>✎</button>
                  </td>
                </tr>);
              })}</tbody>
            </table></div>
          </div>)}
        </div>
      </div>
      <div className="modal-footer">
        <button className="btn btn-ghost btn-sm" onClick={onClone}>Clone flight</button>
        {canRemove&&<button className="btn btn-danger btn-sm" onClick={onRemove}>Remove</button>}
        <div style={{flex:1}}/>
        <button className="btn btn-primary btn-sm" onClick={onClose}>Done</button>
      </div>
    </div>
  </div>);
}

// ─── FLIGHT CARD ──────────────────────────────────────────────────────────────


function FlightCard({flight,result,isSelected,onSelect,onUpdate,onClone,onRemove,canRemove,catalogue,audiences,planId,showAgencyFee,sitesMap,siteOverlaps,trackersCatalogue,trackerEligibility}){
  const[open,setOpen]=useState(false);
  const tot=result&&result.tot;
  const obj=OBJECTIVES.find(o=>o.id===flight.objective)||OBJECTIVES[0];
  const aud=audiences&&audiences.find(a=>a.id===flight.audienceId);
  const freqCapCount=(()=>{
    if(!result?.platforms?.length||!sitesMap)return 0;
    const nWeeks=result.nWeeks||1;
    return result.platforms.filter(r=>{
      if(!r.p?.site_id||!sitesMap[r.p.site_id])return false;
      const cookies=r.frq>0?(r.impr/nWeeks)/r.frq*(r.p.viewability||0.7):0;
      const effFreq=computeEffectiveFrequency({cookies,frq:r.frq,ruc:r.p.real_users_coef||2.0,audComp:r.audComp||r.p.audience_comp||0.65,device:r.p.device,site:sitesMap[r.p.site_id]});
      const cap=r.p.max_weekly_frequency??MAX_FREQ_DEFAULT;
      return effFreq!==null&&effFreq>cap;
    }).length;
  })();
  return(<div className={'flight-card'+(isSelected?' active':'')} style={{borderLeft:'3px solid '+flight.color}}>
    <div className="fc-header" onClick={()=>{onSelect(flight.id);setOpen(true);}} title="Edit">
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontWeight:600,fontSize:12,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}} title={flight.label}>{flight.label}</div>
        <div style={{display:'flex',alignItems:'baseline',gap:6,marginTop:3}}>
          <span style={{fontSize:9,padding:'1px 5px',borderRadius:8,background:obj.color+'22',color:obj.color,fontWeight:700,flexShrink:0}}>{obj.label}</span>
          <span style={{fontSize:10,color:'var(--text3)',flexShrink:0,whiteSpace:'nowrap'}}>{fdate(flight.startDate)} – {fdate(flight.endDate)}</span>
          {aud&&<span style={{fontSize:10,color:'var(--gold-dark)',flexShrink:0,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>· {aud.name}</span>}
          {freqCapCount>0&&<span title={`${freqCapCount} platform${freqCapCount>1?'s':''} exceed${freqCapCount===1?'s':''} weekly frequency cap — open flight to see details`} style={{fontSize:10,color:'#d08000',cursor:'default',lineHeight:1,flexShrink:0}}>⚠</span>}
          <div style={{flex:1,minWidth:4}}/>
          {tot&&tot.budget>0&&<div style={{display:'flex',alignItems:'baseline',gap:6,flexShrink:0}}>
            <span className="mono" style={{fontSize:11,fontWeight:600,color:'var(--gold-dark)'}}>{fe(tot.budget)}</span>
            <span style={{fontSize:10,color:'var(--text3)'}}>{fp(tot.reach)} vRCH</span>
          </div>}
        </div>
      </div>
      <div style={{color:'var(--gold)',fontSize:11,marginLeft:4,flexShrink:0}}>✎</div>
    </div>
    {open&&<FlightModal fl={flight} result={result} onClose={()=>setOpen(false)} onUpdate={onUpdate} onClone={()=>{onClone();setOpen(false);}} onRemove={()=>{onRemove();setOpen(false);}} canRemove={canRemove} catalogue={catalogue} audiences={audiences} showAgencyFee={showAgencyFee} sitesMap={sitesMap} siteOverlaps={siteOverlaps} trackersCatalogue={trackersCatalogue} trackerEligibility={trackerEligibility}/>}
  </div>);
}

// ─── FLOWCHART ────────────────────────────────────────────────────────────────
// Shared objective-label style — the activation section's own label span and the
// TOTAL block's per-objective label both read this, so they can never drift apart.
const OBJ_LABEL_STYLE={fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em'};
function AnnualFlowchart({flights,allResults,displayYear}){
  const grps=useMemo(()=>{const bO=[],bM={};flights.forEach(f=>{const b=f.brand||'Activation 1';if(!bM[b]){bM[b]={};bO.push(b);}const ob=f.objective||'awareness';if(!bM[b][ob])bM[b][ob]=[];bM[b][ob].push(f);});return{bO,bM};},[flights]);
  const groupTots=useMemo(()=>{
    const out={};
    grps.bO.forEach(b=>{out[b]=combineTot(flights.filter(f=>(f.brand||'Activation 1')===b).map(f=>allResults[f.id]).filter(Boolean));});
    return out;
  },[grps,flights,allResults]);
  // Grid columns are scoped to a single real calendar year — every per-week/per-flight
  // match below requires wd.year===dy (not just the bare week number) alongside the
  // bounds check. Without this, two flights in different real years whose weeks happen
  // to share a bare week number would collide: whichever a lookup (.find/.includes) hits
  // first "wins" that column and the other flight silently renders nothing, even though
  // pure-summation totals would still (mis-)include both. dy falls back to YEAR only so
  // this never crashes before displayYear's mount-time default is set.
  const dy=displayYear||YEAR;
  const monthWeeksY=useMemo(()=>monthWeeksFor(dy),[dy]);
  // Column count derives from the ISO calendar for dy itself — 52 most years, 53 when
  // dy is one of the years that actually has an ISO week 53 (e.g. 2026). Not a hardcoded
  // 52 or a hardcoded 53 — the grid supports whatever range the real calendar produces.
  const nW=weeksInYear(dy);
  // Visually separates one activation/TOTAL block from the one above it — a borderless,
  // page-background spacer row (not padding on the header's own cell, which would just
  // extend the dark bar's own background instead of showing empty space above it).
  const sectionGap=(key)=><tr key={key}><td colSpan={nW+1} style={{height:24,padding:0,border:'none',background:'transparent'}}/></tr>;
  // Shared objective sub-header row — used identically by an activation section's own
  // label row and the TOTAL block's per-objective row (with its trailing summary), so
  // the two can never drift apart in centering, font, or row height. The 3-column grid
  // (1fr auto 1fr) centers the label at the row's true midpoint regardless of whether
  // column 3 holds a trailing summary — col1/col3 always share the remaining width
  // equally, which is what keeps the label's own center fixed whether or not a summary
  // is present (plain text-align:center cannot do this once a second inline element is
  // added next to the label, which is why the TOTAL block needed this instead of reusing
  // the activation row's old solo-span markup verbatim).
  const objHeaderRow=(key,label,color,summary)=>(
    <tr key={key}><td colSpan={nW+1} style={{padding:0,background:'var(--surface3)',borderBottom:'1px solid var(--border)'}}>
      <div style={{display:'grid',gridTemplateColumns:'1fr auto 1fr',alignItems:'baseline',padding:'1px 10px 1px 18px',width:'100%',boxSizing:'border-box',lineHeight:1}}>
        <span style={{...OBJ_LABEL_STYLE,color,gridColumn:2,textAlign:'left'}}>{label}</span>
        {summary&&<span style={{fontSize:9,color:'var(--gold-dark)',fontWeight:700,gridColumn:3,paddingLeft:6,textAlign:'left'}}>{summary}</span>}
      </div>
    </td></tr>
  );
  return(<div style={{overflowX:'auto',width:'100%'}}>
    <table className="fc-table">
      {/* Explicit colgroup — the spec-guaranteed (CSS2.1 §17.5.2 rule 1, highest
          priority, respected identically by every browser) way to fix column widths
          under table-layout:fixed. Without this, fixed-layout's width comes from the
          FIRST row's cells (the month header, whose <th>s are multi-week colSpan
          groups with no explicit width at all) — an ambiguous, browser-reconciled
          fallback that a solo single-week block (e.g. a year-boundary flight's
          1-week fragment, only possible since today's earlier fixes) can visibly
          widen relative to every other week, since it's the only column where a
          €/flight block's full text has nowhere to spread across multiple columns.
          nW week-columns, all equal width — never a hardcoded 52. */}
      <colgroup>
        <col style={{width:150}}/>
        {Array.from({length:nW},(_,i)=>i).map(i=><col key={i} style={{width:22}}/>)}
      </colgroup>
      <thead>
        <tr><th className="row-hdr" style={{width:150,fontSize:9,color:'var(--text3)'}}>METRIC</th>{MONTHS.map((m,mi)=><th key={m} colSpan={monthWeeksY[mi].length} className="fc-month-hdr">{MN[mi]}</th>)}</tr>
        <tr><th className="row-hdr" style={{fontSize:8,color:'var(--text3)'}}>WEEK</th>{Array.from({length:nW},(_,i)=>i+1).map(w=><th key={w} style={{fontSize:8,padding:'2px 0',textAlign:'center',color:'var(--text3)',minWidth:22,width:22}}>{w}</th>)}</tr>
      </thead>
      <tbody>
        {grps.bO.map((brand,bIdx)=>{
          const objMap=grps.bM[brand];
          return[
            bIdx>0&&sectionGap('gap_bh_'+brand),
            <tr key={'bh_'+brand}><td colSpan={nW+1} style={{padding:0,background:'#1a2a3a'}}><div style={{display:'flex',alignItems:'baseline',gap:6,padding:'4px 10px',width:'100%',boxSizing:'border-box',lineHeight:1}}><span style={{color:'#ffffff',fontSize:9,fontWeight:700,letterSpacing:'.09em',textTransform:'uppercase'}}>{brand}</span><span style={{color:'#ffffff',fontSize:9,fontWeight:400}}>· {fe(groupTots[brand]?.budget)} · vRCH% {fp(groupTots[brand]?.reach)}</span></div></td></tr>,
            ...Object.keys(objMap).flatMap(objId=>{
              const obj=OBJECTIVES.find(o=>o.id===objId)||{label:objId,color:'#888'};
              const objFl=objMap[objId];
              const wB=Array(nW).fill(0),wI=Array(nW).fill(0),wV=Array(nW).fill(0),wC=Array(nW).fill(0),wR=Array(nW).fill(0);
              objFl.forEach(f=>{const r=allResults[f.id];if(!r)return;r.weekly.forEach(wd=>{if(wd.year!==dy)return;const idx=wd.week-1;if(idx<0||idx>=nW)return;wB[idx]+=wd.budget;wI[idx]+=wd.impressions;wV[idx]+=wd.views;wC[idx]+=wd.clicks;wR[idx]=Math.max(wR[idx],wd.reach||0);});});
              const mxB=Math.max(...wB,1),mxI=Math.max(...wI,1);
              const isAw=objId==='awareness'||objId==='views',isTr=objId==='traffic'||objId==='conversion';
              return[
                objHeaderRow('oh_'+brand+objId,obj.label,obj.color,null),
                <tr key={'cam_'+brand+objId}><td className="row-hdr row-sub" style={{paddingLeft:12,fontSize:9}}>Campaigns</td>{Array.from({length:nW},(_,i)=>i+1).map(w=>{const here=objFl.filter(f=>{const r=allResults[f.id];return r&&r.weekly&&r.weekly.some(wk=>wk.week===w&&wk.year===dy);});if(!here.length)return<td key={w}><div className="fc-cell empty"/></td>;return<td key={w} style={{padding:1}}><div className="fc-cell" style={{background:here[0].color,borderRadius:2}}/></td>;})}</tr>,
                <tr key={'bud_'+brand+objId}><td className="row-hdr row-sub" style={{paddingLeft:12,fontSize:9}}>Budget / wk</td>{wB.map((v,i)=>{if(!v)return<td key={i}><div className="fc-cell empty"/></td>;const p=v/mxB;return<td key={i} style={{padding:0}}><div className="fc-cell" style={{background:p>.7?'var(--gold-dark)':p>.4?'var(--gold)':'#E8D5BC',color:p>.4?'#fff':'#5A3A10',fontSize:7,fontWeight:700}}>{Math.round(v/1000)+'K'}</div></td>;})}</tr>,
                <tr key={'imp_'+brand+objId}><td className="row-hdr row-sub" style={{paddingLeft:12,fontSize:9}}>Impr / wk</td>{wI.map((v,i)=>{if(!v)return<td key={i}><div className="fc-cell empty"/></td>;const p=v/mxI;return<td key={i} style={{padding:0}}><div className="fc-cell" style={{background:p>.7?'#2A5298':p>.4?'#4472C4':'#96B4E8',color:p>.4?'#fff':'#1A2A50',fontSize:7,fontWeight:700}}>{fmil(v)}</div></td>;})}</tr>,
                <tr key={'rch_'+brand+objId}><td className="row-hdr row-sub" style={{paddingLeft:12,fontSize:9,color:'var(--gold-dark)',fontWeight:600}}>vRCH% / wk</td>{wR.map((v,i)=>{if(!v)return<td key={i}><div className="fc-cell empty"/></td>;const p=Math.min(v,0.99);return<td key={i} style={{padding:0}}><div className="fc-cell" style={{background:p>.35?'#9B7E54':p>.20?'#C19A6B':'#E8D5BC',color:p>.20?'#fff':'#5A3A10',fontSize:7,fontWeight:800}}>{(p*100).toFixed(0)+'%'}</div></td>;})}</tr>,
                isAw&&wV.some(v=>v>0)&&<tr key={'views_'+brand+objId}><td className="row-hdr row-sub" style={{paddingLeft:12,fontSize:9}}>Views / wk</td>{wV.map((v,i)=>{if(!v)return<td key={i}><div className="fc-cell empty"/></td>;const p=v/Math.max(...wV,1);return<td key={i} style={{padding:0}}><div className="fc-cell" style={{background:p>.7?'#4A0E8A':p>.4?'#7030A0':'#C09AD8',color:p>.4?'#fff':'#2A0060',fontSize:7,fontWeight:700}}>{fmil(v)}</div></td>;})}</tr>,
                isTr&&wC.some(v=>v>0)&&<tr key={'clk_'+brand+objId}><td className="row-hdr row-sub" style={{paddingLeft:12,fontSize:9}}>Clicks / wk</td>{wC.map((v,i)=>{if(!v)return<td key={i}><div className="fc-cell empty"/></td>;const p=v/Math.max(...wC,1);return<td key={i} style={{padding:0}}><div className="fc-cell" style={{background:p>.7?'#1B6020':p>.4?'#2E8B57':'#90D4AA',color:p>.4?'#fff':'#0A3010',fontSize:7,fontWeight:700}}>{fmil(v)}</div></td>;})}</tr>,
                <tr key={'ftot_'+brand+objId}><td className="row-hdr" style={{paddingLeft:10,fontStyle:'italic',color:'var(--text3)',fontSize:9}}>€ / flight</td>{(()=>{const cells=[];let w=1;while(w<=nW){const f=objFl.find(fl=>{const r=allResults[fl.id];if(!r)return false;const yw=r.weekly.filter(x=>x.year===dy);return yw.length&&yw[0].week===w;});if(f){const r=allResults[f.id];const yw=r.weekly.filter(x=>x.year===dy);const span=Math.min(yw.length,(nW+1)-w);
if(yw.length>=nW){
  // vRCH% here is the flight's real cumulative reach (r.tot.reach) — see below for why,
  // same reasoning applies to every cell in this row, monthly-breakdown or single-block.
  monthWeeksY.forEach((ws,mi)=>{const mBud=ws.reduce((s,wk)=>s+(yw.find(x=>x.week===wk)?.budget||0),0);cells.push(<td key={'m'+mi} colSpan={ws.length} style={{padding:'2px 3px',textAlign:'center',background:'var(--gold-bg)',borderLeft:'1px solid var(--border)'}}><span style={{fontSize:9,color:f.color,fontWeight:700}}>{mBud>0?fe(mBud):'—'}</span><span style={{fontSize:8,color:'var(--gold-dark)',marginLeft:3}}>{mBud>0?fp(r.tot.reach)+' vRCH':''}</span></td>);});w=nW+1;
} else {
  // Year-scoped budget for this block (how much THIS year's portion spent) — budget is
  // additive, so summing just the in-year weeks is correct and meaningful on its own.
  const blockBud=yw.reduce((s,x)=>s+x.budget,0);
  // Reach is NOT additive/per-week — r.tot.reach is the flight's one true cumulative
  // figure (Sainsbury-accumulated across its whole real duration in calcFlight), the
  // exact same source Summary tab and the KPI bar read. The previous version read
  // yw[last].reach — but calcFlight computes each week's `reach` from THAT WEEK'S
  // OWN budget alone (calculatePlanReach({...just wb=wkBudgets[i]...})), never
  // accumulated across weeks — so it was showing "what this one week's spend alone
  // would reach in isolation" (the same number visible in the 'vRCH% / wk' row above),
  // not the flight's real total. A single flight has one true reach figure regardless
  // of how many year-views its blocks are split across, so every block/fragment of
  // the same flight correctly shows the identical r.tot.reach.
  const blockRch=r.tot.reach;
  if(span===1){
    // Single-week fragment (e.g. a flight whose bulk sits in the next/prior year,
    // leaving 1 week in this year's view) — 22px has no room for "€X,XXX Y.Y% vRCH"
    // without wrapping/overflow; rather than cram unreadable text into a column that
    // was never sized for it, show a minimal marker with the real numbers on hover.
    // Checked via span===1 (actual computed column width), not a hardcoded "week 53"
    // — applies to any 1-week fragment, any year, same underlying cause.
    const fullIdx=r.weekly.findIndex(x=>x.week===yw[0].week&&x.year===yw[0].year);
    const contNote=fullIdx+yw.length<r.weekly.length?` — continues into ${r.weekly[fullIdx+yw.length].year}`
      :fullIdx>0?` — continued from ${r.weekly[fullIdx-1].year}`:'';
    cells.push(<td key={w} colSpan={span} title={`${fe(blockBud)} · ${fp(blockRch)} vRCH (flight total)${contNote}`} style={{padding:0,textAlign:'center',background:'var(--gold-bg)',borderLeft:'1px solid var(--border)',borderRight:'1px solid var(--border)',cursor:'default'}}><span style={{fontSize:9,fontWeight:700,color:f.color}}>→</span></td>);w+=span;
  } else {
    // overflow:hidden + overflow-wrap:anywhere — belt-and-suspenders for any narrower
    // multi-week block (2-3 columns) that still might not comfortably fit both spans;
    // solo 1-week blocks never reach here now (handled above), but this stays as a
    // safety net rather than assuming 2+ columns is always enough room.
    cells.push(<td key={w} colSpan={span} style={{padding:'2px 4px',textAlign:'center',background:'var(--gold-bg)',borderLeft:'1px solid var(--border)',borderRight:'1px solid var(--border)',overflow:'hidden'}}><span style={{fontSize:9,color:f.color,fontWeight:700,overflowWrap:'anywhere'}}>{fe(blockBud)}</span><span style={{fontSize:8,color:'var(--gold-dark)',marginLeft:4,overflowWrap:'anywhere'}}>{fp(blockRch)} vRCH</span></td>);w+=span;
  }
}}else{cells.push(<td key={w}/>);w++;}}return cells;})()}</tr>,
                <tr key={'mth_'+brand+objId}><td className="row-hdr" style={{paddingLeft:10,fontStyle:'italic',color:'var(--gold-dark)',fontWeight:600,fontSize:9}}>Monthly €</td>{monthWeeksY.map((ws,mi)=>{const v=ws.reduce((s,w)=>s+(wB[w-1]||0),0);return<td key={mi} colSpan={ws.length} style={{textAlign:'center',padding:'2px 4px',background:'var(--gold-bg)',borderLeft:'1px solid var(--border)'}}><span style={{fontSize:9,color:'var(--gold-dark)',fontWeight:700}}>{v>0?fe(v):'—'}</span></td>;})}</tr>,
              ].filter(Boolean);
            }),
          ];
        })}
        {(()=>{
          // Year-scoped flight set — a flight only counts toward this year's TOTAL
          // (bucket membership, every row below, AND the header vRCH% figures) if it
          // actually has at least one week in the displayed year. A year with zero
          // touching flights renders only the (dashed) TOTAL header, no objective
          // blocks — never a stale/partial figure carried over from another year.
          const yearFlights=flights.filter(f=>{const r=allResults[f.id];return r&&r.weekly&&r.weekly.some(wd=>wd.year===dy);});
          const normObj=o=>o==='views'?'consideration':(o||'awareness');
          const objBuckets={};
          yearFlights.forEach(f=>{const key=normObj(f.objective);(objBuckets[key]=objBuckets[key]||[]).push(f);});
          const grandResults=yearFlights.map(f=>allResults[f.id]).filter(Boolean);
          const grandTot=combineTot(grandResults);
          const rows=[
            sectionGap('gap_totalhdr'),
            <tr key='totalhdr'><td colSpan={nW+1} style={{padding:0,background:'#1a2a3a'}}><div style={{display:'flex',alignItems:'baseline',gap:6,padding:'4px 10px',width:'100%',boxSizing:'border-box',lineHeight:1}}><span style={{color:'#ffffff',fontSize:9,fontWeight:700,letterSpacing:'.09em',textTransform:'uppercase'}}>TOTAL — ALL ACTIVATIONS</span><span style={{color:'#ffffff',fontSize:9,fontWeight:400}}>· {yearFlights.length?fe(grandTot.budget):'—'} · vRCH% {yearFlights.length?fp(grandTot.reach):'—'}</span></div></td></tr>,
          ];
          OBJECTIVES.forEach(obj=>{
            const objId=obj.id;
            const objFl=objBuckets[objId];
            if(!objFl||!objFl.length)return;
            const objResults=objFl.map(f=>allResults[f.id]).filter(Boolean);
            const objTot=combineTot(objResults);
            // Same per-week accumulation as an activation section's own objective block —
            // Budget/Impr/Views/Clicks are additive, so merging every activation's flights
            // of this objective into one objFl list and summing is correct on its own.
            const wB=Array(nW).fill(0),wI=Array(nW).fill(0),wV=Array(nW).fill(0),wC=Array(nW).fill(0),wR=Array(nW).fill(0);
            objFl.forEach(f=>{const r=allResults[f.id];if(!r)return;r.weekly.forEach(wd=>{if(wd.year!==dy)return;const idx=wd.week-1;if(idx<0||idx>=nW)return;wB[idx]+=wd.budget;wI[idx]+=wd.impressions;wV[idx]+=wd.views;wC[idx]+=wd.clicks;});});
            // vRCH%/wk is NOT additive/max like the per-activation row — merging several
            // activations' flights into the same week needs the real Sainsbury combination
            // of that week's active flights' own per-week reach (sbN), never an average
            // or a sum, so a week with several concurrent flights never under- or over-
            // states the combined reach.
            for(let i=0;i<nW;i++){const w=i+1;const weekReaches=objFl.map(f=>{const r=allResults[f.id];if(!r)return null;const wd=r.weekly.find(x=>x.week===w&&x.year===dy);return wd?wd.reach:null;}).filter(v=>v!=null);wR[i]=weekReaches.length?sbN(weekReaches):0;}
            const mxB=Math.max(...wB,1),mxI=Math.max(...wI,1);
            const isAw=objId==='awareness'||objId==='consideration',isTr=objId==='traffic'||objId==='conversion';
            rows.push(
              objHeaderRow('total_oh_'+objId,obj.label,obj.color,`· ${fe(objTot.budget)} · vRCH% ${fp(objTot.reach)}`),
              <tr key={'total_cam_'+objId}><td className="row-hdr row-sub" style={{paddingLeft:12,fontSize:9}}>Campaigns</td>{Array.from({length:nW},(_,i)=>i+1).map(w=>{const here=objFl.filter(f=>{const r=allResults[f.id];return r&&r.weekly&&r.weekly.some(wk=>wk.week===w&&wk.year===dy);});if(!here.length)return<td key={w}><div className="fc-cell empty"/></td>;return<td key={w} style={{padding:1}}><div className="fc-cell" style={{background:'#5A7A9A',color:'#fff',fontSize:7,fontWeight:700,borderRadius:2}}>{here.length}</div></td>;})}</tr>,
              <tr key={'total_bud_'+objId}><td className="row-hdr row-sub" style={{paddingLeft:12,fontSize:9}}>Budget / wk</td>{wB.map((v,i)=>{if(!v)return<td key={i}><div className="fc-cell empty"/></td>;const p=v/mxB;return<td key={i} style={{padding:0}}><div className="fc-cell" style={{background:p>.7?'var(--gold-dark)':p>.4?'var(--gold)':'#E8D5BC',color:p>.4?'#fff':'#5A3A10',fontSize:7,fontWeight:700}}>{Math.round(v/1000)+'K'}</div></td>;})}</tr>,
              <tr key={'total_imp_'+objId}><td className="row-hdr row-sub" style={{paddingLeft:12,fontSize:9}}>Impr / wk</td>{wI.map((v,i)=>{if(!v)return<td key={i}><div className="fc-cell empty"/></td>;const p=v/mxI;return<td key={i} style={{padding:0}}><div className="fc-cell" style={{background:p>.7?'#2A5298':p>.4?'#4472C4':'#96B4E8',color:p>.4?'#fff':'#1A2A50',fontSize:7,fontWeight:700}}>{fmil(v)}</div></td>;})}</tr>,
              <tr key={'total_rch_'+objId}><td className="row-hdr row-sub" style={{paddingLeft:12,fontSize:9,color:'var(--gold-dark)',fontWeight:600}}>vRCH% / wk</td>{wR.map((v,i)=>{if(!v)return<td key={i}><div className="fc-cell empty"/></td>;const p=Math.min(v,0.99);return<td key={i} style={{padding:0}}><div className="fc-cell" style={{background:p>.35?'#9B7E54':p>.20?'#C19A6B':'#E8D5BC',color:p>.20?'#fff':'#5A3A10',fontSize:7,fontWeight:800}}>{(p*100).toFixed(0)+'%'}</div></td>;})}</tr>,
              isAw&&wV.some(v=>v>0)&&<tr key={'total_views_'+objId}><td className="row-hdr row-sub" style={{paddingLeft:12,fontSize:9}}>Views / wk</td>{wV.map((v,i)=>{if(!v)return<td key={i}><div className="fc-cell empty"/></td>;const p=v/Math.max(...wV,1);return<td key={i} style={{padding:0}}><div className="fc-cell" style={{background:p>.7?'#4A0E8A':p>.4?'#7030A0':'#C09AD8',color:p>.4?'#fff':'#2A0060',fontSize:7,fontWeight:700}}>{fmil(v)}</div></td>;})}</tr>,
              isTr&&wC.some(v=>v>0)&&<tr key={'total_clk_'+objId}><td className="row-hdr row-sub" style={{paddingLeft:12,fontSize:9}}>Clicks / wk</td>{wC.map((v,i)=>{if(!v)return<td key={i}><div className="fc-cell empty"/></td>;const p=v/Math.max(...wC,1);return<td key={i} style={{padding:0}}><div className="fc-cell" style={{background:p>.7?'#1B6020':p>.4?'#2E8B57':'#90D4AA',color:p>.4?'#fff':'#0A3010',fontSize:7,fontWeight:700}}>{fmil(v)}</div></td>;})}</tr>,
              <tr key={'total_mth_'+objId}><td className="row-hdr" style={{paddingLeft:10,fontStyle:'italic',color:'var(--gold-dark)',fontWeight:600,fontSize:9}}>Monthly €</td>{monthWeeksY.map((ws,mi)=>{const v=ws.reduce((s,w)=>s+(wB[w-1]||0),0);return<td key={mi} colSpan={ws.length} style={{textAlign:'center',padding:'2px 4px',background:'var(--gold-bg)',borderLeft:'1px solid var(--border)'}}><span style={{fontSize:9,color:'var(--gold-dark)',fontWeight:700}}>{v>0?fe(v):'—'}</span></td>;})}</tr>,
            );
          });
          return rows.filter(Boolean);
        })()}
      </tbody>
    </table>
  </div>);
}

// ─── SUMMARY TAB — platforms inside each format group, matching Excel layout ──
// ─── SHARED EXCEL EXPORT HELPERS (ExcelJS) ────────────────────────────────────
// Dynamic import of the real locally-installed `exceljs` package — not a runtime CDN
// fetch (the old `document.createElement('script')`/`cdnjs.cloudflare.com` loader this
// replaced, 2026-10-05, was a second instance of the same CDN-at-runtime problem already
// fixed elsewhere for `xlsx`; missed earlier because it's a <script> injection, not an
// import() call). Resolved once per call; webpack bundles `exceljs` as its own lazy chunk,
// same pattern as `xlsx`. Shared by exportClientMediaPlan (per-flight/per-month) and
// exportFullPlanExcel (whole-plan, Flowchart/Summary/Details) so neither re-implements the
// other's colors/fonts/number formats/aggregation.
async function loadExcelJS(){
  const mod=await import('exceljs').catch(()=>null);
  return mod?(mod.default||mod):null;
}
const XLSX_COLORS={DARK:'1A3A5C',GOLD:'C9A84C',LIGHT_GOLD:'FFF8E1',LIGHT_BLUE:'DBEAF7',MID_BLUE:'2E6DA4',WHITE:'FFFFFF',GREY:'F2F2F2',TEXT_DARK:'1A3A5C',AMBER:'E07000'};
const XLSX_VAT=1.20;
function xlsxHdr(cell,txt,opts={}){
  cell.value=txt;
  cell.font={name:'Arial',size:opts.sz||9,bold:opts.bold!==false,color:{argb:'FF'+(opts.color||XLSX_COLORS.WHITE)}};
  cell.fill={type:'pattern',pattern:'solid',fgColor:{argb:'FF'+(opts.bg||XLSX_COLORS.DARK)}};
  cell.alignment={horizontal:opts.align||'center',vertical:'middle',wrapText:!!opts.wrap};
  cell.border={top:{style:'thin',color:{argb:'FFCCCCCC'}},bottom:{style:'thin',color:{argb:'FFCCCCCC'}},left:{style:'thin',color:{argb:'FFCCCCCC'}},right:{style:'thin',color:{argb:'FFCCCCCC'}}};
}
function xlsxDat(cell,val,opts={}){
  cell.value=val;
  cell.font={name:'Arial',size:opts.sz||9,bold:!!opts.bold,color:{argb:'FF'+(opts.color||'000000')}};
  if(opts.bg)cell.fill={type:'pattern',pattern:'solid',fgColor:{argb:'FF'+opts.bg}};
  cell.alignment={horizontal:opts.align||'left',vertical:'middle'};
  cell.border={top:{style:'thin',color:{argb:'FFCCCCCC'}},bottom:{style:'thin',color:{argb:'FFCCCCCC'}},left:{style:'thin',color:{argb:'FFCCCCCC'}},right:{style:'thin',color:{argb:'FFCCCCCC'}}};
  if(opts.numFmt)cell.numFmt=opts.numFmt;
}
function xlsxMergeHdr(ws,r1,c1,r2,c2,txt,opts={}){
  ws.mergeCells(r1,c1,r2,c2);
  xlsxHdr(ws.getCell(r1,c1),txt,opts);
  for(let r=r1;r<=r2;r++)for(let c=c1;c<=c2;c++){const cl=ws.getCell(r,c);if(!cl.value&&(r!==r1||c!==c1)){cl.fill={type:'pattern',pattern:'solid',fgColor:{argb:'FF'+(opts.bg||XLSX_COLORS.DARK)}};cl.border={top:{style:'thin',color:{argb:'FFCCCCCC'}},bottom:{style:'thin',color:{argb:'FFCCCCCC'}},left:{style:'thin',color:{argb:'FFCCCCCC'}},right:{style:'thin',color:{argb:'FFCCCCCC'}}};}};
}
function xlsxEu(v){return typeof v==='number'?v:null;}
function xlsxPct(v){return v==null?'—':(+v).toFixed(1)+'%';}
function xlsxFmtNum(v){return Math.round(v);}
// Shared platform-aggregation (Summary sheet's format-group tables: Video/Display Std/
// Display Non-Std/Social/Search) over an arbitrary flight subset — extracted so a
// plan-wide export can run it once per activation plus once for the whole plan, instead
// of re-deriving it. Pure, no ExcelJS dependency.
function aggregatePlatformGroups(flightsSubset,allResults){
  const groups={};
  SUMMARY_GROUPS.forEach(g=>{groups[g.key]={label:g.label,types:g.types,rows:{},groupReach:0};});
  let totBudAll=0,totTrk=0,totAf=0;
  const campBrands=[],campPeriods=[];
  flightsSubset.forEach(fl=>{
    if(!campBrands.includes(fl.brand))campBrands.push(fl.brand);
    const pd=fdate(fl.startDate)+' – '+fdate(fl.endDate);if(!campPeriods.includes(pd))campPeriods.push(pd);
    const r=allResults[fl.id];if(!r)return;
    totTrk+=r.tot.trackerCost||0;totAf+=r.tot.agencyFee||0;
    r.platforms.forEach(pr=>{
      const t=pr.p?.type||'banner';
      const gk=t==='video'?'video':t==='sem'?'sem':t==='social'?'social':t==='banner'?'display_std':'display_ns';
      const g=groups[gk];if(!g)return;
      const pid=pr.p.id;
      if(!g.rows[pid])g.rows[pid]={pid,label:pr.p.label,seller:pr.p.seller||'',type:t,buyingType:(pr.pe?.discount>0)?'Direct Deal':'Programmatic',budget:0,mBud:0,impr:0,vimpr:0,views:0,clicks:0,conversions:0,reach:0,cpm:pr.cpm||0};
      const rr=g.rows[pid];rr.budget+=pr.bud;rr.mBud+=(pr.mBud??pr.bud);rr.impr+=pr.impr;rr.vimpr+=pr.vimpr;rr.views+=pr.views;rr.clicks+=pr.clicks;rr.conversions+=pr.clicks*(pr.p.cvr||0);rr.reach=sbN([rr.reach,pr.reach||0]);
      totBudAll+=pr.bud;
    });
    SUMMARY_GROUPS.forEach(sg=>{const pool=r.platforms.filter(pr=>sg.types.includes(pr.p?.type)).map(pr=>pr.reach||0);groups[sg.key].groupReach=sb2(groups[sg.key].groupReach,sbN(pool));});
  });
  let totImpr=0,totViews=0,totVimpr=0,totClicks=0,totReach=0;
  SUMMARY_GROUPS.forEach(sg=>{const rows=Object.values(groups[sg.key].rows);rows.forEach(r=>{totImpr+=r.impr;totViews+=r.views;totVimpr+=r.vimpr;totClicks+=r.clicks;});totReach=sb2(totReach,groups[sg.key].groupReach);});
  return{groups,totBudAll,totTrk,totAf,campBrands,campPeriods,totImpr,totViews,totVimpr,totClicks,totReach};
}

// ─── CLIENT MEDIA PLAN EXPORT ─────────────────────────────────────────────────
async function exportClientMediaPlan(flights,allResults,mode,targetId){
  const ExcelJS=await loadExcelJS();
  if(!ExcelJS){alert('ExcelJS could not be loaded. Check your connection.');return;}

  const {DARK,GOLD,LIGHT_GOLD,LIGHT_BLUE,MID_BLUE,WHITE,GREY,TEXT_DARK,AMBER}=XLSX_COLORS;
  const VAT=XLSX_VAT;
  const hdr=xlsxHdr,dat=xlsxDat,mergeHdr=xlsxMergeHdr,eu=xlsxEu,pct=xlsxPct,fmt=xlsxFmtNum;

  // Determine which flights to export
  let toExport=[];
  let sheetTitle='';
  if(mode==='flight'){
    const fl=flights.find(f=>f.id===targetId);
    if(fl)toExport=[fl];
    sheetTitle=fl?fl.brand+' — '+fl.label:'Flight';
  } else if(mode==='month'){
    // targetId is month index 0-11
    toExport=flights.filter(fl=>{
      const r=allResults[fl.id];
      return r&&r.weekly.some(wk=>weekMonth(wk.week,wk.year)===targetId);
    });
    // Derive the real year from the matched weeks themselves, not the hardcoded YEAR constant —
    // a flight's weeks now carry their own real year (see calcFlight).
    let sheetYear=YEAR;
    outer: for(const fl of toExport){
      const r=allResults[fl.id];if(!r)continue;
      for(const wk of r.weekly){if(weekMonth(wk.week,wk.year)===targetId){sheetYear=wk.year||YEAR;break outer;}}
    }
    sheetTitle=MN[targetId]+' '+sheetYear;
  }
  if(!toExport.length){alert('No data for selected period');return;}

  // ── SUMMARY SHEET (ExcelJS) ───────────────────────────────────────────────
  function buildSummarySheet(wb){
    const ws=wb.addWorksheet('Summary');
    ws.properties.defaultRowHeight=16;
    let row=1;

    // Aggregate — shared with exportPlanMediaPlan, see aggregatePlatformGroups above
    const{groups,totBudAll,totTrk,totAf,campBrands,campPeriods,totImpr,totViews,totVimpr,totClicks,totReach}=aggregatePlatformGroups(toExport,allResults);

    // Column widths
    ws.columns=[{width:32},{width:16},{width:16},{width:14},{width:14},{width:10},{width:18},{width:14},{width:10},{width:14},{width:16},{width:18},{width:8}];

    // Title
    mergeHdr(ws,row,1,row,13,'DIGITAL MEDIA PLAN',{sz:14,bold:true,bg:DARK,color:WHITE,align:'center'});
    ws.getRow(row).height=24;row++;

    // Info block — left: campaign info, right: KPIs
    const infoLeft=[['Client / Brand',campBrands.join(', ')],['Period',campPeriods.join('; ')],['Target Audience','—'],['Total Budget excl. VAT',totBudAll],['Total Budget incl. VAT 20%',totBudAll*VAT]];
    const kpiRight=[['Total Impressions',fmt(totImpr)],['Total Views',fmt(totViews)],['Viewable Impr.',fmt(totVimpr)],['vRCH%',pct(totReach*100)],['Clicks',fmt(totClicks)]];
    for(let i=0;i<Math.max(infoLeft.length,kpiRight.length);i++){
      const [ll='',lv='']= infoLeft[i]||[];
      const [rl='',rv='']= kpiRight[i]||[];
      dat(ws.getCell(row,1),ll,{bold:true,bg:LIGHT_BLUE,color:TEXT_DARK,align:'right'});
      ws.mergeCells(row,2,row,7);dat(ws.getCell(row,2),lv,{bold:true,align:'left'});
      dat(ws.getCell(row,8),'',{});
      dat(ws.getCell(row,9),rl,{bold:true,bg:DARK,color:WHITE,align:'right'});
      ws.mergeCells(row,10,row,13);dat(ws.getCell(row,10),rv,{bold:true,bg:LIGHT_BLUE,align:'center'});
      row++;
    }
    row++; // spacer

    // Table header
    const HDRS=['Platform / Format','Seller','Buying Type','Buying Unit','Paid Units (Impr)','CPM €','Main KPI','KPI abs.','KPI %','Cost/KPI €','Budget excl.VAT','Budget incl.VAT','SHR%'];
    HDRS.forEach((h,i)=>hdr(ws.getCell(row,i+1),h,{bg:DARK,color:WHITE,align:'center',sz:9,wrap:true}));
    ws.getRow(row).height=18;row++;

    // Groups
    SUMMARY_GROUPS.forEach(sg=>{
      const g=groups[sg.key];const prows=Object.values(g.rows);if(!prows.length)return;
      const gBud=prows.reduce((s,r)=>s+r.budget,0);
      const isVideo=sg.key==='video',isSem=sg.key==='sem';
      const gKpi=isVideo?prows.reduce((s,r)=>s+r.views,0):isSem?prows.reduce((s,r)=>s+r.clicks,0):prows.reduce((s,r)=>s+r.vimpr,0);
      const gShr=totBudAll>0?pct(gBud/totBudAll*100):'';
      mergeHdr(ws,row,1,row,12,g.label,{bg:MID_BLUE,color:WHITE,sz:10,bold:true,align:'center'});
      dat(ws.getCell(row,13),gShr,{bg:MID_BLUE,color:WHITE,bold:true,align:'center'});
      ws.getRow(row).height=18;row++;
      prows.forEach((pr,pi)=>{
        const bg=pi%2===0?null:GREY;
        const isV=pr.type==='video',isS=pr.type==='sem';
        const mkpi=isV?pr.views:isS?pr.clicks:pr.vimpr;
        const mkpiLbl=isV?'Views':isS?'Clicks':'Viewable Impr.';
        const costKpi=mkpi>0?pr.budget/mkpi*1000:null;
        const kpiPct=pr.impr>0?pct(mkpi/pr.impr*100):'—';
        const prShr=totBudAll>0?pct(pr.budget/totBudAll*100):'';
        [pr.label,pr.seller||'',pr.buyingType,'CPM',fmt(pr.impr),pr.cpm||0,mkpiLbl,fmt(mkpi),kpiPct,costKpi,pr.budget,pr.budget*VAT,prShr].forEach((v,i)=>{
          const opts={bg,align:i<2?'left':'center',sz:9,bold:i===0};
          if(i===5||i===9||i===10||i===11)opts.numFmt='"€"#,##0.00';
          if(i===4||i===6||i===7)opts.numFmt='#,##0';
          dat(ws.getCell(row,i+1),v,opts);
        });
        row++;
      });
    });

    // Totals
    const grand=totBudAll+totTrk+totAf;
    mergeHdr(ws,row,1,row,4,'Total Online',{bg:DARK,color:WHITE,sz:10,align:'left'});
    dat(ws.getCell(row,5),fmt(totImpr),{bold:true,bg:DARK,color:WHITE,align:'right',numFmt:'#,##0'});
    dat(ws.getCell(row,6),'',{bg:DARK});
    dat(ws.getCell(row,7),'All Formats',{bold:true,bg:DARK,color:WHITE,align:'center'});
    const totKpiAll=Object.values(groups).reduce((s,g)=>{const isV=g.label==='Video',isS=g.label==='Search';return s+Object.values(g.rows).reduce((ss,rr)=>ss+(isV?rr.views:isS?rr.clicks:rr.vimpr),0);},0);
    dat(ws.getCell(row,8),fmt(totKpiAll),{bold:true,bg:DARK,color:WHITE,align:'right',numFmt:'#,##0'});
    dat(ws.getCell(row,9),'',{bg:DARK});
    dat(ws.getCell(row,10),'',{bg:DARK});
    dat(ws.getCell(row,11),totBudAll,{bold:true,bg:DARK,color:WHITE,align:'right',numFmt:'"€"#,##0.00'});
    dat(ws.getCell(row,12),totBudAll*VAT,{bold:true,bg:DARK,color:WHITE,align:'right',numFmt:'"€"#,##0.00'});
    dat(ws.getCell(row,13),'100%',{bold:true,bg:DARK,color:WHITE,align:'center'});
    row++;
    if(totTrk>0){mergeHdr(ws,row,1,row,10,'3rd Party Trackers',{bg:GREY,color:'000000',align:'left'});dat(ws.getCell(row,11),totTrk,{bg:GREY,numFmt:'"€"#,##0.00',align:'right'});dat(ws.getCell(row,12),totTrk*VAT,{bg:GREY,numFmt:'"€"#,##0.00',align:'right'});row++;}
    if(totAf>0){mergeHdr(ws,row,1,row,10,'Agency Fee',{bg:GREY,color:'000000',align:'left'});dat(ws.getCell(row,11),totAf,{bg:GREY,numFmt:'"€"#,##0.00',align:'right'});dat(ws.getCell(row,12),totAf*VAT,{bg:GREY,numFmt:'"€"#,##0.00',align:'right'});row++;}
    row++;
    mergeHdr(ws,row,1,row,10,'TOTAL BUDGET',{bg:DARK,color:WHITE,sz:11,align:'left'});
    dat(ws.getCell(row,11),grand,{bold:true,bg:DARK,color:WHITE,numFmt:'"€"#,##0.00',align:'right'});
    dat(ws.getCell(row,12),grand*VAT,{bold:true,bg:GOLD,color:TEXT_DARK,numFmt:'"€"#,##0.00',align:'right'});
    dat(ws.getCell(row,13),'',{bg:DARK});
  }

  // ── DETAIL SHEET (ExcelJS) ─────────────────────────────────────────────────
  function buildDetailSheet(wb,fl,r,monthFilter,sheetName){
    const ws=wb.addWorksheet(sheetName);
    ws.properties.defaultRowHeight=15;
    const weeks=monthFilter!=null?r.weekly.filter(wk=>weekMonth(wk.week)===monthFilter):r.weekly;
    if(!weeks.length)return;
    const ncols=4+weeks.length;

    // Column widths
    ws.columns=[{width:28},{width:14},{width:12},{width:12},...weeks.map(()=>({width:14}))];

    let row=1;
    // Title
    mergeHdr(ws,row,1,row,ncols,'WEEKLY MEDIA PLAN — '+fl.brand.toUpperCase()+' — '+(fl.label||'').toUpperCase(),{sz:12,bg:DARK,color:WHITE,align:'center'});
    ws.getRow(row).height=22;row++;
    [['Brand',fl.brand],['Period',fdate(fl.startDate)+' – '+fdate(fl.endDate)],['Budget','€'+Math.round(fl.budget).toLocaleString()+' excl. VAT'],['Audience',r.universe?r.universe.toLocaleString()+' people':'—']].forEach(([l,v])=>{
      dat(ws.getCell(row,1),l,{bold:true,bg:LIGHT_BLUE,color:TEXT_DARK,align:'right'});
      ws.mergeCells(row,2,row,ncols);dat(ws.getCell(row,2),v,{bold:true,align:'left'});row++;
    });
    row++;

    // Helper: per-week value
    function wkVal(pr,wi,field){
      const realWi=r.weekly.findIndex(x=>x.week===weeks[wi].week);
      const wb2=r.wkBudgets?.[realWi]??0;
      const wkMix=fl.weeklyOverrides?.[realWi]?.platforms||null;
      const ap=wkMix||fl.platforms;
      const pe=ap.find(x=>x.platformId===pr.p.id);if(!pe)return 0;
      const disc=Math.max(0,Math.min(0.9,pr.pe?.discount||0));
      const cpm=(pr.p.base_cpm||0)*(1-disc);
      const platBud=wb2*((pe.shr||0)/100);
      if(field==='budget')return platBud;
      const impr=cpm>0?(platBud/cpm)*1000:0;
      if(field==='impr')return impr;
      const frq=pe.frq_override!=null?pe.frq_override:(pr.p.weekly_frq||3);
      const ck=frq>0?impr/frq:0;
      if(field==='cookies')return ck;
      const ac=pr.audComp||pr.p?.audience_comp||0.65;
      const ruc=pr.p.real_users_coef||2.0;
      const view=pr.p.viewability||0.7;
      if(field==='taUsers')return ck/ruc*ac;
      if(field==='vRealUsers')return ck/ruc*ac*view;
      return 0;
    }

    // Metrics — EXACT same formulas as METRIC_SECTIONS getRows() in the platform UI
    const nWksAll=r.weekly.length||1;
    function getPlatWkBudEx(pr,realWi){
      const wb2=r.wkBudgets?.[realWi]??0;
      const wkMix=fl.weeklyOverrides?.[realWi]?.platforms||null;
      const ap=wkMix||fl.platforms;
      const pe=ap.find(x=>x.platformId===pr.p.id);if(!pe)return 0;
      return wb2*((pe.shr||0)/100);
    }
    const metrics=[
      {label:'IMPRESSIONS',color:MID_BLUE,numFmt:'#,##0',
        getRow:(pr)=>{
          const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));const cpm=(pr.p.base_cpm||0)*(1-disc);
          return{tot:Math.round(pr.impr),weeks:weeks.map((_,wi)=>{const realWi=r.weekly.findIndex(x=>x.week===weeks[wi].week);const wb2=getPlatWkBudEx(pr,realWi);return Math.round(cpm>0?(wb2/cpm)*1000:0);})};},
        getTot:()=>({tot:Math.round(r.platforms.reduce((s,pr)=>s+pr.impr,0)),weeks:weeks.map((_,wi)=>{const realWi=r.weekly.findIndex(x=>x.week===weeks[wi].week);return Math.round(r.platforms.reduce((s,pr)=>{const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));const cpm=(pr.p.base_cpm||0)*(1-disc);const wb2=getPlatWkBudEx(pr,realWi);return s+(cpm>0?(wb2/cpm)*1000:0);},0));})})},
      {label:'BUDGET (EUR excl. VAT)',color:DARK,numFmt:'"€"#,##0.00',
        getRow:(pr)=>({tot:pr.bud,weeks:weeks.map((_,wi)=>{const realWi=r.weekly.findIndex(x=>x.week===weeks[wi].week);return getPlatWkBudEx(pr,realWi);})}),
        getTot:()=>({tot:r.tot.budget,weeks:weeks.map((_,wi)=>{const realWi=r.weekly.findIndex(x=>x.week===weeks[wi].week);return r.platforms.reduce((s,pr)=>s+getPlatWkBudEx(pr,realWi),0);})})},
      {label:'UNIQUE COOKIES / WK',color:'70AD47',numFmt:'#,##0',
        getRow:(pr)=>{
          const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));const cpm=(pr.p.base_cpm||0)*(1-disc);
          const totCk=Math.round(pr.frq>0?(pr.impr||0)/pr.frq:0);
          return{tot:totCk,weeks:weeks.map((_,wi)=>{const realWi=r.weekly.findIndex(x=>x.week===weeks[wi].week);const wb2=getPlatWkBudEx(pr,realWi);const wi_impr=cpm>0?(wb2/cpm)*1000:0;return Math.round(pr.frq>0?wi_impr/pr.frq:0);})};},
        getTot:()=>{
          const univ2=r.universe||DEF_UNIVERSE;
          const wkVals=weeks.map((_,wi)=>{const realWi=r.weekly.findIndex(x=>x.week===weeks[wi].week);return r.platforms.reduce((s,pr)=>{const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));const cpm=(pr.p.base_cpm||0)*(1-disc);const wb2=getPlatWkBudEx(pr,realWi);const wi_impr=cpm>0?(wb2/cpm)*1000:0;return s+Math.round(pr.frq>0?wi_impr/pr.frq:0);},0);});
          // cookies TOTAL = totalImpressions / weightedAvgFrq (exact same as platform TOTAL row)
          const totI2=r.platforms.reduce((s,pr)=>s+(pr.impr||0),0)||1;
          const avgFrq2=r.platforms.reduce((s,pr)=>s+(pr.frq||0)*(pr.impr||0),0)/totI2||1;
          return{tot:Math.round(totI2/avgFrq2),weeks:wkVals};}},
      {label:'TA REAL USERS / WK',color:'FF0066',numFmt:'#,##0',note:'Cookies ÷ RUC × AudComp',
        getRow:(pr)=>{
          const ac=pr.audComp||pr.p?.audience_comp||0.65;const ruc=pr.p.real_users_coef||2.0;const univ2=r.universe||DEF_UNIVERSE;
          const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));const cpm=(pr.p.base_cpm||0)*(1-disc);
          const baseWkRU=Math.round((pr.uniqueCookies||0)/ruc*ac);const wkRch=Math.min(0.999,univ2>0?baseWkRU/univ2:0);
          let cum2=0;for(let i=0;i<nWksAll;i++){cum2=cum2+wkRch-cum2*wkRch;}
          return{tot:Math.round(Math.min(0.98,cum2)*univ2),weeks:weeks.map((_,wi)=>{const realWi=r.weekly.findIndex(x=>x.week===weeks[wi].week);const wb2=getPlatWkBudEx(pr,realWi);if(wb2===0)return 0;const wi_impr=cpm>0?(wb2/cpm)*1000:0;const wi_ck=pr.frq>0?wi_impr/pr.frq:0;return Math.round(wi_ck/ruc*ac);})};},
        getTot:()=>{
          const univ2=r.universe||DEF_UNIVERSE;
          const wkVals=weeks.map((_,wi)=>{const realWi=r.weekly.findIndex(x=>x.week===weeks[wi].week);const pools={yt:[],social:[],other:[]};r.platforms.forEach(pr=>{const ac=pr.audComp||pr.p?.audience_comp||0.65;const ruc=pr.p.real_users_coef||2.0;const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));const cpm=(pr.p.base_cpm||0)*(1-disc);const wb2=getPlatWkBudEx(pr,realWi);if(wb2===0)return;const wi_impr=cpm>0?(wb2/cpm)*1000:0;const wi_ck=pr.frq>0?wi_impr/pr.frq:0;const pool=pr.p?.reach_pool==='yt'?'yt':pr.p?.reach_pool==='social'?'social':'other';pools[pool].push({cookies:wi_ck,ruc,audComp:ac});});return Math.round(Math.min(0.98,1-(1-poolReach(pools.yt,univ2))*(1-poolReach(pools.social,univ2))*(1-poolReach(pools.other,univ2)))*univ2);});
          const pools={yt:[],social:[],other:[]};r.platforms.forEach(pr=>{const pool=pr.p?.reach_pool==='yt'?'yt':pr.p?.reach_pool==='social'?'social':'other';pools[pool].push({cookies:pr.uniqueCookies||0,ruc:pr.p.real_users_coef||2.0,audComp:pr.audComp||pr.p?.audience_comp||0.65});});
          const wkR=Math.min(0.98,1-(1-poolReach(pools.yt,univ2))*(1-poolReach(pools.social,univ2))*(1-poolReach(pools.other,univ2)));
          let cum2=0;for(let i=0;i<nWksAll;i++){cum2=cum2+wkR-cum2*wkR;}
          return{tot:Math.round(Math.min(0.98,cum2)*univ2),weeks:wkVals};}},
      {label:'VIEWABLE REAL USERS / WK',color:'C00000',numFmt:'#,##0',note:'(Cookies ÷ RUC × AudComp) × Viewability',
        getRow:(pr)=>{
          const ac=pr.audComp||pr.p?.audience_comp||0.65;const ruc=pr.p.real_users_coef||2.0;const view=pr.p.viewability||0.7;const univ2=r.universe||DEF_UNIVERSE;
          const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));const cpm=(pr.p.base_cpm||0)*(1-disc);
          const baseWkVRU=Math.round((pr.uniqueCookies||0)/ruc*ac*view);const wkRch=Math.min(0.999,univ2>0?baseWkVRU/univ2:0);
          let cum2=0;for(let i=0;i<nWksAll;i++){cum2=cum2+wkRch-cum2*wkRch;}
          return{tot:Math.round(Math.min(0.98,cum2)*univ2),weeks:weeks.map((_,wi)=>{const realWi=r.weekly.findIndex(x=>x.week===weeks[wi].week);const wb2=getPlatWkBudEx(pr,realWi);if(wb2===0)return 0;const wi_impr=cpm>0?(wb2/cpm)*1000:0;const wi_ck=pr.frq>0?wi_impr/pr.frq:0;return Math.round(wi_ck/ruc*ac*view);})};},
        getTot:()=>{
          const univ2=r.universe||DEF_UNIVERSE;
          const wkVals=weeks.map((_,wi)=>{const realWi=r.weekly.findIndex(x=>x.week===weeks[wi].week);const pools={yt:[],social:[],other:[]};r.platforms.forEach(pr=>{const ac=pr.audComp||pr.p?.audience_comp||0.65;const ruc=pr.p.real_users_coef||2.0;const view=pr.p.viewability||0.7;const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));const cpm=(pr.p.base_cpm||0)*(1-disc);const wb2=getPlatWkBudEx(pr,realWi);if(wb2===0)return;const wi_impr=cpm>0?(wb2/cpm)*1000:0;const wi_ck=pr.frq>0?wi_impr/pr.frq:0;const pool=pr.p?.reach_pool==='yt'?'yt':pr.p?.reach_pool==='social'?'social':'other';pools[pool].push({cookies:wi_ck*view,ruc,audComp:ac});});return Math.round(Math.min(0.98,1-(1-poolReach(pools.yt,univ2))*(1-poolReach(pools.social,univ2))*(1-poolReach(pools.other,univ2)))*univ2);});
          const pools={yt:[],social:[],other:[]};r.platforms.forEach(pr=>{const view=pr.p.viewability||0.7;const pool=pr.p?.reach_pool==='yt'?'yt':pr.p?.reach_pool==='social'?'social':'other';pools[pool].push({cookies:(pr.uniqueCookies||0)*view,ruc:pr.p.real_users_coef||2.0,audComp:pr.audComp||pr.p?.audience_comp||0.65});});
          const wkR=Math.min(0.98,1-(1-poolReach(pools.yt,univ2))*(1-poolReach(pools.social,univ2))*(1-poolReach(pools.other,univ2)));
          let cum2=0;for(let i=0;i<nWksAll;i++){cum2=cum2+wkR-cum2*wkR;}
          return{tot:Math.round(Math.min(0.98,cum2)*univ2),weeks:wkVals};}},
    ];

    metrics.forEach(metric=>{
      mergeHdr(ws,row,1,row,ncols,metric.label+(metric.note?' — '+metric.note:''),{bg:metric.color,color:WHITE,sz:10,align:'center'});
      ws.getRow(row).height=18;row++;
      hdr(ws.getCell(row,1),'Platform',{align:'left'});hdr(ws.getCell(row,2),'Format');hdr(ws.getCell(row,3),'Seller');hdr(ws.getCell(row,4),'TOTAL');
      weeks.forEach((wk,wi)=>hdr(ws.getCell(row,5+wi),'W'+wk.week+' '+fdate(weekStartDate(wk.week)),{sz:8,wrap:true}));
      ws.getRow(row).height=18;row++;
      let alt=false;
      r.platforms.forEach(pr=>{
        const bg=alt?GREY:null;alt=!alt;
        const d=metric.getRow(pr);
        dat(ws.getCell(row,1),pr.p.label,{bg,bold:true,color:TEXT_DARK,align:'left'});
        dat(ws.getCell(row,2),pr.p.type,{bg,align:'center'});
        dat(ws.getCell(row,3),pr.p.seller||'',{bg,align:'center'});
        dat(ws.getCell(row,4),d.tot,{bg,bold:true,align:'right',numFmt:metric.numFmt});
        d.weeks.forEach((v,wi)=>dat(ws.getCell(row,5+wi),v,{bg,align:'right',numFmt:metric.numFmt}));
        row++;
      });
      const totD=metric.getTot();
      dat(ws.getCell(row,1),'TOTAL',{bold:true,bg:GOLD,color:TEXT_DARK,align:'left'});
      dat(ws.getCell(row,2),'',{bg:GOLD});dat(ws.getCell(row,3),'',{bg:GOLD});
      dat(ws.getCell(row,4),totD.tot,{bold:true,bg:GOLD,color:TEXT_DARK,align:'right',numFmt:metric.numFmt});
      totD.weeks.forEach((v,wi)=>dat(ws.getCell(row,5+wi),v,{bold:true,bg:GOLD,color:TEXT_DARK,align:'right',numFmt:metric.numFmt}));
      row++;row++;
    });

    // Cumulative Reach
    // Compute per-week viewable reach using Sainsbury poolReach
    const univ=r.universe||DEF_UNIVERSE;
    const wkVRUreach=weeks.map((_,wi)=>{
      const pools={yt:[],social:[],other:[]};
      r.platforms.forEach(pr=>{
        const vv=wkVal(pr,wi,'vRealUsers');if(vv===0)return;
        const ruc=pr.p.real_users_coef||2.0;
        const ac=pr.audComp||pr.p?.audience_comp||0.65;
        const view=pr.p.viewability||0.7;
        const pool=pr.p?.reach_pool==='yt'?'yt':pr.p?.reach_pool==='social'?'social':'other';
        // back-calculate cookies from vRealUsers
        const ck=vv/(ac*view/ruc);
        pools[pool].push({cookies:ck*view,ruc,audComp:ac});
      });
      return Math.min(0.98,1-(1-poolReach(pools.yt,univ))*(1-poolReach(pools.social,univ))*(1-poolReach(pools.other,univ)));
    });

    mergeHdr(ws,row,1,row,8,'CUMULATIVE REACH PROGRESSION',{bg:MID_BLUE,color:WHITE,sz:10,align:'center'});row++;
    ['Week','Date','Days Active','+RCH%/wk','Cum. RCH%','Est. TA Users','+vRCH%/wk','Cum. vRCH%'].forEach((h,i)=>hdr(ws.getCell(row,i+1),h,{sz:8,wrap:true}));
    ws.getRow(row).height=20;row++;
    // Est. TA Users = Sainsbury accumulation of taUsers weekly TOTAL row values
    // Last week overridden with exact TOTAL column value for consistency
    const taUsersTotD=metrics.find(m=>m.label.startsWith('TA REAL USERS')).getTot();
    const taUsersCumWk=[];let cumTA2=0;const univ2=r.universe||DEF_UNIVERSE;
    taUsersTotD.weeks.forEach((wkAbs,wi)=>{
      const wkR=Math.min(0.999,wkAbs/univ2);
      cumTA2=Math.min(0.98,cumTA2+wkR-cumTA2*wkR);
      const isLast=wi===taUsersTotD.weeks.length-1;
      taUsersCumWk.push(isLast?taUsersTotD.tot:Math.round(cumTA2*univ2));
    });
    let cumPub=0,cumExt=0,cumV=0;
    weeks.forEach((wk,wi)=>{
      cumPub=Math.min(sb2(cumPub,wk.publisherReach||0),r.publisherCap??1);
      cumExt=sb2(cumExt,wk.externalReach||0);
      const cum=Math.min(0.98,sb2(cumPub,cumExt));
      const vwkR=wkVRUreach[wi]||0;
      cumV=Math.min(0.98,cumV+vwkR-cumV*vwkR);
      const days=Math.round(wk.frac*7);
      dat(ws.getCell(row,1),'W'+wk.week,{align:'center',bold:true});
      dat(ws.getCell(row,2),fdate(weekStartDate(wk.week)),{align:'center'});
      dat(ws.getCell(row,3),days,{align:'center',color:days<7?AMBER:'000000'});
      dat(ws.getCell(row,4),pct((wk.reach||0)*100),{align:'center',color:MID_BLUE,bold:true});
      dat(ws.getCell(row,5),pct(cum*100),{align:'center',color:DARK,bold:true});
      dat(ws.getCell(row,6),taUsersCumWk[wi],{align:'right'});
      dat(ws.getCell(row,7),pct(vwkR*100),{align:'center',color:'C00000',bold:true});
      dat(ws.getCell(row,8),pct(cumV*100),{align:'center',color:DARK,bold:true});
      row++;
    });
  }

  // Assemble workbook
  const wb=new ExcelJS.Workbook();
  wb.creator='Digital Planning Engine';
  wb.created=new Date();
  buildSummarySheet(wb);
  if(mode==='flight'){
    const fl=toExport[0];const r=allResults[fl.id];
    if(r)buildDetailSheet(wb,fl,r,null,'Weekly Detail');
  } else {
    toExport.forEach(fl=>{
      const r=allResults[fl.id];if(!r)return;
      const nm=(fl.brand.slice(0,10)+'-'+fl.label.slice(0,8)).replace(/[^\w]/g,'_').slice(0,28);
      buildDetailSheet(wb,fl,r,targetId,nm);
    });
  }

  // Download
  const buf=await wb.xlsx.writeBuffer();
  const blob=new Blob([buf],{type:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
  const url=URL.createObjectURL(blob);
  const a=document.createElement('a');a.href=url;
  a.download='MediaPlan_'+sheetTitle.replace(/[^\w ]/g,'_').slice(0,30)+'_'+new Date().toISOString().slice(0,10)+'.xlsx';
  document.body.appendChild(a);a.click();document.body.removeChild(a);URL.revokeObjectURL(url);
}

// ─── FULL PLAN EXCEL EXPORT (Flowchart / Summary / Details, all activations) ──
// Rebuilt 2026-10-05 — reproduces the three on-screen tabs 1:1 (same data builders, same
// row order/labels/grouping/totals), not a separate reduced summary. Reuses the shared
// ExcelJS helpers above (loadExcelJS/xlsxHdr/xlsxDat/xlsxMergeHdr/XLSX_COLORS/VAT) and
// `combineTot`/`aggregatePlatformGroups` — no second calculation. Only 4 fill colors are
// ever used (DARK=title/column-headers, MID_BLUE=section/group bands, LIGHT_BLUE=label
// cells, GREY=alternating rows) — no per-metric tints, one font (Calibri 10) throughout.
async function exportFullPlanExcel(flights,allResults,displayYear,planName,clientName){
  const ExcelJS=await loadExcelJS();
  if(!ExcelJS){alert('ExcelJS could not be loaded. Check your connection.');return;}
  if(!flights.length){alert('No data to export');return;}

  const {DARK,MID_BLUE,LIGHT_BLUE,GREY,WHITE,TEXT_DARK}=XLSX_COLORS;
  const todayStr=new Date().toISOString().slice(0,10);

  // Calibri-10 wrappers around the shared Arial/9 helpers — local to this export only,
  // exportClientMediaPlan's own Arial/9 styling is untouched.
  function fHdr(cell,txt,opts={}){xlsxHdr(cell,txt,{sz:10,...opts});cell.font={...cell.font,name:'Calibri'};}
  function fDat(cell,val,opts={}){xlsxDat(cell,val,{sz:10,...opts});cell.font={...cell.font,name:'Calibri'};}
  function fMerge(ws,r1,c1,r2,c2,txt,opts={}){
    xlsxMergeHdr(ws,r1,c1,r2,c2,txt,{sz:10,...opts});
    for(let r=r1;r<=r2;r++)for(let c=c1;c<=c2;c++){const cl=ws.getCell(r,c);cl.font={...cl.font,name:'Calibri'};}
  }
  function pageSetup(ws,repeatRows){
    ws.pageSetup={orientation:'landscape',fitToPage:true,fitToWidth:1,fitToHeight:0,printTitlesRow:repeatRows};
  }

  // Activations in first-seen order — same grouping key and order as AnnualFlowchart's
  // own grps.bO / SummaryTab's own `brands` list.
  const activationOrder=[];
  const flightsByActivation={};
  flights.forEach(f=>{
    const b=f.brand||'Activation 1';
    if(!flightsByActivation[b]){flightsByActivation[b]=[];activationOrder.push(b);}
    flightsByActivation[b].push(f);
  });
  const allFlights=activationOrder.flatMap(b=>flightsByActivation[b]);
  const starts=allFlights.map(f=>f.startDate).filter(Boolean).sort();
  const ends=allFlights.map(f=>f.endDate).filter(Boolean).sort();
  const planPeriod=starts.length?fdate(starts[0])+' – '+fdate(ends[ends.length-1]):'—';

  function titleRows(ws,ncols,sheetLabel){
    fMerge(ws,1,1,1,ncols,sheetLabel,{sz:14,bold:true,bg:DARK,color:WHITE,align:'center'});
    ws.getRow(1).height=22;
    const subtitle=[clientName,planName].filter(Boolean).join(' · ')+(planName||clientName?' · ':'')+planPeriod+' · Values as of '+todayStr;
    fMerge(ws,2,1,2,ncols,subtitle,{sz:9,bold:true,bg:LIGHT_BLUE,color:TEXT_DARK,align:'center'});
    ws.getRow(2).height=16;
  }

  const wb=new ExcelJS.Workbook();
  wb.creator='Digital Planning Engine';
  wb.created=new Date();

  // ── FLOWCHART SHEET(S) — one per real calendar year the plan's flights touch ────────
  function buildFlowchartSheet(sheetName,dy){
    const monthWeeksY=monthWeeksFor(dy);
    const nW=weeksInYear(dy);
    const ncols=1+nW;
    const ws=wb.addWorksheet(sheetName);
    ws.columns=[{width:24},...Array.from({length:nW},()=>({width:7}))];
    titleRows(ws,ncols,'DIGITAL MEDIA PLAN — FLOWCHART'+(sheetName!=='Flowchart'?' ('+dy+')':''));
    let row=3;

    // Month header row (merged per month's week span) + week-number row
    fHdr(ws.getCell(row,1),'',{bg:DARK,color:WHITE});
    let colc=2;
    monthWeeksY.forEach((ws_,mi)=>{
      if(!ws_.length)return;
      fMerge(ws,row,colc,row,colc+ws_.length-1,MN[mi],{bg:DARK,color:WHITE,sz:9});
      colc+=ws_.length;
    });
    row++;
    fHdr(ws.getCell(row,1),'WEEK',{bg:DARK,color:WHITE,sz:9});
    for(let w=1;w<=nW;w++)fDat(ws.getCell(row,1+w),w,{bg:DARK,color:WHITE,bold:true,align:'center',sz:8});
    row++;
    const headerRows='3:4';
    // Longest formatted value actually written into each weekly column, across every row
    // in the sheet (Budget/Impr/vRCH%/Views/Clicks/€-per-flight/Monthly€) — used to size
    // that column at the end, instead of one fixed width that overflows ("###") on large
    // impressions. Tracked inline as cells are written, not guessed up front.
    const colMaxLen=Array(nW).fill(0);
    const IMPR_COMPACT_FMT='[>=1000000]0.0,,"M";[>=1000]0,"K";0'; // same magnitude cut as fmil()
    function fmtLen(v,numFmt){
      if(v==null)return 0;
      if(numFmt==='0.0%')return((v*100).toFixed(1)+'%').length;
      if(numFmt===IMPR_COMPACT_FMT)return fmil(v).length;
      if(numFmt&&numFmt.indexOf('€')>=0)return('€'+Math.round(v).toLocaleString()).length;
      return Math.round(v).toLocaleString().length;
    }
    function trackW(colIdx,v,numFmt){
      const i=colIdx-2;if(i<0||i>=nW)return;
      const len=fmtLen(v,numFmt);
      if(len>colMaxLen[i])colMaxLen[i]=len;
    }

    let stripe=0;
    function metricRow(label,values,numFmt,colorText){
      const bg=(stripe++%2===1)?GREY:null;
      fDat(ws.getCell(row,1),label,{bg,bold:false,color:colorText||'000000'});
      for(let i=0;i<nW;i++){
        const v=values[i];
        if(v==null||v===0){fDat(ws.getCell(row,2+i),null,{bg,align:'right'});continue;}
        fDat(ws.getCell(row,2+i),v,{bg,align:'right',numFmt});
        trackW(2+i,v,numFmt);
      }
      row++;
    }
    function bandRow(text){
      fMerge(ws,row,1,row,ncols,text,{bg:MID_BLUE,color:WHITE,bold:true,align:'left',sz:10});
      row++;
      stripe=0;
    }
    function gapRow(){row++;}

    // Per-flight "€ / flight" block, ported from AnnualFlowchart's own computation
    // (budget is a real number; the flight's own cumulative vRCH% goes on a cell note,
    // same information the on-screen tooltip carries for the single-week case).
    function flightRow(objFl){
      const bg=(stripe++%2===1)?GREY:null;
      fDat(ws.getCell(row,1),'€ / flight',{bg,italic:true,color:'000000'});
      let w=1;
      while(w<=nW){
        const f=objFl.find(fl=>{const r=allResults[fl.id];if(!r)return false;const yw=r.weekly.filter(x=>x.year===dy);return yw.length&&yw[0].week===w;});
        if(!f){w++;continue;}
        const r=allResults[f.id];const yw=r.weekly.filter(x=>x.year===dy);
        if(yw.length>=nW){
          let colc2=2;
          monthWeeksY.forEach((ws_,mi)=>{
            if(!ws_.length)return;
            const mBud=ws_.reduce((s,wk)=>s+(yw.find(x=>x.week===wk)?.budget||0),0);
            if(mBud>0){
              ws.mergeCells(row,colc2,row,colc2+ws_.length-1);
              const cell=ws.getCell(row,colc2);
              fDat(cell,mBud,{bg,align:'center',numFmt:'"€"#,##0'});
              cell.note=fp(r.tot.reach)+' vRCH (flight total)';
              trackW(colc2,mBud,'"€"#,##0');
            }
            colc2+=ws_.length;
          });
          w=nW+1;
        } else {
          const span=Math.min(yw.length,(nW+1)-w);
          const blockBud=yw.reduce((s,x)=>s+x.budget,0);
          const blockRch=r.tot.reach;
          if(span>1)ws.mergeCells(row,1+w,row,1+w+span-1);
          const cell=ws.getCell(row,1+w);
          fDat(cell,blockBud>0?blockBud:null,{bg,align:'center',numFmt:'"€"#,##0'});
          cell.note=fe(blockBud)+' · '+fp(blockRch)+' vRCH (flight total)';
          trackW(1+w,blockBud,'"€"#,##0');
          w+=span;
        }
      }
      row++;
    }
    function monthlyRow(wB){
      const bg=(stripe++%2===1)?GREY:null;
      fDat(ws.getCell(row,1),'Monthly €',{bg,italic:true,color:'000000'});
      let colc2=2;
      monthWeeksY.forEach((ws_,mi)=>{
        if(!ws_.length)return;
        const v=ws_.reduce((s,w)=>s+(wB[w-1]||0),0);
        ws.mergeCells(row,colc2,row,colc2+ws_.length-1);
        fDat(ws.getCell(row,colc2),v>0?v:null,{bg,align:'center',numFmt:'"€"#,##0'});
        trackW(colc2,v>0?v:null,'"€"#,##0');
        colc2+=ws_.length;
      });
      row++;
    }

    // Per-activation sections — same grps.bO/grps.bM grouping as AnnualFlowchart, in
    // the same objective-per-activation first-seen order (not the canonical OBJECTIVES
    // order — that reordering is specific to the TOTAL block, same as on screen).
    activationOrder.forEach((brand,bi)=>{
      const objMap={};const objOrder=[];
      flightsByActivation[brand].forEach(f=>{const ob=f.objective||'awareness';if(!objMap[ob]){objMap[ob]=[];objOrder.push(ob);}objMap[ob].push(f);});
      const groupTot=combineTot(flightsByActivation[brand].map(f=>allResults[f.id]).filter(Boolean));
      if(bi>0)gapRow();
      bandRow(brand+'  ·  '+fe(groupTot.budget)+'  ·  vRCH% '+fp(groupTot.reach));
      objOrder.forEach(objId=>{
        const obj=OBJECTIVES.find(o=>o.id===objId)||{label:objId};
        const objFl=objMap[objId];
        const wB=Array(nW).fill(0),wI=Array(nW).fill(0),wV=Array(nW).fill(0),wC=Array(nW).fill(0),wR=Array(nW).fill(0),wCamp=Array(nW).fill(0);
        objFl.forEach(f=>{const r=allResults[f.id];if(!r)return;r.weekly.forEach(wd=>{if(wd.year!==dy)return;const idx=wd.week-1;if(idx<0||idx>=nW)return;wB[idx]+=wd.budget;wI[idx]+=wd.impressions;wV[idx]+=wd.views;wC[idx]+=wd.clicks;wR[idx]=Math.max(wR[idx],wd.reach||0);});});
        for(let i=0;i<nW;i++){const w=i+1;wCamp[i]=objFl.filter(f=>{const r=allResults[f.id];return r&&r.weekly&&r.weekly.some(wk=>wk.week===w&&wk.year===dy);}).length;}
        const isAw=objId==='awareness'||objId==='views',isTr=objId==='traffic'||objId==='conversion';
        bandRow(obj.label);
        metricRow('Campaigns',wCamp,'#,##0');
        metricRow('Budget / wk',wB,'"€"#,##0');
        metricRow('Impr / wk',wI,IMPR_COMPACT_FMT);
        metricRow('vRCH% / wk',wR,'0.0%');
        if(isAw&&wV.some(v=>v>0))metricRow('Views / wk',wV,'#,##0');
        if(isTr&&wC.some(v=>v>0))metricRow('Clicks / wk',wC,'#,##0');
        flightRow(objFl);
        monthlyRow(wB);
      });
    });

    // TOTAL — ALL ACTIVATIONS — canonical OBJECTIVES order, year-scoped flight set,
    // vRCH%/wk via the real per-week Sainsbury fold (sbN), never a max/average — same
    // as AnnualFlowchart's own TOTAL block.
    const yearFlights=flights.filter(f=>{const r=allResults[f.id];return r&&r.weekly&&r.weekly.some(wd=>wd.year===dy);});
    const normObj=o=>o==='views'?'consideration':(o||'awareness');
    const objBuckets={};
    yearFlights.forEach(f=>{const key=normObj(f.objective);(objBuckets[key]=objBuckets[key]||[]).push(f);});
    const grandTot=combineTot(yearFlights.map(f=>allResults[f.id]).filter(Boolean));
    gapRow();
    bandRow('TOTAL — ALL ACTIVATIONS'+(yearFlights.length?'  ·  '+fe(grandTot.budget)+'  ·  vRCH% '+fp(grandTot.reach):''));
    OBJECTIVES.forEach(obj=>{
      const objId=obj.id;
      const objFl=objBuckets[objId];
      if(!objFl||!objFl.length)return;
      const objTot=combineTot(objFl.map(f=>allResults[f.id]).filter(Boolean));
      const wB=Array(nW).fill(0),wI=Array(nW).fill(0),wV=Array(nW).fill(0),wC=Array(nW).fill(0),wR=Array(nW).fill(0),wCamp=Array(nW).fill(0);
      objFl.forEach(f=>{const r=allResults[f.id];if(!r)return;r.weekly.forEach(wd=>{if(wd.year!==dy)return;const idx=wd.week-1;if(idx<0||idx>=nW)return;wB[idx]+=wd.budget;wI[idx]+=wd.impressions;wV[idx]+=wd.views;wC[idx]+=wd.clicks;});});
      for(let i=0;i<nW;i++){const w=i+1;wCamp[i]=objFl.filter(f=>{const r=allResults[f.id];return r&&r.weekly&&r.weekly.some(wk=>wk.week===w&&wk.year===dy);}).length;
        const weekReaches=objFl.map(f=>{const r=allResults[f.id];if(!r)return null;const wd=r.weekly.find(x=>x.week===w&&x.year===dy);return wd?wd.reach:null;}).filter(v=>v!=null);
        wR[i]=weekReaches.length?sbN(weekReaches):0;
      }
      const isAw=objId==='awareness'||objId==='consideration',isTr=objId==='traffic'||objId==='conversion';
      bandRow(obj.label+'  ·  '+fe(objTot.budget)+'  ·  vRCH% '+fp(objTot.reach));
      metricRow('Campaigns',wCamp,'#,##0');
      metricRow('Budget / wk',wB,'"€"#,##0');
      metricRow('Impr / wk',wI,IMPR_COMPACT_FMT);
      metricRow('vRCH% / wk',wR,'0.0%');
      if(isAw&&wV.some(v=>v>0))metricRow('Views / wk',wV,'#,##0');
      if(isTr&&wC.some(v=>v>0))metricRow('Clicks / wk',wC,'#,##0');
      monthlyRow(wB);
    });

    // Size each weekly column from the longest formatted value actually written into it
    // (min 9, max 16) — fixes "###" on columns that needed more room than the old flat
    // width:7 gave them (e.g. a plain impressions column before the Impr/wk row switched
    // to the compact M/K format above).
    for(let i=0;i<nW;i++)ws.getColumn(2+i).width=Math.max(9,Math.min(16,colMaxLen[i]+1));

    // Outline-group (collapsed by default) the leading/trailing weeks with no data in any
    // activation this year — "data" = any flight's real per-week budget>0. Only collapses
    // whole leading/trailing runs, never weeks in between; never touches the month-header
    // merges themselves, which stay keyed to the full nW week range regardless of which
    // columns are hidden.
    {
      const weeksWithData=new Set();
      flights.forEach(f=>{const r=allResults[f.id];if(!r)return;r.weekly.forEach(wd=>{if(wd.year===dy&&wd.budget>0)weeksWithData.add(wd.week);});});
      if(weeksWithData.size){
        const used=[...weeksWithData];
        const minW=Math.min(...used),maxW=Math.max(...used);
        for(let w=1;w<minW;w++){const col=ws.getColumn(1+w);col.outlineLevel=1;col.hidden=true;}
        for(let w=maxW+1;w<=nW;w++){const col=ws.getColumn(1+w);col.outlineLevel=1;col.hidden=true;}
      }
    }

    pageSetup(ws,headerRows);
    ws.views=[{state:'frozen',xSplit:1,ySplit:4}];
  }

  const yearsTouched=[...new Set(allFlights.flatMap(f=>{const r=allResults[f.id];return r?r.weekly.map(wd=>wd.year):[];}))].sort();
  const yearsToRender=yearsTouched.length?yearsTouched:[displayYear||YEAR];
  yearsToRender.forEach(yr=>buildFlowchartSheet(yearsToRender.length>1?'Flowchart '+yr:'Flowchart',yr));

  // ── SUMMARY SHEET — same groups/columns/totals as SummaryTab, one block per activation
  // plus TOTAL — ALL BRANDS, reusing aggregatePlatformGroups for the per-platform rows and
  // the identical rowVal/groupVal/totalVal formula set (ported, not re-derived) ──────────
  function buildSummarySheet(){
    const ws=wb.addWorksheet('Summary');
    const cols=SUMMARY_COLS.filter(c=>SUMCOLS_DEFAULT.includes(c.key));
    const ncols=1+cols.length;
    ws.columns=[{width:30},...cols.map(c=>({width:Math.max(10,Math.round(c.w/7))}))];
    titleRows(ws,ncols,'DIGITAL MEDIA PLAN — SUMMARY');
    let row=3;

    function sumRowVal(key,row_,totBudAll,totImprAll){
      const{budget,mBud,impr,vimpr,clicks,conversions,type}=row_;
      switch(key){
        case 'buying_unit': return type==='sem'?'CPC':'CPM';
        case 'impressions': return Math.round(impr)||null;
        case 'cpm': return(type!=='sem'&&impr>0)?(budget/impr)*1000:null;
        case 'clicks': return Math.round(clicks)||null;
        case 'cpc': return clicks>0?(type==='sem'?mBud:budget)/clicks:null;
        case 'ctr': return impr>0?clicks/impr:null;
        case 'viewability': return(type!=='sem'&&impr>0)?vimpr/impr:null;
        case 'vcpm': return(type!=='sem'&&vimpr>0)?(budget/vimpr)*1000:null;
        case 'conversions': return conversions>0?Math.round(conversions):null;
        case 'cost_conv': return conversions>0?budget/conversions:null;
        case 'budget': return budget;
        case 'shr_bud': return totBudAll>0?budget/totBudAll:null;
        case 'shr_impr': return totImprAll>0?impr/totImprAll:null;
        default: return null;
      }
    }
    function sumGroupVal(key,rowsArr,totBudAll,totImprAll){
      const gBud=rowsArr.reduce((s,r)=>s+r.budget,0);
      const gImpr=rowsArr.reduce((s,r)=>s+r.impr,0);
      const gClicks=rowsArr.reduce((s,r)=>s+r.clicks,0);
      const gConv=rowsArr.reduce((s,r)=>s+r.conversions,0);
      const gCpcBud=rowsArr.reduce((s,r)=>s+(r.type==='sem'?(r.mBud??r.budget):r.budget),0);
      const ns=rowsArr.filter(r=>r.type!=='sem');
      const nsImpr=ns.reduce((s,r)=>s+r.impr,0),nsBud=ns.reduce((s,r)=>s+r.budget,0),nsVimpr=ns.reduce((s,r)=>s+r.vimpr,0);
      switch(key){
        case 'buying_unit':{const u=[...new Set(rowsArr.map(r=>r.type==='sem'?'CPC':'CPM'))];return u.length===1?u[0]:'Mixed';}
        case 'impressions': return Math.round(gImpr)||null;
        case 'cpm': return nsImpr>0?(nsBud/nsImpr)*1000:null;
        case 'clicks': return Math.round(gClicks)||null;
        case 'cpc': return gClicks>0?gCpcBud/gClicks:null;
        case 'ctr': return gImpr>0?gClicks/gImpr:null;
        case 'viewability': return nsImpr>0?nsVimpr/nsImpr:null;
        case 'vcpm': return nsVimpr>0?(nsBud/nsVimpr)*1000:null;
        case 'conversions': return gConv>0?Math.round(gConv):null;
        case 'cost_conv': return gConv>0?gBud/gConv:null;
        case 'budget': return gBud;
        case 'shr_bud': return totBudAll>0?gBud/totBudAll:null;
        case 'shr_impr': return totImprAll>0?gImpr/totImprAll:null;
        default: return null;
      }
    }
    function sumTotalVal(key,all,totBudAll){
      const tImpr=all.reduce((s,r)=>s+r.impr,0),tClicks=all.reduce((s,r)=>s+r.clicks,0),tConv=all.reduce((s,r)=>s+r.conversions,0);
      const tCpcBud=all.reduce((s,r)=>s+(r.type==='sem'?(r.mBud??r.budget):r.budget),0);
      const ns=all.filter(r=>r.type!=='sem');
      const nsImpr=ns.reduce((s,r)=>s+r.impr,0),nsBud=ns.reduce((s,r)=>s+r.budget,0),nsVimpr=ns.reduce((s,r)=>s+r.vimpr,0);
      switch(key){
        case 'buying_unit': return 'Mixed';
        case 'impressions': return Math.round(tImpr)||null;
        case 'cpm': return nsImpr>0?(nsBud/nsImpr)*1000:null;
        case 'clicks': return Math.round(tClicks)||null;
        case 'cpc': return tClicks>0?tCpcBud/tClicks:null;
        case 'ctr': return tImpr>0?tClicks/tImpr:null;
        case 'viewability': return nsImpr>0?nsVimpr/nsImpr:null;
        case 'vcpm': return nsVimpr>0?(nsBud/nsVimpr)*1000:null;
        case 'conversions': return tConv>0?Math.round(tConv):null;
        case 'cost_conv': return tConv>0?totBudAll/tConv:null;
        case 'budget': return totBudAll;
        case 'shr_bud': return 1;
        case 'shr_impr': return 1;
        default: return null;
      }
    }
    const CELL_FMT={impressions:'#,##0',cpm:'"€"#,##0.00',clicks:'#,##0',cpc:'"€"#,##0.00',ctr:'0.00%',viewability:'0.0%',vcpm:'"€"#,##0.00',conversions:'#,##0',cost_conv:'"€"#,##0.00',budget:'"€"#,##0.00',shr_bud:'0.0%',shr_impr:'0.0%'};

    [...activationOrder,'__TOTAL__'].forEach(brand=>{
      const isT=brand==='__TOTAL__';
      const flsA=isT?allFlights:flightsByActivation[brand];
      const {groups,totTrk,totAf}=aggregatePlatformGroups(flsA,allResults);
      const totBudAll=Object.values(groups).reduce((s,g)=>s+Object.values(g.rows).reduce((ss,r)=>ss+r.budget,0),0);
      const totImprAll=Object.values(groups).reduce((s,g)=>s+Object.values(g.rows).reduce((ss,r)=>ss+r.impr,0),0);
      if(totBudAll===0&&!isT)return;

      fMerge(ws,row,1,row,ncols,isT?'TOTAL — ALL BRANDS':brand,{bg:MID_BLUE,color:WHITE,bold:true,align:'left',sz:11});
      row++;
      fHdr(ws.getCell(row,1),'Platform / Format',{bg:DARK,color:WHITE,align:'left'});
      cols.forEach((c,i)=>fHdr(ws.getCell(row,2+i),c.label,{bg:DARK,color:WHITE,align:'center',wrap:true}));
      row++;
      let stripe=0;
      SUMMARY_GROUPS.forEach(sg=>{
        const g=groups[sg.key];const rowsArr=Object.values(g.rows);if(!rowsArr.length)return;
        fDat(ws.getCell(row,1),g.label,{bg:LIGHT_BLUE,bold:true,color:TEXT_DARK});
        cols.forEach((c,i)=>{
          const v=sumGroupVal(c.key,rowsArr,totBudAll,totImprAll);
          fDat(ws.getCell(row,2+i),v,{bg:LIGHT_BLUE,bold:true,align:'center',color:TEXT_DARK,numFmt:CELL_FMT[c.key]});
        });
        row++;stripe=0;
        rowsArr.forEach(r=>{
          const bg=(stripe++%2===1)?GREY:null;
          fDat(ws.getCell(row,1),r.label+(r.seller?' ('+r.seller+')':''),{bg});
          cols.forEach((c,i)=>{
            const v=sumRowVal(c.key,r,totBudAll,totImprAll);
            fDat(ws.getCell(row,2+i),v,{bg,align:'center',numFmt:CELL_FMT[c.key]});
          });
          row++;
        });
      });
      const all=Object.values(groups).flatMap(g=>Object.values(g.rows));
      fDat(ws.getCell(row,1),'TOTAL ONLINE',{bg:DARK,color:WHITE,bold:true});
      cols.forEach((c,i)=>{
        const v=sumTotalVal(c.key,all,totBudAll);
        fDat(ws.getCell(row,2+i),v,{bg:DARK,color:WHITE,bold:true,align:'center',numFmt:CELL_FMT[c.key]});
      });
      row++;
      if(totTrk>0){
        fDat(ws.getCell(row,1),'3rd Party Trackers (Gemius / DV / IAS)',{bg:GREY,italic:true});
        cols.forEach((c,i)=>fDat(ws.getCell(row,2+i),c.key==='budget'?totTrk:null,{bg:GREY,align:'center',numFmt:c.key==='budget'?CELL_FMT.budget:null}));
        row++;
      }
      if(totAf>0){
        fDat(ws.getCell(row,1),'Agency Fee',{bg:GREY,italic:true});
        cols.forEach((c,i)=>fDat(ws.getCell(row,2+i),c.key==='budget'?totAf:null,{bg:GREY,align:'center',numFmt:c.key==='budget'?CELL_FMT.budget:null}));
        row++;
      }
      if(totTrk>0||totAf>0){
        fDat(ws.getCell(row,1),'TOTAL INCL. FEES',{bg:DARK,color:WHITE,bold:true});
        cols.forEach((c,i)=>fDat(ws.getCell(row,2+i),c.key==='budget'?(totBudAll+totTrk+totAf):null,{bg:DARK,color:WHITE,bold:true,align:'center',numFmt:c.key==='budget'?CELL_FMT.budget:null}));
        row++;
      }
      row++; // spacer between blocks
    });

    ws.views=[{state:'frozen',xSplit:1,ySplit:2}];
    pageSetup(ws,'1:2');
  }
  buildSummarySheet();

  // ── DETAILS SHEET — same METRIC_SECTIONS/weekly tables as DetailsTab, one block per
  // flight (the tab is single-flight-scoped via its own dropdown; concatenating every
  // flight's block, in the same order the dropdown lists them, is the faithful "ALL
  // activations" extension — not an invented grouping) ────────────────────────────────
  function buildDetailsSheet(){
    const ws=wb.addWorksheet('Details');
    ws.columns=[{width:26},{width:10}];
    titleRows(ws,10,'DIGITAL MEDIA PLAN — DETAILS');
    let row=3;
    // Same "###" fix as the Flowchart sheet: track the longest formatted value actually
    // written into column 2 (KPI strip values share this column with the metric tables'
    // narrower "Format" labels) and into the week/TOTAL columns (3+, shared across every
    // flight block regardless of how many weeks that flight has), then size both at the
    // end instead of the fixed width:10/no-width-at-all this sheet started with.
    let col2MaxLen=10,weekColMaxLen=0,maxColUsed=2;
    function trackDetailsW(colIdx,len){
      if(colIdx===2){if(len>col2MaxLen)col2MaxLen=len;}
      else{if(len>weekColMaxLen)weekColMaxLen=len;if(colIdx>maxColUsed)maxColUsed=colIdx;}
    }
    function detailsLen(v,numFmt){
      if(v==null)return 0;
      if(numFmt==='0.0%')return((v*100).toFixed(1)+'%').length;
      if(numFmt==='0.0')return v.toFixed(1).length;
      if(numFmt&&numFmt.indexOf('.00')>=0)return('€'+v.toFixed(2)).length;
      if(numFmt&&numFmt.indexOf('€')>=0)return('€'+Math.round(v).toLocaleString()).length;
      return Math.round(v).toLocaleString().length;
    }

    function flightMetricSections(r,fl){
      const wFT=(r.weekly||[]).reduce((s,wk)=>s+wk.frac,0)||1;
      function platWkBud(pr,wi){
        const wb_=r.wkBudgets?.[wi]??0;
        const wkMixPlats=fl?.weeklyOverrides?.[wi]?.platforms||null;
        const activePlats=wkMixPlats||(fl?.platforms||[]);
        const pe=activePlats.find(x=>x.platformId===pr.p.id);
        if(!pe)return 0;
        return wb_*((pe.shr||0)/100);
      }
      function platWeekly(field){
        const wkBuds=r.wkBudgets||[];
        const fl_platforms=fl?.platforms||[];
        const wkOverrides=fl?.weeklyOverrides||{};
        return r.platforms.map(pr=>{
          const tot=pr[field]||0;
          const weeks=(r.weekly||[]).map((wk,wi)=>{
            const wb_=wkBuds[wi]??0;
            const wkMixPlats=wkOverrides[wi]?.platforms||null;
            const activePlats=wkMixPlats||fl_platforms;
            const pe=activePlats.find(x=>x.platformId===pr.p.id);
            if(!pe)return 0;
            const platBud=wb_*((pe.shr||0)/100);
            const disc=Math.max(0,Math.min(0.90,pe.discount||0));
            const cpm=(pr.p.base_cpm||0)*(1-disc);
            if(field==='impr')return cpm>0?(platBud/cpm)*1000:0;
            if(field==='vimpr')return cpm>0?(platBud/cpm)*1000*(pr.p.viewability||0.7):0;
            if(field==='views')return cpm>0?(platBud/cpm)*1000*(pr.p.vtr||0):0;
            if(field==='clicks')return cpm>0?(platBud/cpm)*1000*(pr.p.ctr||0):0;
            return tot*(platBud/(r.tot.budget||1));
          });
          return{pid:pr.p.id,label:pr.p.label,seller:pr.p.seller||'',type:pr.p.type,tot,weeks};
        });
      }
      const SECTIONS=[
        {key:'impressions',label:'Impressions',getRows:()=>platWeekly('impr')},
        {key:'frq',label:'FRQ (avg/wk)',isFixed:true,getRows:()=>r.platforms.map(pr=>({pid:pr.p.id,label:pr.p.label,seller:pr.p.seller||'',type:pr.p.type,tot:pr.frq||0,weeks:(r.weekly||[]).map(()=>pr.frq||0)}))},
        {key:'cookies',label:'Unique Cookies / wk',getRows:()=>r.platforms.map(pr=>{const totCk=Math.round(pr.frq>0?(pr.impr||0)/pr.frq:0);const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));const cpm=(pr.p.base_cpm||0)*(1-disc);const weeks=(r.weekly||[]).map((_,wi)=>{const wb_=platWkBud(pr,wi);const wi_impr=cpm>0?(wb_/cpm)*1000:0;return Math.round(pr.frq>0?wi_impr/pr.frq:0);});return{pid:pr.p.id,label:pr.p.label,seller:pr.p.seller||'',type:pr.p.type,tot:totCk,weeks};})},
        {key:'taUsers',label:'TA Real Users / wk',getRows:()=>{const nWks=r.weekly&&r.weekly.length||1;return r.platforms.map(pr=>{const ac=pr.audComp||pr.p?.audience_comp||0.65;const ruc=pr.p.real_users_coef||2.0;const univ=r.universe||DEF_UNIVERSE;const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));const cpm=(pr.p.base_cpm||0)*(1-disc);const baseWkRU=Math.round((pr.uniqueCookies||0)/ruc*ac);const wkRch=Math.min(0.999,baseWkRU/univ);let cum=0;for(let i=0;i<nWks;i++){cum=cum+wkRch-cum*wkRch;}const totRU=Math.round(Math.min(0.98,cum)*univ);const weeks=(r.weekly||[]).map((_,wi)=>{const wb_=platWkBud(pr,wi);if(wb_===0)return 0;const wi_impr=cpm>0?(wb_/cpm)*1000:0;const wi_ck=pr.frq>0?wi_impr/pr.frq:0;return Math.round(wi_ck/ruc*ac);});return{pid:pr.p.id,label:pr.p.label,seller:pr.p.seller||'',type:pr.p.type,tot:totRU,weeks};});}},
        {key:'vRealUsers',label:'Viewable Real Users / wk',getRows:()=>{const nWks=r.weekly&&r.weekly.length||1;return r.platforms.map(pr=>{const ac=pr.audComp||pr.p?.audience_comp||0.65;const ruc=pr.p.real_users_coef||2.0;const view=pr.p.viewability||0.7;const univ=r.universe||DEF_UNIVERSE;const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));const cpm=(pr.p.base_cpm||0)*(1-disc);const baseWkVRU=Math.round((pr.uniqueCookies||0)/ruc*ac*view);const wkRch=Math.min(0.999,baseWkVRU/univ);let cum=0;for(let i=0;i<nWks;i++){cum=cum+wkRch-cum*wkRch;}const totVRU=Math.round(Math.min(0.98,cum)*univ);const weeks=(r.weekly||[]).map((_,wi)=>{const wb_=platWkBud(pr,wi);if(wb_===0)return 0;const wi_impr=cpm>0?(wb_/cpm)*1000:0;const wi_ck=pr.frq>0?wi_impr/pr.frq:0;return Math.round(wi_ck/ruc*ac*view);});return{pid:pr.p.id,label:pr.p.label,seller:pr.p.seller||'',type:pr.p.type,tot:totVRU,weeks};});}},
        {key:'clicks',label:'Clicks',getRows:()=>platWeekly('clicks')},
        {key:'conversions',label:'Conversions',getRows:()=>{const wkBuds=r.wkBudgets||[];const totBud=r.tot.budget||1;return r.platforms.map(pr=>{const cvr=pr.p.cvr||0;const tot=pr.clicks*(cvr||0);const weeks=wkBuds.length>0?wkBuds.map(wb_=>tot*(wb_/totBud)):(r.weekly||[]).map(wk=>tot*(wk.frac/wFT));return{pid:pr.p.id,label:pr.p.label,seller:pr.p.seller||'',type:pr.p.type,tot,weeks};});}},
        {key:'views',label:'Views',getRows:()=>platWeekly('views')},
        {key:'vimpr',label:'Viewable Impressions',getRows:()=>platWeekly('vimpr')},
      ];
      return SECTIONS.map(sec=>{
        const rowsArr=sec.getRows();
        let totRow;
        if(sec.isFixed){
          const totI=r.platforms.reduce((s,pr)=>s+(pr.impr||0),0)||1;
          const wAvg=r.platforms.reduce((s,pr)=>s+(pr.frq||0)*(pr.impr||0),0)/totI;
          totRow={label:'Total',tot:wAvg,weeks:(r.weekly||[]).map(()=>wAvg)};
        } else {
          totRow={label:'Total',tot:rowsArr.reduce((s,rr)=>s+rr.tot,0),weeks:(r.weekly||[]).map((_,i)=>rowsArr.reduce((s,rr)=>s+(rr.weeks[i]||0),0))};
        }
        return{...sec,rows:rowsArr,totRow};
      });
    }

    flights.forEach((fl,fi)=>{
      const r=allResults[fl.id];if(!r||!r.platforms||!r.platforms.length)return;
      if(fi>0)row++;
      fMerge(ws,row,1,row,2+Math.max(1,r.weekly.length),fl.brand+' — '+fl.label+'  ('+fdate(fl.startDate)+' – '+fdate(fl.endDate)+')',{bg:MID_BLUE,color:WHITE,bold:true,align:'left'});
      row++;
      // Campaign KPI strip
      const kpis=[['Budget',r.tot.budget,'"€"#,##0'],['TA Impressions',r.tot.impressions,'#,##0'],['vRCH%',r.tot.reach,'0.0%'],['vRCH abs.',Math.round(r.tot.reach*(r.universe||DEF_UNIVERSE)),'#,##0'],['eCPM',r.tot.ecpm,'"€"#,##0.00'],['vCPM',r.tot.vcpm,'"€"#,##0.00'],['Avg Freq.',r.tot.frequency,'0.0'],['Views',r.tot.views,'#,##0'],['Clicks',r.tot.clicks,'#,##0']];
      kpis.forEach(([label,val,fmt])=>{
        fDat(ws.getCell(row,1),label,{bg:LIGHT_BLUE,bold:true,color:TEXT_DARK});
        fDat(ws.getCell(row,2),val,{bold:true,align:'left',numFmt:fmt});
        trackDetailsW(2,detailsLen(val,fmt));
        row++;
      });
      row++;

      const sections=flightMetricSections(r,fl);
      sections.forEach(sec=>{
        const ncols2=2+r.weekly.length+1;
        fMerge(ws,row,1,row,ncols2,sec.label,{bg:MID_BLUE,color:WHITE,bold:true,align:'left'});row++;
        fHdr(ws.getCell(row,1),'Platform',{bg:DARK,color:WHITE,align:'left'});
        fHdr(ws.getCell(row,2),'Format',{bg:DARK,color:WHITE});
        r.weekly.forEach((wk,wi)=>fHdr(ws.getCell(row,3+wi),'W'+wk.week,{bg:DARK,color:WHITE,sz:8}));
        fHdr(ws.getCell(row,3+r.weekly.length),'TOTAL',{bg:DARK,color:WHITE});
        row++;
        let stripe=0;
        const secFmt=sec.isFixed?'0.0':'#,##0';
        [...sec.rows,sec.totRow].forEach(rr=>{
          const isTotal=rr.pid==null;
          const bg=isTotal?LIGHT_BLUE:((stripe++%2===1)?GREY:null);
          fDat(ws.getCell(row,1),isTotal?'Total':rr.label,{bg,bold:isTotal});
          fDat(ws.getCell(row,2),isTotal?'':(rr.type||''),{bg,align:'center'});
          rr.weeks.forEach((v,wi)=>{const vv=sec.isFixed?v:Math.round(v);fDat(ws.getCell(row,3+wi),vv||null,{bg,align:'right',numFmt:secFmt});trackDetailsW(3+wi,detailsLen(vv,secFmt));});
          const tv=sec.isFixed?rr.tot:Math.round(rr.tot);
          fDat(ws.getCell(row,3+r.weekly.length),tv||null,{bg,bold:true,align:'right',numFmt:secFmt});
          trackDetailsW(3+r.weekly.length,detailsLen(tv,secFmt));
          row++;
        });
        row++;
      });

      // Cumulative reach table — verbatim port of DetailsTab's own `cumRch` useMemo
      // (pool-based viewable-real-users fold via the shared `poolReach`), not an
      // approximation — Cum. RCH% and Cum. View.Users are genuinely different folds.
      const univ=r.universe||DEF_UNIVERSE;
      const wkOverridesG=fl?.weeklyOverrides||{};
      const fl_platformsG=fl?.platforms||[];
      function platWkBudG(pr,wi){
        const wb_=r.wkBudgets?.[wi]??0;
        const wkMixPlats=wkOverridesG[wi]?.platforms||null;
        const activePlats=wkMixPlats||fl_platformsG;
        const pe=activePlats.find(x=>x.platformId===pr.p.id);
        if(!pe)return 0;
        return wb_*((pe.shr||0)/100);
      }
      const wkVRU=r.weekly.map((wk,wi)=>{
        const pools={yt:[],social:[],other:[]};
        r.platforms.forEach(pr=>{
          const wb_=platWkBudG(pr,wi);if(wb_===0)return;
          const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));
          const cpm=(pr.p.base_cpm||0)*(1-disc);
          const wi_impr=cpm>0?(wb_/cpm)*1000:0;
          const wi_ck=pr.frq>0?wi_impr/pr.frq:0;
          const ac=pr.audComp||pr.p?.audience_comp||0.65;
          const ruc=pr.p.real_users_coef||2.0;
          const view=pr.p.viewability||0.7;
          const pool=pr.p?.reach_pool==='yt'?'yt':pr.p?.reach_pool==='social'?'social':'other';
          pools[pool].push({cookies:wi_ck*view,ruc,audComp:ac});
        });
        const rYT=poolReach(pools.yt,univ),rSoc=poolReach(pools.social,univ),rOth=poolReach(pools.other,univ);
        return Math.min(0.98,1-(1-rYT)*(1-rSoc)*(1-rOth));
      });
      fMerge(ws,row,1,row,6,'CUMULATIVE REACH PROGRESSION',{bg:MID_BLUE,color:WHITE,bold:true,align:'left'});row++;
      ['Week','Days Active','+RCH%/wk','Cum. RCH%','+View.Users/wk','Cum. View.Users'].forEach((h,i)=>fHdr(ws.getCell(row,1+i),h,{bg:DARK,color:WHITE,sz:8,wrap:true}));
      row++;
      let cumPub=0,cumExt=0,cumVRU=0;
      r.weekly.forEach((wk,i)=>{
        cumPub=Math.min(sb2(cumPub,wk.publisherReach||0),r.publisherCap??1);
        cumExt=sb2(cumExt,wk.externalReach||0);
        const cum=Math.min(0.98,sb2(cumPub,cumExt));
        cumVRU=sb2(cumVRU,wkVRU[i]||0);
        const days=Math.round(wk.frac*7);
        const bg=(i%2===1)?GREY:null;
        fDat(ws.getCell(row,1),'W'+wk.week,{bg});
        fDat(ws.getCell(row,2),days,{bg,align:'center'});
        fDat(ws.getCell(row,3),wk.reach||0,{bg,align:'center',numFmt:'0.0%'});
        fDat(ws.getCell(row,4),cum,{bg,bold:true,align:'center',numFmt:'0.0%'});
        fDat(ws.getCell(row,5),Math.round((wkVRU[i]||0)*univ),{bg,align:'center',numFmt:'#,##0'});
        fDat(ws.getCell(row,6),Math.round(cumVRU*univ),{bg,bold:true,align:'center',numFmt:'#,##0'});
        [3,4].forEach(c=>trackDetailsW(c,5));
        trackDetailsW(5,detailsLen(Math.round((wkVRU[i]||0)*univ),'#,##0'));
        trackDetailsW(6,detailsLen(Math.round(cumVRU*univ),'#,##0'));
        row++;
      });
      row++;
    });

    ws.getColumn(2).width=Math.max(10,Math.min(16,col2MaxLen+1));
    for(let c=3;c<=Math.max(maxColUsed,6);c++)ws.getColumn(c).width=Math.max(9,Math.min(16,weekColMaxLen+1));
    ws.views=[{state:'frozen',xSplit:1,ySplit:2}];
    pageSetup(ws,'1:2');
  }
  buildDetailsSheet();

  const buf=await wb.xlsx.writeBuffer();
  const blob=new Blob([buf],{type:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
  const url=URL.createObjectURL(blob);
  const a=document.createElement('a');a.href=url;
  const safeClient=(clientName||'Client').replace(/[^\w]/g,'_').slice(0,24);
  const safePlan=(planName||'Plan').replace(/[^\w]/g,'_').slice(0,30);
  a.download='MediaPlan_'+safePlan+'_'+safeClient+'_ALL_'+todayStr+'.xlsx';
  document.body.appendChild(a);a.click();document.body.removeChild(a);URL.revokeObjectURL(url);
}

// ─── EXPORT MODAL ─────────────────────────────────────────────────────────────
function ExportMediaPlanModal({flights,allResults,onClose}){
  const[mode,setMode]=useState('flight');
  const[flightId,setFlightId]=useState(flights[0]?.id||'');
  const[monthIdx,setMonthIdx]=useState(null);

  // Compute which months have activity across all flights
  const activeMonths=useMemo(()=>{
    const months=new Set();
    flights.forEach(fl=>{const r=allResults[fl.id];if(!r)return;r.weekly.forEach(wk=>months.add(weekMonth(wk.week,wk.year)));});
    return [...months].sort((a,b)=>a-b);
  },[flights,allResults]);
  // Representative real year for each active month (a plan can technically span two ISO
  // week-years) — derived from the flights' own weekly data, not the hardcoded YEAR constant.
  const monthYearMap=useMemo(()=>{
    const m=new Map();
    flights.forEach(fl=>{const r=allResults[fl.id];if(!r)return;r.weekly.forEach(wk=>{const mi=weekMonth(wk.week,wk.year);if(!m.has(mi))m.set(mi,wk.year||YEAR);});});
    return m;
  },[flights,allResults]);

  // Set default month
  React.useEffect(()=>{if(activeMonths.length&&monthIdx===null)setMonthIdx(activeMonths[0]);},[activeMonths]);

  function doExport(){
    exportClientMediaPlan(flights,allResults,mode,mode==='flight'?flightId:monthIdx);
    onClose();
  }

  return(<div className="modal-overlay" onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
    <div className="modal-box" style={{maxWidth:440}} onClick={e=>e.stopPropagation()}>
      <div className="modal-hdr">
        <span style={{fontWeight:700,fontSize:13}}>⬇ Export Client Media Plan</span>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
      </div>
      <div style={{padding:'16px 20px',display:'flex',flexDirection:'column',gap:14}}>
        <div style={{fontSize:11,color:'var(--text3)'}}>Select what to include in the export. Each option generates a <b>Summary</b> sheet + <b>Weekly Detail</b> sheet.</div>
        <div style={{display:'flex',gap:8}}>
          {[{v:'flight',Icon:Plane,l:'By Flight'},{v:'month',Icon:Calendar,l:'By Month'}].map(({v,Icon:Ic,l})=>(
            <button key={v} onClick={()=>setMode(v)} style={{flex:1,padding:'8px',borderRadius:8,border:'2px solid '+(mode===v?'var(--teal)':'var(--border)'),background:mode===v?'#00807015':'var(--surface2)',fontWeight:mode===v?700:400,fontSize:11,cursor:'pointer',color:mode===v?'var(--teal)':'var(--text)',display:'flex',alignItems:'center',justifyContent:'center',gap:5}}>
              <Ic size={12}/>{l}
            </button>
          ))}
        </div>
        {mode==='flight'&&(
          <div>
            <div style={{fontSize:10,fontWeight:600,color:'var(--text3)',marginBottom:6}}>Select flight:</div>
            <div style={{display:'flex',flexDirection:'column',gap:4}}>
              {flights.map(fl=>{
                const r=allResults[fl.id];
                return(<div key={fl.id} onClick={()=>setFlightId(fl.id)} style={{padding:'8px 12px',borderRadius:6,border:'2px solid '+(flightId===fl.id?'var(--gold-dark)':'var(--border)'),background:flightId===fl.id?'var(--gold-bg)':'var(--surface2)',cursor:'pointer',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div>
                    <div style={{fontWeight:600,fontSize:11,color:'var(--text)'}}>{fl.brand} — {fl.label}</div>
                    <div style={{fontSize:9,color:'var(--text3)'}}>{fdate(fl.startDate)} – {fdate(fl.endDate)}</div>
                  </div>
                  <div style={{fontWeight:700,fontSize:11,color:'var(--gold-dark)'}}>{r?fe(r.tot.budget):'—'}</div>
                </div>);
              })}
            </div>
          </div>
        )}
        {mode==='month'&&(
          <div>
            <div style={{fontSize:10,fontWeight:600,color:'var(--text3)',marginBottom:6}}>Select month (all active flights included):</div>
            <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
              {activeMonths.map(mi=>(
                <button key={mi} onClick={()=>setMonthIdx(mi)} style={{padding:'6px 14px',borderRadius:6,border:'2px solid '+(monthIdx===mi?'var(--teal)':'var(--border)'),background:monthIdx===mi?'#00807015':'var(--surface2)',fontWeight:monthIdx===mi?700:400,fontSize:11,cursor:'pointer',color:monthIdx===mi?'var(--teal)':'var(--text)'}}>
                  {MN[mi]} {monthYearMap.get(mi)||YEAR}
                </button>
              ))}
            </div>
            <div style={{fontSize:9,color:'var(--text3)',marginTop:8}}>
              Active flights in selected month: <b>{monthIdx!==null?flights.filter(fl=>{const r=allResults[fl.id];return r&&r.weekly.some(wk=>weekMonth(wk.week,wk.year)===monthIdx);}).map(f=>f.brand+' — '+f.label).join(', '):'—'}</b>
            </div>
          </div>
        )}
      </div>
      <div className="modal-footer">
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={doExport}>⬇ Download Excel</button>
      </div>
    </div>
  </div>);
}

// ─── SUMMARY TAB — column definitions ────────────────────────────────────────
const SUMMARY_COLS=[
  {key:'buying_unit', label:'Buying Unit',    w:62,  align:'center'},
  {key:'impressions', label:'Impressions',    w:90,  align:'right'},
  {key:'cpm',         label:'CPM €',          w:72,  align:'center'},
  {key:'clicks',      label:'Clicks',         w:80,  align:'right'},
  {key:'cpc',         label:'CPC €',          w:72,  align:'center'},
  {key:'ctr',         label:'CTR%',           w:58,  align:'center'},
  {key:'viewability', label:'Viewability',    w:74,  align:'center'},
  {key:'vcpm',        label:'vCPM €',         w:72,  align:'center'},
  {key:'conversions', label:'Conversions',    w:84,  align:'right'},
  {key:'cost_conv',   label:'Cost/Conv. €',   w:84,  align:'right'},
  {key:'budget',      label:'Budget €',       w:90,  align:'right'},
  {key:'shr_bud',     label:'SHR% Bud.',      w:68,  align:'center'},
  {key:'shr_impr',    label:'SHR% Impr.',     w:68,  align:'center'},
  {key:'engagement_rate', label:'Engagement Rate', w:88, align:'center'},
  {key:'vtr',             label:'VTR%',            w:64, align:'center'},
];
const SUMCOLS_DEFAULT=['buying_unit','impressions','cpm','clicks','cpc','ctr','viewability','budget','shr_bud','shr_impr'];
function _sumColsLoad(){try{const s=localStorage.getItem('dpe_sum_cols');if(s)return JSON.parse(s);}catch(e){}return SUMCOLS_DEFAULT;}

function SummaryTab({flights,allResults,onExportExcel}){
  const brands=[...new Set(flights.map(f=>f.brand||'Brand 1'))];
  const[visCols,setVisCols]=useState(_sumColsLoad);
  const[colModal,setColModal]=useState(false);
  const[exportModal,setExportModal]=useState(false);

  function toggleCol(key){
    setVisCols(prev=>{
      const next=prev.includes(key)?prev.filter(k=>k!==key):[...prev,key];
      try{localStorage.setItem('dpe_sum_cols',JSON.stringify(next));}catch(e){}
      return next;
    });
  }
  function resetCols(){
    setVisCols(SUMCOLS_DEFAULT);
    try{localStorage.setItem('dpe_sum_cols',JSON.stringify(SUMCOLS_DEFAULT));}catch(e){}
  }

  function buildData(bf){
    const groups={};
    SUMMARY_GROUPS.forEach(g=>{groups[g.key]={label:g.label,types:g.types,rows:{},groupReach:0};});
    let totTrk=0,totAf=0;
    flights.filter(f=>bf==='__TOTAL__'||(f.brand||'Brand 1')===bf).forEach(f=>{
      const r=allResults[f.id];if(!r)return;
      totTrk+=r.tot.trackerCost||0;totAf+=r.tot.agencyFee||0;
      r.platforms.forEach(pr=>{
        const t=pr.p?.type||'banner';
        const gk=t==='video'?'video':t==='sem'?'sem':t==='social'?'social':t==='banner'?'display_std':'display_ns';
        const g=groups[gk];if(!g)return;
        const pid=pr.p.id,plabel=pr.p.label,seller=pr.p.seller||'';
        if(!g.rows[pid])g.rows[pid]={pid,label:plabel,seller,type:t,budget:0,mBud:0,impr:0,vimpr:0,views:0,clicks:0,conversions:0,engagement_rate:null,vtr:null,isConsideration:false};
        const row=g.rows[pid];
        row.budget+=pr.bud;row.mBud+=(pr.mBud??pr.bud);row.impr+=pr.impr;row.vimpr+=pr.vimpr;
        row.views+=pr.views;row.clicks+=pr.clicks;
        row.conversions+=pr.clicks*(pr.p.cvr||0);
        // Consideration objective: surface the catalogue Engagement Rate / VTR% inputs that
        // actually drove the platform's inclusion, so the mix is defensible to a client.
        row.engagement_rate=pr.p.engagement_rate;row.vtr=pr.p.vtr;
        if(f.objective==='consideration')row.isConsideration=true;
      });
      SUMMARY_GROUPS.forEach(sg=>{
        const pool=r.platforms.filter(pr=>sg.types.includes(pr.p?.type)).map(pr=>pr.reach||0);
        groups[sg.key].groupReach=sb2(groups[sg.key].groupReach,sbN(pool));
      });
    });
    return{groups,totTrk,totAf};
  }

  // Cell value for an individual platform row
  function rowVal(key,row,totBudAll,totImprAll){
    const{budget,mBud,impr,vimpr,clicks,conversions,type,engagement_rate,vtr,isConsideration}=row;
    switch(key){
      case 'engagement_rate': return(isConsideration&&engagement_rate!=null)?(engagement_rate*100).toFixed(2)+'%':'—';
      case 'vtr':          return(isConsideration&&vtr)?(vtr*100).toFixed(1)+'%':'—';
      case 'buying_unit':  return type==='sem'?'CPC':'CPM';
      case 'impressions':  return fn(impr);
      case 'cpm':          return(type!=='sem'&&impr>0)?'€'+((budget/impr)*1000).toFixed(2):'—';
      case 'clicks':       return fn(clicks);
      case 'cpc':          return clicks>0?'€'+((type==='sem'?mBud:budget)/clicks).toFixed(2):'—';
      case 'ctr':          return impr>0?fp2(clicks/impr):'—';
      case 'viewability':  return(type!=='sem'&&impr>0)?fp(vimpr/impr):'—';
      case 'vcpm':         return(type!=='sem'&&vimpr>0)?'€'+((budget/vimpr)*1000).toFixed(2):'—';
      case 'conversions':  return conversions>0?fn(Math.round(conversions)):'—';
      case 'cost_conv':    return conversions>0?'€'+(budget/conversions).toFixed(2):'—';
      case 'budget':       return fe(budget);
      case 'shr_bud':      return totBudAll>0?((budget/totBudAll)*100).toFixed(1)+'%':'—';
      case 'shr_impr':     return totImprAll>0?((impr/totImprAll)*100).toFixed(1)+'%':'—';
      default: return'—';
    }
  }

  // Cell value for a group-header row (aggregate of rows array)
  function groupVal(key,rows,totBudAll,totImprAll){
    const gBud=rows.reduce((s,r)=>s+r.budget,0);
    const gImpr=rows.reduce((s,r)=>s+r.impr,0);
    const gClicks=rows.reduce((s,r)=>s+r.clicks,0);
    const gConv=rows.reduce((s,r)=>s+r.conversions,0);
    const gCpcBud=rows.reduce((s,r)=>s+(r.type==='sem'?(r.mBud??r.budget):r.budget),0);
    const ns=rows.filter(r=>r.type!=='sem');
    const nsImpr=ns.reduce((s,r)=>s+r.impr,0);
    const nsBud=ns.reduce((s,r)=>s+r.budget,0);
    const nsVimpr=ns.reduce((s,r)=>s+r.vimpr,0);
    switch(key){
      case 'buying_unit':{const u=[...new Set(rows.map(r=>r.type==='sem'?'CPC':'CPM'))];return u.length===1?u[0]:'Mixed';}
      case 'impressions':  return fn(gImpr);
      case 'cpm':          return nsImpr>0?'€'+((nsBud/nsImpr)*1000).toFixed(2):'—';
      case 'clicks':       return fn(gClicks);
      case 'cpc':          return gClicks>0?'€'+(gCpcBud/gClicks).toFixed(2):'—';
      case 'ctr':          return gImpr>0?fp2(gClicks/gImpr):'—';
      case 'viewability':  return nsImpr>0?fp(nsVimpr/nsImpr):'—';
      case 'vcpm':         return nsVimpr>0?'€'+((nsBud/nsVimpr)*1000).toFixed(2):'—';
      case 'conversions':  return gConv>0?fn(Math.round(gConv)):'—';
      case 'cost_conv':    return gConv>0?'€'+(gBud/gConv).toFixed(2):'—';
      case 'budget':       return fe(gBud);
      case 'shr_bud':      return totBudAll>0?((gBud/totBudAll)*100).toFixed(1)+'%':'—';
      case 'shr_impr':     return totImprAll>0?((gImpr/totImprAll)*100).toFixed(1)+'%':'—';
      default: return'—';
    }
  }

  // Cell value for the TOTAL ONLINE row
  function totalVal(key,groups,totBudAll,totImprAll){
    const all=Object.values(groups).flatMap(g=>Object.values(g.rows));
    const tImpr=all.reduce((s,r)=>s+r.impr,0);
    const tClicks=all.reduce((s,r)=>s+r.clicks,0);
    const tConv=all.reduce((s,r)=>s+r.conversions,0);
    const tCpcBud=all.reduce((s,r)=>s+(r.type==='sem'?(r.mBud??r.budget):r.budget),0);
    const ns=all.filter(r=>r.type!=='sem');
    const nsImpr=ns.reduce((s,r)=>s+r.impr,0);
    const nsBud=ns.reduce((s,r)=>s+r.budget,0);
    const nsVimpr=ns.reduce((s,r)=>s+r.vimpr,0);
    switch(key){
      case 'buying_unit':  return 'Mixed';
      case 'impressions':  return fn(tImpr);
      case 'cpm':          return nsImpr>0?'€'+((nsBud/nsImpr)*1000).toFixed(2):'—';
      case 'clicks':       return fn(tClicks);
      case 'cpc':          return tClicks>0?'€'+(tCpcBud/tClicks).toFixed(2):'—';
      case 'ctr':          return tImpr>0?fp2(tClicks/tImpr):'—';
      case 'viewability':  return nsImpr>0?fp(nsVimpr/nsImpr):'—';
      case 'vcpm':         return nsVimpr>0?'€'+((nsBud/nsVimpr)*1000).toFixed(2):'—';
      case 'conversions':  return tConv>0?fn(Math.round(tConv)):'—';
      case 'cost_conv':    return tConv>0?'€'+(totBudAll/tConv).toFixed(2):'—';
      case 'budget':       return fe(totBudAll);
      case 'shr_bud':      return'100%';
      case 'shr_impr':     return'100%';
      default: return'—';
    }
  }

  const HDR={...TH,fontSize:9,background:'#1A3A5C',color:'#fff',padding:'5px 6px',lineHeight:1.3};
  const activeCols=SUMMARY_COLS.filter(c=>visCols.includes(c.key));

  return(<div style={{padding:'16px 24px'}}>
    <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginBottom:10}}>
      <button className="btn btn-ghost btn-sm" style={{display:'inline-flex',alignItems:'center',gap:5}} onClick={()=>setColModal(true)}>
        <Settings size={13}/>Columns
      </button>
      <button className="btn btn-primary btn-sm" onClick={()=>setExportModal(true)}>⬇ Export Client Media Plan</button>
    </div>

    {exportModal&&<ExportMediaPlanModal flights={flights} allResults={allResults} onClose={()=>setExportModal(false)}/>}

    {/* Column picker modal */}
    {colModal&&(
      <div className="modal-overlay" onClick={()=>setColModal(false)}>
        <div className="modal-box" style={{maxWidth:400}} onClick={e=>e.stopPropagation()}>
          <div className="modal-hdr">
            <span style={{fontWeight:700,fontSize:13}}>Visible Columns</span>
            <button className="btn btn-ghost btn-sm" onClick={()=>setColModal(false)}>✕</button>
          </div>
          <div style={{padding:'16px 20px'}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px 24px',marginBottom:20}}>
              {SUMMARY_COLS.map(c=>(
                <label key={c.key} style={{display:'flex',alignItems:'center',gap:8,fontSize:12,color:'var(--text)',cursor:'pointer',userSelect:'none'}}>
                  <input type="checkbox" checked={visCols.includes(c.key)} onChange={()=>toggleCol(c.key)} style={{width:14,height:14,accentColor:'#0090d0',cursor:'pointer'}}/>
                  {c.label}
                </label>
              ))}
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end',borderTop:'1px solid var(--border)',paddingTop:14}}>
              <button className="btn btn-ghost btn-sm" onClick={resetCols}>Reset defaults</button>
              <button className="btn btn-primary btn-sm" onClick={()=>setColModal(false)}>Done</button>
            </div>
          </div>
        </div>
      </div>
    )}

    {[...brands,'__TOTAL__'].map(brand=>{
      const{groups,totTrk,totAf}=buildData(brand);
      const isT=brand==='__TOTAL__';
      const totBudAll=Object.values(groups).reduce((s,g)=>s+Object.values(g.rows).reduce((ss,r)=>ss+r.budget,0),0);
      const totImprAll=Object.values(groups).reduce((s,g)=>s+Object.values(g.rows).reduce((ss,r)=>ss+r.impr,0),0);
      if(totBudAll===0&&!isT)return null;
      return(<div key={brand} style={{marginBottom:32}}>
        <div style={{padding:'7px 14px',background:isT?'var(--text)':'var(--surface2)',color:isT?'var(--gold)':'var(--text)',fontWeight:700,fontSize:11,borderRadius:'6px 6px 0 0',border:'1px solid var(--border)',letterSpacing:'.04em'}}>
          {isT?'TOTAL — ALL BRANDS':brand}
        </div>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:10.5}}>
            <thead>
              <tr style={{background:'#1A3A5C'}}>
                <th style={{...HDR,textAlign:'left',minWidth:160,position:'sticky',left:0,background:'#1A3A5C',zIndex:2}}>Platform / Format</th>
                {activeCols.map(c=><th key={c.key} style={{...HDR,width:c.w,textAlign:c.align}}>{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {SUMMARY_GROUPS.map(sg=>{
                const g=groups[sg.key];
                const rows=Object.values(g.rows);
                if(!rows.length)return null;
                const dot=AD_TYPES.find(t=>sg.types.includes(t.key));
                return[
                  <tr key={'gh_'+sg.key} style={{background:'#E8F0F8'}}>
                    <td style={{...TD,fontWeight:700,fontSize:10,color:dot?.color||'#333',paddingLeft:8,position:'sticky',left:0,background:'#E8F0F8',zIndex:1}}>
                      <div style={{display:'flex',alignItems:'center',gap:6}}><span style={{width:9,height:9,borderRadius:'50%',background:dot?.color||'#ccc',display:'inline-block',flexShrink:0}}/>{g.label}</div>
                    </td>
                    {activeCols.map(c=>(
                      <td key={c.key} style={{...TD,textAlign:c.align,fontWeight:600,fontSize:10,color:c.key==='budget'?'var(--gold-dark)':'#1a2a3a'}}>
                        {groupVal(c.key,rows,totBudAll,totImprAll)}
                      </td>
                    ))}
                  </tr>,
                  ...rows.map(row=>(
                    <tr key={row.pid} style={{background:'#fff'}}>
                      <td style={{...TD,paddingLeft:22,fontWeight:500,position:'sticky',left:0,background:'#fff',zIndex:1}}>
                        <div style={{fontSize:10}}>{row.label}</div>
                        {row.seller&&<div style={{fontSize:8,color:'var(--text3)'}}>{row.seller}</div>}
                      </td>
                      {activeCols.map(c=>(
                        <td key={c.key} style={{...TD,textAlign:c.align,fontSize:10,
                          color:c.key==='budget'?'var(--gold-dark)':c.key==='buying_unit'?'var(--text3)':'#1a2a3a',
                          fontWeight:c.key==='budget'?600:400}}>
                          {rowVal(c.key,row,totBudAll,totImprAll)}
                        </td>
                      ))}
                    </tr>
                  )),
                ];
              })}
              {/* TOTAL ROW */}
              <tr style={{background:'#1A3A5C',color:'#fff',fontWeight:700}}>
                <td style={{...TD,color:'#fff',fontWeight:700,position:'sticky',left:0,background:'#1A3A5C',zIndex:1}}>TOTAL ONLINE</td>
                {activeCols.map(c=>(
                  <td key={c.key} style={{...TD,textAlign:c.align,fontWeight:700,fontSize:c.key==='budget'?12:10,
                    color:c.key==='budget'?'#FFD700':'#C8A86B'}}>
                    {totalVal(c.key,groups,totBudAll,totImprAll)}
                  </td>
                ))}
              </tr>
              {totTrk>0&&(
                <tr style={{background:'#FFF3E0'}}>
                  <td style={{...TD,color:'#E07000',fontStyle:'italic',paddingLeft:8,position:'sticky',left:0,background:'#FFF3E0',zIndex:1}}>3rd Party Trackers (Gemius / DV / IAS)</td>
                  {activeCols.map(c=><td key={c.key} style={{...TD,textAlign:c.align,color:'#E07000',fontWeight:600}}>{c.key==='budget'?fe(totTrk):''}</td>)}
                </tr>
              )}
              {totAf>0&&(
                <tr style={{background:'#F3E8FF'}}>
                  <td style={{...TD,color:'#7030A0',fontStyle:'italic',paddingLeft:8,position:'sticky',left:0,background:'#F3E8FF',zIndex:1}}>Agency Fee</td>
                  {activeCols.map(c=><td key={c.key} style={{...TD,textAlign:c.align,color:'#7030A0',fontWeight:600}}>{c.key==='budget'?fe(totAf):''}</td>)}
                </tr>
              )}
              {(totTrk>0||totAf>0)&&(
                <tr style={{background:'#f5f0e8',fontWeight:700}}>
                  <td style={{...TD,fontWeight:700,paddingLeft:8,position:'sticky',left:0,background:'#f5f0e8',zIndex:1}}>TOTAL INCL. FEES</td>
                  {activeCols.map(c=><td key={c.key} style={{...TD,textAlign:c.align,color:'var(--gold-dark)',fontWeight:800,fontSize:c.key==='budget'?12:10}}>{c.key==='budget'?fe(totBudAll+totTrk):''}</td>)}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>);
    })}
  </div>);
}

// ─── DETAILS TAB — matching Excel: weekly tables per metric type ──────────────
function DetailsTab({flights,allResults,catalogue,onExportExcel,displayYear}){
  const[selFlight,setSelFlight]=useState(null);
  const f=selFlight?flights.find(x=>x.id===selFlight):flights[0];
  const r=f&&allResults[f?.id];
  const[metric,setMetric]=useState('impressions');
  // Monthly rollup columns scoped to the same plan-wide displayYear as the Flowchart —
  // a flight ≥52 weeks can revisit the same bare week-number in a later real year, so
  // both the month-grouping check AND the week-number-only findIndex lookups it feeds
  // need the year match, not just the grouping (same collision class fixed in
  // AnnualFlowchart, applied consistently here rather than leaving half the path patched).
  const dy=displayYear||YEAR;
  const monthWeeksY=useMemo(()=>monthWeeksFor(dy),[dy]);

  const cumRch=useMemo(()=>{
    if(!r||!r.weekly)return[];
    const univ=r.universe||DEF_UNIVERSE;
    // Weekly viewable real users — use platWkBud to respect mix overrides
    const wkOverridesG=f?.weeklyOverrides||{};
    const fl_platformsG=f?.platforms||[];
    function platWkBudG(pr,wi){
      const wb=r.wkBudgets?.[wi]??0;
      const wkMixPlats=wkOverridesG[wi]?.platforms||null;
      const activePlats=wkMixPlats||fl_platformsG;
      const pe=activePlats.find(x=>x.platformId===pr.p.id);
      if(!pe)return 0;
      return wb*((pe.shr||0)/100);
    }
    const wkVRU=r.weekly.map((wk,wi)=>{
      const pools={yt:[],social:[],other:[]};
      r.platforms.forEach(pr=>{
        const wb=platWkBudG(pr,wi);if(wb===0)return;
        const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));
        const cpm=(pr.p.base_cpm||0)*(1-disc);
        const wi_impr=cpm>0?(wb/cpm)*1000:0;
        const wi_ck=pr.frq>0?wi_impr/pr.frq:0;
        const ac=pr.audComp||pr.p?.audience_comp||0.65;
        const ruc=pr.p.real_users_coef||2.0;
        const view=pr.p.viewability||0.7;
        const pool=pr.p?.reach_pool==='yt'?'yt':pr.p?.reach_pool==='social'?'social':'other';
        pools[pool].push({cookies:wi_ck*view,ruc,audComp:ac});
      });
      const rYT=poolReach(pools.yt,univ);
      const rSoc=poolReach(pools.social,univ);
      const rOth=poolReach(pools.other,univ);
      return Math.min(0.98,1-(1-rYT)*(1-rSoc)*(1-rOth));
    });
    let cumPub=0,cumExt=0,cumVRU=0;
    return r.weekly.map((wk,i)=>{
      cumPub=Math.min(sb2(cumPub,wk.publisherReach||0),r.publisherCap??1);
      cumExt=sb2(cumExt,wk.externalReach||0);
      const cum=Math.min(0.98,sb2(cumPub,cumExt));
      cumVRU=sb2(cumVRU,wkVRU[i]||0);
      return{week:wk.week,frac:wk.frac,cumReach:cum,weekReach:wk.reach||0,weekVRU:wkVRU[i]||0,cumVRU,cumVRUabs:Math.round(cumVRU*univ),weekVRUabs:Math.round((wkVRU[i]||0)*univ)};
    });
  },[r]);
  const wFT=r?(r.weekly||[]).reduce((s,wk)=>s+wk.frac,0)||1:1;

  // Build per-platform weekly values
  function platWeekly(field, transform){
    if(!r)return[];
    const wkBuds=r.wkBudgets||[];
    const totBud=r.tot.budget||1;
    const fl_platforms=f?.platforms||[];
    const wkOverrides=f?.weeklyOverrides||{};
    return r.platforms.map(pr=>{
      const raw=pr[field]||0;
      const tot=transform?transform(raw,pr):raw;
      // Per-week: use actual platform share for that week (respects mix overrides)
      const weeks=(r.weekly||[]).map((wk,wi)=>{
        const wb=wkBuds[wi]??0;
        // Find this platform's share for this week
        const wkMixPlats=wkOverrides[wi]?.platforms||null;
        const activePlats=wkMixPlats||fl_platforms;
        const pe=activePlats.find(x=>x.platformId===pr.p.id);
        if(!pe)return 0; // platform not in this week's mix
        const platBud=wb*((pe.shr||0)/100);
        const disc=Math.max(0,Math.min(0.90,pe.discount||0));
        const cpm=(pr.p.base_cpm||0)*(1-disc);
        if(field==='impr')return cpm>0?(platBud/cpm)*1000:0;
        if(field==='vimpr')return cpm>0?(platBud/cpm)*1000*(pr.p.viewability||0.7):0;
        if(field==='views')return cpm>0?(platBud/cpm)*1000*(pr.p.vtr||0):0;
        if(field==='clicks')return cpm>0?(platBud/cpm)*1000*(pr.p.ctr||0):0;
        // For other fields: proportional to platform budget share this week
        return tot*(platBud/(totBud||1));
      });
      return{pid:pr.p.id,label:pr.p.label,seller:pr.p.seller||'',type:pr.p.type,tot,weeks};
    });
  }

  // Helper: get platform's effective budget for a specific week (respects mix overrides)
  function platWkBud(pr,wi){
    const wb=r.wkBudgets?.[wi]??0;
    const wkMixPlats=f?.weeklyOverrides?.[wi]?.platforms||null;
    const activePlats=wkMixPlats||(f?.platforms||[]);
    const pe=activePlats.find(x=>x.platformId===pr.p.id);
    if(!pe)return 0;
    return wb*((pe.shr||0)/100);
  }

  const METRIC_SECTIONS=[
    {key:'impressions', label:'Impressions',         color:'#4472C4', getRows:()=>platWeekly('impr')},
    {key:'frq', label:'FRQ (avg/wk)', color:'#ED7D31', isFixed:true, getRows:()=>r.platforms.map(pr=>({pid:pr.p.id,label:pr.p.label,seller:pr.p.seller||'',type:pr.p.type,tot:pr.frq||0,weeks:(r.weekly||[]).map(()=>pr.frq||0)})), totFn:()=>{const totI=r.platforms.reduce((s,pr)=>s+(pr.impr||0),0);return totI>0?r.platforms.reduce((s,pr)=>s+(pr.frq||0)*(pr.impr||0),0)/totI:0;}},
    {key:'cookies', label:'Unique Cookies/wk', color:'#70AD47', getRows:()=>{return r.platforms.map(pr=>{const totCk=Math.round(pr.frq>0?(pr.impr||0)/pr.frq:0);const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));const cpm=(pr.p.base_cpm||0)*(1-disc);const weeks=(r.weekly||[]).map((_,wi)=>{const wb=platWkBud(pr,wi);const wi_impr=cpm>0?(wb/cpm)*1000:0;return Math.round(pr.frq>0?wi_impr/pr.frq:0);});return{pid:pr.p.id,label:pr.p.label,seller:pr.p.seller||'',type:pr.p.type,tot:totCk,weeks};});}},
    {key:'taUsers', label:'TA Real Users/wk', color:'#FF0066', note:'Cookies ÷ RUC × AudComp', getRows:()=>{const nWks=r.weekly&&r.weekly.length||1;return r.platforms.map(pr=>{const ac=pr.audComp||pr.p?.audience_comp||0.65;const ruc=pr.p.real_users_coef||2.0;const univ=r.universe||DEF_UNIVERSE;const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));const cpm=(pr.p.base_cpm||0)*(1-disc);const baseWkRU=Math.round((pr.uniqueCookies||0)/ruc*ac);const wkRch=Math.min(0.999,baseWkRU/univ);let cum=0;for(let i=0;i<nWks;i++){cum=cum+wkRch-cum*wkRch;}const totRU=Math.round(Math.min(0.98,cum)*univ);const weeks=(r.weekly||[]).map((_,wi)=>{const wb=platWkBud(pr,wi);if(wb===0)return 0;const wi_impr=cpm>0?(wb/cpm)*1000:0;const wi_ck=pr.frq>0?wi_impr/pr.frq:0;return Math.round(wi_ck/ruc*ac);});return{pid:pr.p.id,label:pr.p.label,seller:pr.p.seller||'',type:pr.p.type,tot:totRU,monthVal:totRU,weeks,noMonthSum:true};});}},
    {key:'vRealUsers',  label:'Viewable Real Users/wk', color:'#C00000', note:'(Cookies ÷ RUC × AudComp) × Viewability — users who actually saw the ad', getRows:()=>{const nWks=r.weekly&&r.weekly.length||1;return r.platforms.map(pr=>{const ac=pr.audComp||pr.p?.audience_comp||0.65;const ruc=pr.p.real_users_coef||2.0;const view=pr.p.viewability||0.7;const univ=r.universe||DEF_UNIVERSE;const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));const cpm=(pr.p.base_cpm||0)*(1-disc);const baseWkVRU=Math.round((pr.uniqueCookies||0)/ruc*ac*view);const wkRch=Math.min(0.999,baseWkVRU/univ);let cum=0;for(let i=0;i<nWks;i++){cum=cum+wkRch-cum*wkRch;}const totVRU=Math.round(Math.min(0.98,cum)*univ);const weeks=(r.weekly||[]).map((_,wi)=>{const wb=platWkBud(pr,wi);if(wb===0)return 0;const wi_impr=cpm>0?(wb/cpm)*1000:0;const wi_ck=pr.frq>0?wi_impr/pr.frq:0;return Math.round(wi_ck/ruc*ac*view);});return{pid:pr.p.id,label:pr.p.label,seller:pr.p.seller||'',type:pr.p.type,tot:totVRU,monthVal:totVRU,weeks,noMonthSum:true};});}},
    {key:'clicks',      label:'Clicks',               color:'#00B0F0', getRows:()=>platWeekly('clicks')},
    {key:'conversions', label:'Conversions',           color:'#1B5E8C', note:'Post-click & post-view (set conversion rate per platform)', getRows:()=>{const wkBuds=r.wkBudgets||[];const totBud=r.tot.budget||1;return r.platforms.map(pr=>{const cvr=pr.p.cvr||0;const tot=pr.clicks*(cvr||0);const weeks=wkBuds.length>0?wkBuds.map(wb=>tot*(wb/totBud)):(r.weekly||[]).map(wk=>tot*(wk.frac/wFT));return{pid:pr.p.id,label:pr.p.label,seller:pr.p.seller||'',type:pr.p.type,tot,weeks};});}},
    {key:'views',       label:'Views',                color:'#7030A0', getRows:()=>platWeekly('views')},
    {key:'vimpr',       label:'Viewable Impressions', color:'#C19A6B', getRows:()=>platWeekly('vimpr')},
  ];

  function WeeklyTable({sectionKey,label,color,note}){
    const sec=METRIC_SECTIONS.find(s=>s.key===sectionKey);if(!sec||!r)return null;
    const rows=sec.getRows();
    // For FRQ/Cookies/RealUsers: weighted avg; others: sum
    let totRow;
    if(sec.key==='frq'){
      const totI=r.platforms.reduce((s,pr)=>s+(pr.impr||0),0)||1;
      const wAvg=r.platforms.reduce((s,pr)=>s+(pr.frq||0)*(pr.impr||0),0)/totI;
      totRow={pid:'__tot',label:'Total',tot:wAvg,weeks:(r.weekly||[]).map(()=>wAvg),noMonthSum:true};
    } else if(sec.key==='cookies'){
      const totI=r.platforms.reduce((s,pr)=>s+(pr.impr||0),0)||1;
      const avgFrq=r.platforms.reduce((s,pr)=>s+(pr.frq||0)*(pr.impr||0),0)/totI||1;
      const totCk=Math.round(totI/avgFrq);
      // Per-week: sum cookies across all platforms using platWkBud
      const wkCkArr=(r.weekly||[]).map((_,wi)=>rows.reduce((s,row)=>s+(row.weeks[wi]||0),0));
      totRow={pid:'__tot',label:'Total',tot:totCk,monthVal:totCk,weeks:wkCkArr,noMonthSum:true};
    } else if(sec.key==='taUsers'){
      const universe=r.universe||DEF_UNIVERSE;
      // Per-week Sainsbury using platWkBud for each week
      const wkRUArr=(r.weekly||[]).map((_,wi)=>{
        const pools={yt:[],social:[],other:[]};
        r.platforms.forEach(pr=>{
          const wb=platWkBud(pr,wi);if(wb===0)return;
          const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));
          const cpm=(pr.p.base_cpm||0)*(1-disc);
          const wi_impr=cpm>0?(wb/cpm)*1000:0;
          const wi_ck=pr.frq>0?wi_impr/pr.frq:0;
          const ac=pr.audComp||pr.p?.audience_comp||0.65;
          const ruc=pr.p.real_users_coef||2.0;
          const pool=pr.p?.reach_pool==='yt'?'yt':pr.p?.reach_pool==='social'?'social':'other';
          pools[pool].push({cookies:wi_ck,ruc,audComp:ac});
        });
        return Math.round(Math.min(0.98,1-(1-poolReach(pools.yt,universe))*(1-poolReach(pools.social,universe))*(1-poolReach(pools.other,universe)))*universe);
      });
      // Total: cumulative Sainsbury using average week pools
      const wPools={yt:[],social:[],other:[]};
      r.platforms.forEach(pr=>{const pool=pr.p?.reach_pool==='yt'?'yt':pr.p?.reach_pool==='social'?'social':'other';wPools[pool].push({cookies:pr.uniqueCookies||0,ruc:pr.p.real_users_coef||2.0,audComp:pr.audComp||pr.p?.audience_comp||0.65});});
      const wkReach=Math.min(0.98,1-(1-poolReach(wPools.yt,universe))*(1-poolReach(wPools.social,universe))*(1-poolReach(wPools.other,universe)));
      const nWksRU=r.weekly&&r.weekly.length||1;let cumRU=0;for(let i=0;i<nWksRU;i++){cumRU=cumRU+wkReach-cumRU*wkReach;}
      const totRU=Math.round(Math.min(0.98,cumRU)*universe);
      totRow={pid:'__tot',label:'Total',tot:totRU,monthVal:totRU,weeks:wkRUArr,noMonthSum:true};
    } else if(sec.key==='vRealUsers'){
      const universe=r.universe||DEF_UNIVERSE;
      const nWksVRU=r.weekly&&r.weekly.length||1;
      // Per-week Sainsbury using platWkBud (respects mix overrides)
      const wkVRUArr=(r.weekly||[]).map((_,wi)=>{
        const pools={yt:[],social:[],other:[]};
        r.platforms.forEach(pr=>{
          const wb=platWkBud(pr,wi);if(wb===0)return;
          const disc=Math.max(0,Math.min(0.90,pr.pe.discount||0));
          const cpm=(pr.p.base_cpm||0)*(1-disc);
          const wi_impr=cpm>0?(wb/cpm)*1000:0;
          const wi_ck=pr.frq>0?wi_impr/pr.frq:0;
          const ac=pr.audComp||pr.p?.audience_comp||0.65;
          const ruc=pr.p.real_users_coef||2.0;
          const view=pr.p.viewability||0.7;
          const pool=pr.p?.reach_pool==='yt'?'yt':pr.p?.reach_pool==='social'?'social':'other';
          pools[pool].push({cookies:wi_ck*view,ruc,audComp:ac});
        });
        return Math.round(Math.min(0.98,1-(1-poolReach(pools.yt,universe))*(1-poolReach(pools.social,universe))*(1-poolReach(pools.other,universe)))*universe);
      });
      // Total: cumulative Sainsbury
      const wPools={yt:[],social:[],other:[]};
      r.platforms.forEach(pr=>{
        const ac=pr.audComp||pr.p?.audience_comp||0.65;
        const ruc=pr.p.real_users_coef||2.0;
        const view=pr.p.viewability||0.7;
        const pool=pr.p?.reach_pool==='yt'?'yt':pr.p?.reach_pool==='social'?'social':'other';
        wPools[pool].push({cookies:(pr.uniqueCookies||0)*view,ruc,audComp:ac});
      });
      const wYT=poolReach(wPools.yt,universe);
      const wSoc=poolReach(wPools.social,universe);
      const wOth=poolReach(wPools.other,universe);
      const wkReach=Math.min(0.98,1-(1-wYT)*(1-wSoc)*(1-wOth));
      let cumVRU2=0;for(let i=0;i<nWksVRU;i++){cumVRU2=cumVRU2+wkReach-cumVRU2*wkReach;}
      const totVRU=Math.round(Math.min(0.98,cumVRU2)*universe);
      totRow={pid:'__tot',label:'Total',tot:totVRU,monthVal:totVRU,weeks:wkVRUArr,noMonthSum:true};
    } else {
      totRow={pid:'__tot',label:'Total',tot:rows.reduce((s,r)=>s+r.tot,0),weeks:(r.weekly||[]).map((_,i)=>rows.reduce((s,r2)=>s+(r2.weeks[i]||0),0))};
    }

    return(<div style={{marginBottom:24}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
        <div style={{width:10,height:10,borderRadius:'50%',background:color,flexShrink:0}}/>
        <span style={{fontWeight:700,fontSize:12,color}}>{label}</span>
        {note&&<span style={{fontSize:9,color:'var(--text3)',fontStyle:'italic'}}>{note}</span>}
      </div>
      <div style={{overflowX:'auto'}}>
        <table style={{borderCollapse:'collapse',fontSize:10,width:'100%',minWidth:700}}>
          <thead><tr style={{background:color+'22',borderBottom:'2px solid '+color}}>
            <th style={{...TH,background:color+'22',textAlign:'left',minWidth:140,position:'sticky',left:0,zIndex:2}}>Platform</th>
            <th style={{...TH,background:color+'22',width:60}}>Format</th>
            {(r.weekly||[]).map(wk=><th key={wk.week} style={{...TH,background:color+'22',width:44,fontSize:8}}>
              W{wk.week}
              {wk.frac<0.99&&<span style={{color:'#E07000'}}>*</span>}
              <div style={{fontWeight:400,color:'var(--text3)',fontSize:7,whiteSpace:'nowrap'}}>{fdate(weekStartDate(wk.week))}</div>
            </th>)}
            <th style={{...TH,background:color,color:'#fff',width:72}}>TOTAL</th>
            {/* Monthly summary cols */}
            {monthWeeksY.map((ws,mi)=>{const hasData=r.weekly&&r.weekly.some(wk=>ws.includes(wk.week)&&wk.year===dy);return hasData?<th key={mi} style={{...TH,background:color+'44',width:64,fontSize:8}}>{MN[mi]}</th>:null;})}
          </tr></thead>
          <tbody>
            {[...rows,totRow].map((row,ri)=>{
              const isTotal=row.pid==='__tot';
              const typ=AD_TYPES.find(t=>t.key===row.type);
              const monthTots=monthWeeksY.map((ws)=>{
                const mWks=(r.weekly||[]).filter(wk=>ws.includes(wk.week)&&wk.year===dy);
                if(!mWks.length)return 0;
                if(sec.isFixed){
                  // FRQ: simple average of weekly values for that month
                  return mWks.reduce((s,wk)=>{const idx=(r.weekly||[]).findIndex(x=>x.week===wk.week&&x.year===wk.year);return s+(row.weeks[idx]||0);},0)/mWks.length;
                }
                if(row.noMonthSum){
                  const univ2=r.universe||DEF_UNIVERSE;
                  if(sec.key==='cookies'&&row.pid==='__tot'){
                    // cookies TOTAL monthly = monthImpressions / weightedAvgFrq (same as flight total)
                    const totI2=r.platforms.reduce((s,pr)=>s+(pr.impr||0),0)||1;
                    const avgFrq2=r.platforms.reduce((s,pr)=>s+(pr.frq||0)*(pr.impr||0),0)/totI2||1;
                    const mTotImpr=mWks.reduce((s,wk)=>{const idx=(r.weekly||[]).findIndex(x=>x.week===wk.week&&x.year===wk.year);return s+r.platforms.reduce((ss,pr)=>ss+(pr.impr||0)*(wk.frac||1)/((r.weekly||[]).reduce((sf,w)=>sf+(w.frac||1),0)||1),0);},0);
                    return Math.round(mTotImpr/avgFrq2);
                  }
                  if(sec.key==='cookies'){
                    // per-platform cookies monthly = sum of weekly values
                    return mWks.reduce((s,wk)=>{const idx=(r.weekly||[]).findIndex(x=>x.week===wk.week&&x.year===wk.year);return s+(row.weeks[idx]||0);},0);
                  }
                  // taUsers/vRealUsers: Sainsbury cumulative for this month's weeks
                  let cumM=0;
                  mWks.forEach(wk=>{
                    const idx=(r.weekly||[]).findIndex(x=>x.week===wk.week&&x.year===wk.year);
                    const wkAbs=row.weeks[idx]||0;
                    const wkR=Math.min(0.999,wkAbs/univ2);
                    cumM=Math.min(0.98,cumM+wkR-cumM*wkR);
                  });
                  return Math.round(cumM*univ2);
                }
                return mWks.reduce((s,wk)=>{const idx=(r.weekly||[]).findIndex(x=>x.week===wk.week&&x.year===wk.year);return s+(row.weeks[idx]||0);},0);
              });
              return(<tr key={row.pid} style={{background:isTotal?color+'11':'#fff',fontWeight:isTotal?700:400}}>
                <td style={{...TD,fontWeight:isTotal?700:500,position:'sticky',left:0,background:isTotal?color+'11':'#fff',zIndex:1}}>
                  {isTotal?<span style={{color}}>Total</span>:<div><div style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:6,height:6,borderRadius:'50%',background:typ?.color||'#ccc',display:'inline-block',flexShrink:0}}/>{row.label}</div><div style={{fontSize:8,color:'var(--text3)'}}>{row.seller}</div></div>}
                </td>
                <td style={{...TDc,fontSize:8,color:'var(--text3)'}}>{isTotal?'':typ?.label||''}</td>
                {row.weeks.map((v,wi)=>(
                  <td key={wi} style={{...TDr,color:isTotal?color:'var(--text2)',fontSize:9}}>
                    {sec.isFixed?ff(v):fn(Math.round(v))}
                  </td>
                ))}
                <td style={{...TDr,fontWeight:700,color,fontSize:10}}>{sec.isFixed?ff(row.tot):fn(Math.round(row.tot))}</td>
                {monthTots.map((mv,mi)=>{const ws=monthWeeksY[mi];const hasData=r.weekly&&r.weekly.some(wk=>ws.includes(wk.week)&&wk.year===dy);if(!hasData)return null;const dispV=row.noMonthSum?monthTots[mi]:mv;return<td key={mi} style={{...TDr,color,fontSize:9,fontWeight:isTotal?700:400}}>{sec.isFixed?ff(dispV):fn(Math.round(dispV))}</td>;})}
              </tr>);
            })}
          </tbody>
        </table>
      </div>
    </div>);
  }

  if(!f||!r)return<div style={{padding:24,color:'var(--text3)'}}>Select a flight.</div>;

  return(<div style={{padding:'16px 24px'}}>
    <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:16,flexWrap:'wrap'}}>
      <div className="lbl" style={{marginBottom:0,whiteSpace:'nowrap'}}>Flight:</div>
      <select className="sel" style={{minWidth:240}} value={f.id} onChange={e=>setSelFlight(e.target.value)}>
        {flights.map(fl=><option key={fl.id} value={fl.id}>{fl.brand} — {fl.label} ({fdate(fl.startDate)}–{fdate(fl.endDate)})</option>)}
      </select>
      <div style={{flex:1}}/>
      <button className="btn btn-ghost btn-sm" onClick={()=>onExportExcel(f.id)}>⬇ Export Excel</button>
    </div>

    {/* Campaign summary KPIs */}
    <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:20,padding:'10px 12px',background:'var(--gold-bg)',border:'1px solid var(--gold-border)',borderRadius:6}}>
      {[
        {l:'Budget',v:fe(r.tot.budget)},
        {l:'TA Impressions',v:fn(r.tot.impressions)},
        {l:'VRCH%',v:fp(r.tot.reach),hi:true},
        {l:'vRCH abs.',v:fn(Math.round(r.tot.reach*(r.universe||DEF_UNIVERSE)))||'—'},
        {l:'eCPM',v:fe2(r.tot.ecpm)},
        {l:'vCPM',v:fe2(r.tot.vcpm)},
        {l:'Avg Freq.',v:ff(r.tot.frequency)},
        {l:'Views',v:fn(r.tot.views)},
        {l:'Clicks',v:fn(r.tot.clicks)},
      ].map(k=>(
        <div key={k.l} style={{background:k.hi?'var(--gold)':'#fff',border:'1px solid '+(k.hi?'#B8860B':'var(--border)'),borderRadius:5,padding:'4px 10px',textAlign:'center',minWidth:72}}>
          <div style={{fontSize:8,color:k.hi?'#fff':'var(--text3)',textTransform:'uppercase',letterSpacing:'.04em',marginBottom:1}}>{k.l}</div>
          <div style={{fontSize:12,fontWeight:800,color:k.hi?'#fff':'var(--text)'}}>{k.v}</div>
        </div>
      ))}
    </div>

    {METRIC_SECTIONS.map(sec=><WeeklyTable key={sec.key} sectionKey={sec.key} label={sec.label} color={sec.color} note={sec.note}/>)}

    {/* Cumulative vRCH curve */}
    <div style={{marginBottom:24}}>
      <div style={{fontWeight:700,fontSize:12,marginBottom:8,color:'var(--gold-dark)',display:'flex',alignItems:'center',gap:5}}><TrendingUp size={12}/>Cumulative Reach & Viewable Real Users — {f.label}</div>
      <div style={{overflowX:'auto'}}>
        <table style={{borderCollapse:'collapse',fontSize:10,width:'100%'}}>
          <thead><tr style={{background:'var(--gold-bg)'}}>
            <th style={{...TH,textAlign:'left',width:120}}>Week</th>
            <th style={{...TH,width:50}}>Days</th>
            <th style={{...TH,width:70}}>+RCH%/wk</th>
            <th style={{...TH,width:80}}>Cum. RCH%</th>
            <th style={{...TH,width:80,color:'#C00000'}}>+View. Users/wk</th>
            <th style={{...TH,width:90,color:'#C00000'}}>Cum. View. Users</th>
            <th style={TH}>Progress (View. Users)</th>
          </tr></thead>
          <tbody>
            {cumRch.map((cr,i)=>{
              const days=Math.round(cr.frac*7);
              const taUsers=Math.round(cr.cumReach*(r.universe||DEF_UNIVERSE));
              return(<tr key={i} style={{background:i%2===0?'#fff':'#FAFAFA'}}>
                <td style={{...TD,fontSize:9,color:'var(--text3)',fontWeight:600}}>W{cr.week} · {fdate(weekStartDate(cr.week))}{days<7&&<span style={{color:'#E07000'}}> ({days}d)</span>}</td>
                <td style={{...TDc,fontSize:9,color:days<7?'#E07000':'var(--text3)'}}>{days}</td>
                <td style={{...TDc,color:'var(--text3)',fontSize:9}}>+{fp(cr.weekReach)}</td>
                <td style={{...TDc,color:'var(--gold-dark)',fontWeight:800,fontSize:12}}>{fp(cr.cumReach)}</td>
                <td style={{...TDc,color:'#C00000',fontSize:9,fontWeight:600}}>{fn(cr.weekVRUabs||0)}</td>
                <td style={{...TDc,color:'#C00000',fontWeight:800,fontSize:11}}>{fn(cr.cumVRUabs||0)}</td>
                <td style={TD}>
                  <div style={{position:'relative',height:20,background:'var(--surface3)',borderRadius:8,overflow:'hidden',minWidth:120}}>
                    <div style={{position:'absolute',left:0,top:0,height:'100%',width:Math.min(100,(cr.cumVRU!=null?cr.cumVRU:cr.cumReach)*100).toFixed(1)+'%',background:'linear-gradient(90deg,#C00000 0%,#FF4444 100%)',borderRadius:8,transition:'width .3s'}}/>
                    <div style={{position:'absolute',left:0,top:0,height:'100%',width:Math.min(100,cr.cumReach*100).toFixed(1)+'%',background:'linear-gradient(90deg,var(--gold) 0%,var(--gold-dark) 100%)',borderRadius:8,opacity:0.5,transition:'width .3s'}}/>
                    <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,color:'#fff',fontWeight:700,textShadow:'0 1px 2px rgba(0,0,0,.5)'}}>{fp(cr.cumVRU!=null?cr.cumVRU:cr.cumReach)}</div>
                  </div>
                </td>
              </tr>);
            })}
          </tbody>
        </table>
        <div style={{display:'flex',gap:16,marginTop:6,fontSize:9,color:'var(--text3)'}}>
          <span><span style={{display:'inline-block',width:12,height:8,background:'linear-gradient(90deg,#C00000,#FF4444)',borderRadius:2,marginRight:4,verticalAlign:'middle'}}/>Site-aware reach estimate (bar)</span>
          <span><span style={{display:'inline-block',width:12,height:8,background:'linear-gradient(90deg,var(--gold),var(--gold-dark))',borderRadius:2,marginRight:4,verticalAlign:'middle',opacity:0.5}}/>Legacy pooled estimate (overlay)</span>
          <span style={{color:'var(--text3)'}}>Both include viewability. Progress bar accounts for site/device overlap; gold overlay uses the older pooled calculation, which doesn't.</span>
        </div>
      </div>
    </div>
  </div>);
}

// ─── AUDIENCE MANAGER — with per-platform audience_comp overrides ─────────────
export function AudienceManager({audiences,setAudiences,catalogue,onClose,clientId}){
  const[selId,setSelId]=useState(audiences[0]?.id||null);
  const[editName,setEditName]=useState('');
  const[editUniv,setEditUniv]=useState(1887226);
  const[editComp,setEditComp]=useState({});
  const[editMode,setEditMode]=useState(false);
  const[newScope,setNewScope]=useState('client'); // scope for next new audience
  const[saving,setSaving]=useState(false);

  // On mount: fetch DB audiences and merge with plan snapshot
  React.useEffect(()=>{
    api.getAudiences(clientId).then(dbAuds=>{
      if(!dbAuds.length)return;
      // Map DB audiences to frontend format
      const dbMapped=dbAuds.map(a=>({
        id:'db_'+a.id, _db_id:a.id, name:a.name, universe:a.universe,
        platComp:a.plat_comp||{}, scope:a.scope, client_id:a.client_id,
      }));
      // Build label->dbId map for deduplication
      const dbByName={};
      dbMapped.forEach(a=>{dbByName[a.name.toLowerCase().trim()]=a;});
      // Merge: keep plan-only audiences (no _db_id, not matching any DB name)
      setAudiences(prev=>{
        const planOnly=prev.filter(a=>!a._db_id&&!dbByName[a.name.toLowerCase().trim()]);
        return [...dbMapped,...planOnly];
      });
    }).catch(()=>{});
  },[]);

  function startEdit(a){setSelId(a.id);setEditName(a.name);setEditUniv(a.universe);setEditComp({...(a.platComp||{})});setEditMode(true);}

  function saveEdit(){
    const a=audiences.find(x=>x.id===selId);
    const updated={...a,name:editName,universe:editUniv,platComp:editComp,_has_plan_override:true};
    setAudiences(p=>p.map(x=>x.id===selId?updated:x));
    setEditMode(false);
    // Persist to DB if has _db_id
    if(a?._db_id){
      // Convert platComp keys: if they're frontend ids (db_X), store as db_id strings
      const platCompForDb={};
      Object.entries(editComp).forEach(([k,v])=>{
        const dbId=k.startsWith('db_')?k.replace('db_',''):k;
        platCompForDb[dbId]=v;
      });
      api.updateAudience(a._db_id,{name:editName,universe:editUniv,plat_comp:platCompForDb}).catch(()=>{});
    }
  }

  function addNew(){
    const tempId='aud_'+Date.now();
    const na={id:tempId,name:'New Audience',universe:1887226,platComp:{},scope:newScope};
    setAudiences(p=>[...p,na]);
    startEdit(na);
    // POST to DB unless plan-only
    if(newScope!=='plan'){
      setSaving(true);
      api.createAudience({name:'New Audience',universe:1887226,plat_comp:{},scope:newScope,client_id:newScope==='client'?clientId:null}).then(d=>{
        if(d){
          const finalA={...na,_db_id:d.id,id:'db_'+d.id,scope:d.scope};
          setAudiences(p=>p.map(x=>x.id===tempId?finalA:x));
          setSelId('db_'+d.id);
        }
        setSaving(false);
      }).catch(()=>setSaving(false));
    }
  }

  function del(id){
    if(!window.confirm('Delete audience?'))return;
    const a=audiences.find(x=>x.id===id);
    setAudiences(p=>p.filter(x=>x.id!==id));
    setSelId(audiences.find(x=>x.id!==id)?.id||null);
    setEditMode(false);
    if(a?._db_id){api.deleteAudience(a._db_id).catch(()=>{});}
  }

  const sel=audiences.find(a=>a.id===selId);

  return(<div className="modal-overlay" onClick={ev=>{if(ev.target===ev.currentTarget)onClose();}}>
    <div className="modal-box" style={{maxWidth:780,height:'80vh',display:'flex',flexDirection:'column'}}>
      <div className="modal-hdr"><span style={{fontWeight:700,fontSize:13,display:'inline-flex',alignItems:'center',gap:6}}><Users size={13}/>Target Audience Database</span><button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button></div>
      <div style={{display:'flex',flex:1,overflow:'hidden'}}>
        {/* LEFT: audience list */}
        <div style={{width:220,borderRight:'1px solid var(--border)',padding:12,overflowY:'auto',flexShrink:0}}>
          <div style={{fontSize:10,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:8}}>Audiences</div>
          {audiences.map(a=>{
            const scopeColor=a.scope==='global'?'var(--teal)':a.scope==='client'?'#7030A0':'var(--gold-dark)';
            const ScopeIc=a.scope==='global'?Globe:a.scope==='client'?Building2:ClipboardList;
            // Same "Global X / Client X" wording convention as the Catalogue tab (CatalogueTable.js).
            const scopeLabel=a.scope==='global'?'Global audience':a.scope==='client'?'Client audience':'Plan audience';
            return(<div key={a.id} onClick={()=>{setSelId(a.id);setEditMode(false);}} style={{padding:'7px 10px',marginBottom:4,borderRadius:5,cursor:'pointer',background:selId===a.id?'var(--gold-bg)':'transparent',border:'1px solid '+(selId===a.id?'var(--gold-border)':'transparent')}}>
              <div style={{display:'flex',alignItems:'center',gap:4}}>
                <ScopeIc size={10} color={scopeColor}/>
                <span style={{fontWeight:600,fontSize:11}}>{a.name}</span>
              </div>
              <div style={{fontSize:9,color:scopeColor,fontWeight:600}}>{scopeLabel}</div>
              <div style={{fontSize:9,color:'var(--text3)'}}>Universe: {a.universe.toLocaleString()}</div>
              <div style={{fontSize:9,color:'var(--gold-dark)'}}>{Object.keys(a.platComp||{}).length} comp. overrides</div>
            </div>);
          })}
          <div style={{marginTop:8,borderTop:'1px solid var(--border)',paddingTop:8}}>
            <div style={{fontSize:9,color:'var(--text3)',marginBottom:4,fontWeight:600}}>New audience scope:</div>
            <div style={{display:'flex',gap:4,marginBottom:6}}>
              {[{s:'client',Icon:Building2,l:'Client',c:'#7030A0'},{s:'plan',Icon:ClipboardList,l:'Plan',c:'var(--gold-dark)'}].map(({s,Icon:Ic,l,c})=>(
                <button key={s} onClick={()=>setNewScope(s)} style={{flex:1,fontSize:9,padding:'2px 4px',borderRadius:8,border:'1px solid '+(newScope===s?c:'var(--border)'),background:newScope===s?c+'22':'transparent',color:newScope===s?c:'var(--text3)',fontWeight:newScope===s?700:400,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:3}}><Ic size={9}/>{l}</button>
              ))}
            </div>
            <button className="btn btn-ghost btn-sm" style={{width:'100%'}} onClick={addNew} disabled={saving}>{saving?'Saving…':'+ New audience'}</button>
          </div>
        </div>
        {/* RIGHT: editor */}
        <div style={{flex:1,padding:'16px 24px',overflowY:'auto'}}>
          {sel&&(<>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
              <div style={{fontWeight:700,fontSize:13}}>{editMode?'Editing:':''} {sel.name}</div>
              <div style={{display:'flex',gap:6}}>
                {!editMode&&<button className="btn btn-ghost btn-sm" onClick={()=>startEdit(sel)}>✎ Edit</button>}
                {editMode&&<button className="btn btn-primary btn-sm" onClick={saveEdit}>✓ Save</button>}
                {editMode&&<button className="btn btn-ghost btn-sm" onClick={()=>setEditMode(false)}>Cancel</button>}
                <button className="btn btn-danger btn-sm" onClick={()=>del(sel.id)}>Delete</button>
              </div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:16}}>
              <div><div className="lbl">Audience name</div>
                {editMode?<input className="inp" value={editName} style={{width:'100%'}} onChange={e=>setEditName(e.target.value)}/>:<div style={{fontWeight:600,padding:'5px 8px',background:'var(--surface2)',borderRadius:4,border:'1px solid var(--border)'}}>{sel.name}</div>}
              </div>
              <div><div className="lbl">Universe (population)</div>
                {editMode?<input className="inn" type="number" value={editUniv} style={{width:'100%'}} onChange={e=>setEditUniv(+e.target.value)}/>:<div style={{fontWeight:600,padding:'5px 8px',background:'var(--surface2)',borderRadius:4,border:'1px solid var(--border)'}}>{sel.universe.toLocaleString()}</div>}
              </div>
            </div>
            <div style={{marginBottom:8}}>
              <div style={{fontWeight:700,fontSize:11,marginBottom:4}}>Platform Audience Composition Overrides</div>
              <p style={{fontSize:10,color:'var(--text3)',marginBottom:10}}>Set a custom <strong>audience_comp</strong> for each platform when using this audience. Leave blank to use the platform default. This affects reach calculations — when this audience is selected for a flight, these values replace the defaults automatically.</p>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}>
                <thead><tr style={{background:'var(--gold-bg)'}}>
                  <th style={{...TH,textAlign:'left',minWidth:160}}>Platform</th>
                  <th style={{...TH,width:80}}>Type</th>
                  <th style={{...TH,width:100}}>Default comp.</th>
                  <th style={{...TH,width:120}}>Override for this audience</th>
                  <th style={{...TH,width:80}}>Δ vs default</th>
                </tr></thead>
                <tbody>
                  {catalogue.filter(p=>p.active!==false).map(p=>{
                    const typ=AD_TYPES.find(t=>t.key===p.type);
                    const def=p.audience_comp||0.65;
                    const ov=editMode?editComp[p._db_id??p.id]:sel.platComp?.[p._db_id??p.id];
                    const hasOv=ov!=null&&ov!=='';
                    const delta=hasOv?(+ov-def):0;
                    return(<tr key={p.id}>
                      <td style={{...TD,fontWeight:500}}>{p.label}<div style={{fontSize:8,color:'var(--text3)'}}>{p.seller}</div></td>
                      <td style={{...TDc}}><span style={{color:typ?.color,fontWeight:600,fontSize:9}}>{typ?.label}</span></td>
                      <td style={{...TDc,color:'var(--text3)'}}>{(def*100).toFixed(1)}%</td>
                      <td style={TDc}>
                        {editMode
                          ?<input className="inn" type="number" min={0} max={100} step={0.1} placeholder={(def*100).toFixed(1)} key={p.id+'_'+selId} defaultValue={editComp[p._db_id??p.id]!=null?(editComp[p._db_id??p.id]*100):''} style={{width:'100%',textAlign:'center',background:hasOv?'var(--gold-bg)':'transparent'}}
                            onBlur={e=>{const v=e.target.value;const key=p._db_id??p.id;setEditComp(prev=>{if(v===''||v===null||isNaN(+v)){const n={...prev};delete n[key];return n;}return{...prev,[key]:parseFloat(v)/100};});}}/>
                          :<span style={{fontWeight:hasOv?700:400,color:hasOv?'var(--gold-dark)':'var(--text3)'}}>{hasOv?(+ov*100).toFixed(1)+'%':'—'}</span>
                        }
                      </td>
                      <td style={{...TDc,color:delta>0.01?'#2E8B57':delta<-0.01?'#C00000':'var(--text3)',fontWeight:Math.abs(delta)>0.01?700:400}}>
                        {hasOv?(delta>=0?'+':'')+((delta)*100).toFixed(1)+'pp':'—'}
                      </td>
                    </tr>);
                  })}
                </tbody>
              </table>
            </div>
          </>)}
          {!sel&&<div style={{color:'var(--text3)',padding:20}}>Select or create an audience.</div>}
        </div>
      </div>
      <div className="modal-footer"><div style={{flex:1}}/><button className="btn btn-primary btn-sm" onClick={onClose}>Done</button></div>
    </div>
  </div>);
}

// ─── ACTUALS / PACING TAB ─────────────────────────────────────────────────────
// Per-platform weekly actuals + CSV/paste upload + benchmark recalculation

const ACTUAL_FIELDS=[
  {key:'impr',   label:'Impressions',    color:'#4472C4'},
  {key:'vimpr',  label:'View. Impr.',    color:'#C19A6B'},
  {key:'views',  label:'Views',          color:'#7030A0'},
  {key:'clicks', label:'Clicks',         color:'#00B0F0'},
  {key:'budget', label:'Budget Spent €', color:'#2E8B57'},
];

function computePlatformBenchmarks(platActuals,planPlatResult,planWeekly,nWeeks=12){
  if(!planPlatResult||!planWeekly)return null;
  const wFT=planWeekly.reduce((s,w)=>s+w.frac,0)||1;
  const pairs=[];
  planWeekly.forEach((pw,wi)=>{
    const a=(platActuals||{})[wi];if(!a)return;
    const planImpr=(planPlatResult.impr||0)*(pw.frac/wFT);
    const planBud=(planPlatResult.bud||0)*(pw.frac/wFT);
    if(!planImpr)return;
    pairs.push({viewability:a.vimpr&&a.impr?a.vimpr/a.impr:null,vtr:a.views&&a.impr?a.views/a.impr:null,ctr:a.clicks&&a.impr?a.clicks/a.impr:null,impIndex:planImpr>0?a.impr/planImpr:null,budIndex:planBud>0&&a.budget?a.budget/planBud:null});
  });
  const last=pairs.slice(-nWeeks).filter(p=>p.impIndex!=null);
  if(!last.length)return null;
  const avg=fn=>{const v=last.map(fn).filter(x=>x!=null);return v.length?v.reduce((s,x)=>s+x,0)/v.length:null;};
  return{viewability:avg(p=>p.viewability),vtr:avg(p=>p.vtr),ctr:avg(p=>p.ctr),impIndex:avg(p=>p.impIndex),budIndex:avg(p=>p.budIndex),n:last.length};
}

function UploadModal({flight,result,onClose,onImport}){
  const[tab,setTab]=useState('paste');
  const[text,setText]=useState('');
  const[parsed,setParsed]=useState(null);
  const[error,setError]=useState('');
  const fl=flight;
  const r=result;

  function matchPlatform(name,platforms){
    const n=name.toLowerCase().trim();
    const direct=platforms.find(p=>p.p.label.toLowerCase()===n);
    if(direct)return direct;
    const ALIASES={dv360_yt:['youtube','dv360 youtube','dv 360 youtube'],eskimi_video:['eskimi in-stream','eskimi in stream','eskimi video'],teads_video:['teads out-stream','teads out stream','teads'],simpals_skin:['simpals fluid skin','fluid skin'],meteofor_brand:['meteofor branding','meteofor.md branding','meteofor'],dv360_display:['dv360 display','dv 360 display'],eskimi_rm:['eskimi rich media','eskimi rich'],simpals_rm:['simpals rich media','simpals rich'],meta_multi:['meta','facebook','fb+ig','meta multiformat'],gads_search:['google search','gads search','google ads search']};
    for(const[pid,aliases]of Object.entries(ALIASES)){if(aliases.some(a=>n.includes(a))){const found=platforms.find(p=>p.p.id===pid||p.p.id.includes(pid.split('_')[0]));if(found)return found;}}
    return platforms.find(p=>n.includes(p.p.label.toLowerCase().slice(0,5))||p.p.label.toLowerCase().includes(n.slice(0,5)));
  }

  function parseData(raw){
    if(!raw.trim()){return;}
    const lines=raw.trim().split('\n').map(l=>l.split(/[\t,;]/).map(c=>c.trim().replace(/^["']|["']$/g,'')));
    if(lines.length<2){setError('Need at least a header row and one data row.');return;}
    const hdr=lines[0].map(h=>h.toLowerCase());
    const ci={};
    const find=kws=>hdr.findIndex(h=>kws.some(k=>h.includes(k)));
    ci.platform=find(['platform','format','ad format','placement','source','channel','ad name']);
    ci.week=find(['week','date','period','start date','start']);
    ci.impr=find(['impression','impr','served impressions','total impressions']);
    ci.vimpr=find(['viewable imp','viewable_imp','view. imp','viewable impressions']);
    ci.views=find(['completed view','video view','views','vtr']);
    ci.clicks=find(['click']);
    ci.budget=find(['cost','spend','budget','eur','amount','revenue']);
    if(ci.impr===-1){setError('Cannot find Impressions column. Check headers.');return;}
    const platforms=r?.platforms||[];
    const weeklyMap={};const unmapped=new Set();
    lines.slice(1).forEach(row=>{
      if(!row[ci.impr]||row[ci.impr]==='')return;
      const platName=ci.platform>=0?row[ci.platform]:'';
      const pr=matchPlatform(platName,platforms);
      if(!pr){if(platName.trim())unmapped.add(platName);return;}
      const pid=pr.p.id;
      let weekIdx=0;
      if(ci.week>=0&&row[ci.week]){
        const rw=row[ci.week].toLowerCase();
        const wm=rw.match(/w(\d+)/i)||rw.match(/week\s*(\d+)/i);
        if(wm){const wn=parseInt(wm[1]);weekIdx=(r.weekly||[]).findIndex(w=>w.week===wn);}
        else{const d=new Date(row[ci.week]);if(!isNaN(d)){const wn=dateToWeek(d.toISOString().slice(0,10));weekIdx=(r.weekly||[]).findIndex(w=>w.week===wn);}}
        if(weekIdx<0)weekIdx=0;
      }
      if(!weeklyMap[pid])weeklyMap[pid]={};
      if(!weeklyMap[pid][weekIdx])weeklyMap[pid][weekIdx]={};
      const e=weeklyMap[pid][weekIdx];
      const n=s=>s&&s!==''?parseFloat(String(s).replace(/[^\d.-]/g,''))||0:0;
      if(ci.impr>=0)e.impr=(e.impr||0)+n(row[ci.impr]);
      if(ci.vimpr>=0)e.vimpr=(e.vimpr||0)+n(row[ci.vimpr]);
      if(ci.views>=0)e.views=(e.views||0)+n(row[ci.views]);
      if(ci.clicks>=0)e.clicks=(e.clicks||0)+n(row[ci.clicks]);
      if(ci.budget>=0)e.budget=(e.budget||0)+n(row[ci.budget]);
    });
    const rowCount=Object.values(weeklyMap).reduce((s,p)=>s+Object.keys(p).length,0);
    if(!rowCount){setError('No rows could be matched to platforms. Check platform names or use the template.');return;}
    setParsed({weeklyMap,unmapped:[...unmapped],rowCount,colsFound:Object.entries(ci).filter(([k,v])=>v>=0).map(([k])=>k)});
    setError('');
  }

  function handleFile(e){
    const f=e.target.files[0];if(!f)return;
    const reader=new FileReader();
    reader.onload=ev=>{const t=ev.target.result;setText(t);parseData(t);};
    reader.readAsText(f);
  }

  function downloadTemplate(){
    const platforms=r?.platforms||[];const weeks=r?.weekly||[];
    const rows=[['Platform','Week','Week Start Date','Impressions','Viewable Impressions','Views','Clicks','Budget Spent (EUR)']];
    platforms.forEach(pr=>{weeks.forEach(w=>{rows.push([pr.p.label,'W'+w.week,weekStartDate(w.week),'','','','','']);});});
    const csv=rows.map(row=>row.join('\t')).join('\n');
    const blob=new Blob([csv],{type:'text/tab-separated-values'});
    const url=URL.createObjectURL(blob);
    const a=document.createElement('a');a.href=url;a.download='actuals_template_'+flight.label.replace(/\s+/g,'_')+'.tsv';a.click();
  }

  return(<div className="modal-overlay" onClick={ev=>{if(ev.target===ev.currentTarget)onClose();}}>
    <div className="modal-box" style={{maxWidth:720,height:'82vh',display:'flex',flexDirection:'column'}}>
      <div className="modal-hdr"><span style={{fontWeight:700,fontSize:13,display:'inline-flex',alignItems:'center',gap:6}}><Upload size={13}/>Upload Actuals — {fl.label}</span><button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button></div>
      <div className="inp-drawer-tabs">{[['paste','Paste / Upload'],['template','Download Template'],['instructions','How It Works']].map(([t,l])=><button key={t} className={'tab-btn'+(tab===t?' on':'')} onClick={()=>setTab(t)}>{l}</button>)}</div>
      <div style={{flex:1,overflowY:'auto',padding:'16px 20px'}}>
        {tab==='paste'&&<>
          <p style={{fontSize:11,color:'var(--text3)',marginBottom:12}}>Paste data directly from Excel / Google Sheets, or upload a CSV/TSV exported from DV360, Meta Ads Manager, Google Ads, or any ad server. Columns are auto-detected; platforms are matched by name.</p>
          <div style={{display:'flex',gap:8,marginBottom:12,alignItems:'center'}}>
            <label className="btn btn-ghost btn-sm" style={{cursor:'pointer'}}>
              <FolderOpen size={13} style={{verticalAlign:'middle',marginRight:5}}/>Upload file (CSV / TSV)
              <input type="file" accept=".csv,.tsv,.txt" style={{display:'none'}} onChange={handleFile}/>
            </label>
            <span style={{fontSize:10,color:'var(--text3)'}}>or paste below ↓</span>
          </div>
          <textarea value={text} onChange={e=>{setText(e.target.value);if(e.target.value.length>30)parseData(e.target.value);}}
            placeholder={"Paste from Excel or ad server report...\n\nPlatform\tWeek\tImpressions\tViewable Impressions\tViews\tClicks\tCost\nDV 360 YouTube\tW1\t545000\t462250\t381500\t820\t600\nEskimi In-Stream\tW1\t163000\t122250\t114100\t195\t500"}
            style={{width:'100%',height:160,padding:'10px 12px',background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:6,fontSize:11,fontFamily:'monospace',resize:'vertical',outline:'none',boxSizing:'border-box'}}
          />
          {error&&<div style={{marginTop:8,padding:'8px 12px',background:'#FFE8E8',border:'1px solid #FFB0B0',borderRadius:5,fontSize:11,color:'var(--red)'}}>{error}</div>}
          {parsed&&<div style={{marginTop:12,padding:'12px 14px',background:'var(--gold-bg)',border:'1px solid var(--gold-border)',borderRadius:6}}>
            <div style={{fontWeight:700,fontSize:12,marginBottom:6,color:'var(--gold-dark)'}}>✓ {parsed.rowCount} platform-week rows parsed</div>
            <div style={{fontSize:10,color:'var(--text3)',marginBottom:4}}>Columns: {parsed.colsFound.join(' · ')}</div>
            {parsed.unmapped.length>0&&<div style={{fontSize:10,color:'var(--red)',marginBottom:8}}>⚠ Unmatched platforms (skipped): {parsed.unmapped.join(', ')}</div>}
            <div style={{overflowX:'auto',maxHeight:180}}>
              <table style={{borderCollapse:'collapse',fontSize:9,width:'100%'}}>
                <thead><tr style={{background:'var(--surface3)'}}><th style={TH}>Platform</th><th style={TH}>Week</th>{ACTUAL_FIELDS.map(f=><th key={f.key} style={TH}>{f.label}</th>)}</tr></thead>
                <tbody>{Object.entries(parsed.weeklyMap).flatMap(([pid,wkMap])=>{
                  const pr=(r?.platforms||[]).find(p=>p.p.id===pid);if(!pr)return[];
                  return Object.entries(wkMap).slice(0,6).map(([wi,vals])=>(
                    <tr key={pid+wi}><td style={{...TD,fontSize:9}}>{pr.p.label}</td><td style={{...TDc,fontSize:9}}>W{(r?.weekly||[])[+wi]?.week||+wi+1}</td>{ACTUAL_FIELDS.map(f=><td key={f.key} style={{...TDr,fontSize:9}}>{vals[f.key]?fn(Math.round(vals[f.key])):'—'}</td>)}</tr>
                  ));
                })}</tbody>
              </table>
            </div>
          </div>}
        </>}
        {tab==='template'&&<>
          <p style={{fontSize:11,color:'var(--text3)',marginBottom:14}}>Download a pre-filled template with all platforms and weeks for this flight. Fill from your ad server reports and upload it back.</p>
          <button className="btn btn-primary btn-sm" onClick={downloadTemplate}>⬇ Download Template (.TSV / Excel)</button>
          <div style={{marginTop:18,padding:'12px 14px',background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:6}}>
            <div style={{fontWeight:700,fontSize:11,marginBottom:8}}>Template columns:</div>
            <table style={{borderCollapse:'collapse',fontSize:10,width:'100%'}}>
              <thead><tr><th style={TH}>Column</th><th style={TH}>Source</th><th style={TH}>Notes</th></tr></thead>
              <tbody>{[['Platform','Ad server report','Must match platform name or known alias'],['Week','Flight week number','W1, W2… or date YYYY-MM-DD'],['Impressions','Ad server','Total served impressions'],['Viewable Impressions','Ad server','MRC viewable impressions'],['Views','Ad server','Completed video views (video only)'],['Clicks','Ad server','Total clicks'],['Budget Spent (EUR)','Ad server','Net media cost in EUR']].map(([c,s,n])=><tr key={c}><td style={{...TD,fontWeight:600}}>{c}</td><td style={{...TD,color:'var(--text3)'}}>{s}</td><td style={{...TD,color:'var(--text3)',fontSize:9}}>{n}</td></tr>)}</tbody>
            </table>
          </div>
        </>}
        {tab==='instructions'&&<div style={{fontSize:11,lineHeight:1.8,color:'var(--text2)'}}>
          <p><strong>Supported sources:</strong> DV360, Meta Ads Manager, Google Ads, Eskimi, Teads, Simpals — any platform that exports a CSV/TSV with impressions and spend per week.</p>
          <p><strong>DV360:</strong> Reports → Standard → Insertion Order + Week + Impressions + Viewable Impressions + Clicks + Revenue → Download CSV</p>
          <p><strong>Meta Ads Manager:</strong> Campaigns → Export → Columns: Impressions, Clicks, Amount Spent → Breakdown: Week</p>
          <p><strong>Column detection:</strong> Headers are matched automatically. Supported keywords: "impression", "viewable", "click", "cost", "spend", "week", "date", "platform".</p>
          <p><strong>Platform matching:</strong> Matched by name (partial). If not matched, appears in the warning and is skipped. Use the template for guaranteed matching.</p>
          <p><strong>Multiple uploads:</strong> Each import merges with existing data — it doesn't overwrite previously entered weeks.</p>
        </div>}
      </div>
      <div className="modal-footer">
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <div style={{flex:1}}/>
        {parsed&&parsed.rowCount>0&&<button className="btn btn-primary btn-sm" onClick={()=>onImport(parsed.weeklyMap)}>✓ Import {parsed.rowCount} platform-week rows</button>}
      </div>
    </div>
  </div>);
}

function ActualsTab({flights,allResults,actualsData,setActualsData,catalogue}){
  const[selId,setSelId]=useState(flights[0]?.id||null);
  const[showUpload,setShowUpload]=useState(false);
  const[benchmarkWeeks,setBenchmarkWeeks]=useState(12);
  const[selMetric,setSelMetric]=useState('impr');
  const[viewMode,setViewMode]=useState('table');

  const fl=flights.find(f=>f.id===selId)||flights[0];
  const r=fl&&allResults[fl.id];
  const flActuals=actualsData[selId]||{};
  const wFT=r?(r.weekly||[]).reduce((s,w)=>s+w.frac,0)||1:1;
  const today=new Date();

  function handleImport(weeklyMap){
    setActualsData(prev=>({...prev,[selId]:{...(prev[selId]||{}),...Object.fromEntries(Object.entries(weeklyMap).map(([pid,wkMap])=>[pid,{...((prev[selId]||{})[pid]||{}),...wkMap}]))}}));
    setShowUpload(false);
  }

  const platformBenchmarks=useMemo(()=>{
    if(!r)return{};
    const out={};
    (r.platforms||[]).forEach(pr=>{out[pr.p.id]=computePlatformBenchmarks(flActuals[pr.p.id],pr,r.weekly,benchmarkWeeks);});
    return out;
  },[flActuals,r,benchmarkWeeks]);

  const platTotals=useMemo(()=>{
    if(!r)return{};
    const out={};
    (r.platforms||[]).forEach(pr=>{
      const pid=pr.p.id;const acts=flActuals[pid]||{};
      const totA={impr:0,vimpr:0,views:0,clicks:0,budget:0};
      Object.values(acts).forEach(w=>{ACTUAL_FIELDS.forEach(f=>{totA[f.key]+=(w[f.key]||0);});});
      out[pid]={act:totA,plan:{impr:pr.impr||0,vimpr:pr.vimpr||0,views:pr.views||0,clicks:pr.clicks||0,budget:pr.bud||0},weeksWithData:Object.keys(acts).filter(i=>acts[i]&&acts[i].impr>0).length};
    });
    return out;
  },[flActuals,r]);

  if(!fl||!r)return<div style={{padding:24,color:'var(--text3)'}}>No flights.</div>;

  const metric=ACTUAL_FIELDS.find(f=>f.key===selMetric)||ACTUAL_FIELDS[0];
  const weeksTotal=(r.weekly||[]).length;
  const weeksPast=(r.weekly||[]).filter(w=>new Date(weekStartDate(w.week,w.year||YEAR))<today).length;
  const grandAct={impr:0,vimpr:0,views:0,clicks:0,budget:0};
  const grandPlan={impr:r.tot.impressions,vimpr:r.tot.vimpressions,views:r.tot.views,clicks:r.tot.clicks,budget:fl.budget||0};
  Object.values(platTotals).forEach(pt=>ACTUAL_FIELDS.forEach(f=>{grandAct[f.key]+=(pt.act[f.key]||0);}));

  return(<div style={{padding:'16px 24px'}}>
    <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:14,flexWrap:'wrap'}}>
      <select className="sel" style={{minWidth:260}} value={selId||''} onChange={e=>setSelId(e.target.value)}>
        {flights.map(f=><option key={f.id} value={f.id}>{f.brand} — {f.label} ({fdate(f.startDate)}–{fdate(f.endDate)})</option>)}
      </select>
      <div style={{fontSize:10,color:'var(--text3)'}}>{weeksPast} of {weeksTotal} weeks elapsed</div>
      <div style={{flex:1}}/>
      {[['table','Weekly Table'],['summary','By Platform']].map(([v,l])=>(
        <button key={v} className={'btn btn-sm'+(viewMode===v?' btn-primary':' btn-ghost')} onClick={()=>setViewMode(v)}>{l}</button>
      ))}
      <button className="btn btn-ghost btn-sm" style={{display:'inline-flex',alignItems:'center',gap:5}} onClick={()=>setShowUpload(true)}><Upload size={13}/>Upload Actuals</button>
    </div>

    {/* Grand total progress */}
    <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:16}}>
      {ACTUAL_FIELDS.map(f=>{
        const pv=grandPlan[f.key]||0,av=grandAct[f.key]||0;
        const pct=pv>0?av/pv:0;
        const expPct=weeksTotal>0?weeksPast/weeksTotal:0;
        const isLow=weeksPast>0&&pct<expPct*0.85,isHigh=weeksPast>0&&pct>expPct*1.15;
        return(<div key={f.key} onClick={()=>setSelMetric(f.key)} style={{flex:1,minWidth:95,background:selMetric===f.key?'var(--gold-bg)':'#fff',border:'1px solid '+(selMetric===f.key?'var(--gold-border)':'var(--border)'),borderRadius:7,padding:'8px 10px',cursor:'pointer'}}>
          <div style={{fontSize:9,color:f.color,fontWeight:700,textTransform:'uppercase',letterSpacing:'.05em',marginBottom:3}}>{f.label}</div>
          <div style={{fontSize:12,fontWeight:800}}>{f.key==='budget'?fe(av):fn(Math.round(av))}</div>
          <div style={{fontSize:9,color:'var(--text3)',marginBottom:4}}>/ {f.key==='budget'?fe(pv):fn(Math.round(pv))}</div>
          <div style={{height:4,background:'var(--surface3)',borderRadius:3,overflow:'hidden'}}>
            <div style={{height:'100%',borderRadius:3,background:isLow?'var(--red)':isHigh?'var(--amber)':f.color,width:Math.min(100,pct*100)+'%'}}/>
          </div>
          <div style={{fontSize:9,fontWeight:700,marginTop:2,color:isLow?'var(--red)':isHigh?'var(--amber)':'var(--text3)'}}>{(pct*100).toFixed(1)}%{isLow?' ⚠ Under':isHigh?' ↑ Over':''}</div>
        </div>);
      })}
    </div>

    {viewMode==='summary'&&<>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8,flexWrap:'wrap'}}>
        <span style={{fontSize:11,fontWeight:700}}>Platform totals vs plan</span>
        <label style={{fontSize:10,color:'var(--text3)',marginLeft:8,display:'flex',alignItems:'center',gap:4}}>
          Benchmarks over <select className="sel" value={benchmarkWeeks} style={{width:52,fontSize:10}} onChange={e=>setBenchmarkWeeks(+e.target.value)}>{[4,8,12].map(n=><option key={n}>{n}</option>)}</select> weeks
        </label>
      </div>
      <div style={{overflowX:'auto'}}>
        <table style={{borderCollapse:'collapse',fontSize:10,width:'100%'}}>
          <thead>
            <tr style={{background:'#1A3A5C'}}>
              <th style={{...TH,color:'#fff',textAlign:'left',minWidth:160,position:'sticky',left:0,background:'#1A3A5C',zIndex:2}}>Platform</th>
              <th style={{...TH,color:'#fff',width:52}}>Weeks</th>
              {ACTUAL_FIELDS.map(f=><th key={f.key} style={{...TH,color:'#fff'}} colSpan={2}>{f.label}</th>)}
              <th style={{...TH,color:'#C8A86B',width:70}}>View. idx</th>
              <th style={{...TH,color:'#C8A86B',width:60}}>Imp. idx</th>
            </tr>
            <tr style={{background:'#2A4A6C'}}>
              <th style={{...TH,position:'sticky',left:0,background:'#2A4A6C',zIndex:2,color:'#C8A86B'}}/>
              <th style={{...TH,color:'#C8A86B',fontSize:8}}>w/data</th>
              {ACTUAL_FIELDS.map(f=>[<th key={f.key+'p'} style={{...TH,color:'#C8A86B',fontSize:8}}>Plan</th>,<th key={f.key+'a'} style={{...TH,color:'#fff',fontSize:8}}>Actual</th>])}
              <th style={{...TH,color:'#C8A86B',fontSize:8}}>vs plan</th>
              <th style={{...TH,color:'#C8A86B',fontSize:8}}>vs plan</th>
            </tr>
          </thead>
          <tbody>
            {(r.platforms||[]).map(pr=>{
              const pt=platTotals[pr.p.id];const bm=platformBenchmarks[pr.p.id];if(!pt)return null;
              const typ=AD_TYPES.find(t=>t.key===pr.p.type);
              return(<tr key={pr.p.id} style={{background:'#fff',borderBottom:'1px solid var(--border)'}}>
                <td style={{...TD,fontWeight:500,position:'sticky',left:0,background:'#fff',zIndex:1}}>
                  <div style={{display:'flex',alignItems:'center',gap:5}}><span style={{width:7,height:7,borderRadius:'50%',background:typ?.color,display:'inline-block',flexShrink:0}}/><span>{pr.p.label}</span></div>
                  <div style={{fontSize:8,color:'var(--text3)'}}>{pr.p.seller}</div>
                </td>
                <td style={{...TDc,color:pt.weeksWithData>0?'var(--green)':'var(--red)',fontWeight:700}}>{pt.weeksWithData}/{weeksTotal}</td>
                {ACTUAL_FIELDS.map(f=>{const pv=pt.plan[f.key]||0,av=pt.act[f.key]||0;const delta=pv>0&&av>0?(av-pv)/pv:null;const isOff=delta!=null&&Math.abs(delta)>0.1;return[<td key={f.key+'p'} style={{...TDr,color:'var(--text3)',fontSize:9}}>{f.key==='budget'?fe(pv):fn(Math.round(pv))}</td>,<td key={f.key+'a'} style={{...TDr,fontWeight:av>0?700:400,color:isOff?(delta>0?'var(--green)':'var(--red)'):'var(--text)'}}>{av>0?(f.key==='budget'?fe(av):fn(Math.round(av))):'—'}{delta!=null&&<span style={{fontSize:8,marginLeft:2,opacity:.8}}>{delta>=0?'+':''}{(delta*100).toFixed(0)}%</span>}</td>];})}
                <td style={{...TDc,fontWeight:700,color:bm?.viewability!=null?(bm.viewability>=(pr.p.viewability||0.7)?'var(--green)':'var(--red)'):'var(--text3)',fontSize:10}}>{bm?.viewability!=null?(bm.viewability*100).toFixed(1)+'%':'—'}</td>
                <td style={{...TDc,fontWeight:700,color:bm?.impIndex!=null?(bm.impIndex>=0.9?'var(--green)':'var(--red)'):'var(--text3)',fontSize:10}}>{bm?.impIndex!=null?bm.impIndex.toFixed(2)+'×':'—'}</td>
              </tr>);
            })}
          </tbody>
          <tfoot><tr style={{background:'#1A3A5C'}}>
            <td style={{...TD,color:'#C8A86B',fontWeight:700,position:'sticky',left:0,background:'#1A3A5C',zIndex:1}}>TOTAL</td>
            <td style={TDc}/>
            {ACTUAL_FIELDS.map(f=>{const pv=grandPlan[f.key]||0,av=grandAct[f.key]||0;const delta=pv>0&&av>0?(av-pv)/pv:null;return[<td key={f.key+'p'} style={{...TDr,color:'#C8A86B',fontSize:10}}>{f.key==='budget'?fe(pv):fn(Math.round(pv))}</td>,<td key={f.key+'a'} style={{...TDr,color:delta==null?'#fff':Math.abs(delta)<0.05?'#90EE90':delta<0?'#FFB0A0':'#90EE90',fontWeight:700,fontSize:10}}>{av>0?(f.key==='budget'?fe(av):fn(Math.round(av))):'—'}{delta!=null&&<span style={{fontSize:8,marginLeft:2}}>{delta>=0?'+':''}{(delta*100).toFixed(0)}%</span>}</td>];})}
            <td colSpan={2}/>
          </tr></tfoot>
        </table>
      </div>
    </>}

    {viewMode==='table'&&<>
      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:8,flexWrap:'wrap'}}>
        <span style={{fontSize:11,fontWeight:700,color:metric.color}}>{metric.label} — weekly by platform · plan vs actual</span>
        <div style={{display:'flex',gap:3,marginLeft:'auto'}}>
          {ACTUAL_FIELDS.map(f=><button key={f.key} onClick={()=>setSelMetric(f.key)} style={{fontSize:9,padding:'2px 7px',borderRadius:4,border:'1px solid '+(selMetric===f.key?f.color:'var(--border)'),background:selMetric===f.key?f.color:'transparent',color:selMetric===f.key?'#fff':'var(--text3)',cursor:'pointer'}}>{f.label}</button>)}
        </div>
      </div>
      <div style={{overflowX:'auto'}}>
        <table style={{borderCollapse:'collapse',fontSize:10,minWidth:500}}>
          <thead><tr style={{background:metric.color+'22',borderBottom:'2px solid '+metric.color}}>
            <th style={{...TH,background:metric.color+'22',textAlign:'left',minWidth:150,position:'sticky',left:0,zIndex:2}}>Platform</th>
            <th style={{...TH,background:metric.color+'22',width:60}}>Plan</th>
            {(r.weekly||[]).map((wk,wi)=>{const isPast=new Date(weekStartDate(wk.week,wk.year||YEAR))<today;return(<th key={wi} style={{...TH,background:isPast?metric.color+'33':metric.color+'11',width:66,fontSize:8}}>W{wk.week}<div style={{fontWeight:400,fontSize:7,color:'var(--text3)'}}>{fdate(weekStartDate(wk.week,wk.year||YEAR))}</div></th>);})}
            <th style={{...TH,background:metric.color,color:'#fff',width:68}}>Actual</th>
            <th style={{...TH,background:metric.color+'44',width:52,fontSize:8}}>Δ</th>
          </tr></thead>
          <tbody>
            {(r.platforms||[]).map(pr=>{
              const pid=pr.p.id;const typ=AD_TYPES.find(t=>t.key===pr.p.type);
              const planTot=selMetric==='budget'?pr.bud:selMetric==='impr'?pr.impr:selMetric==='vimpr'?pr.vimpr:selMetric==='views'?pr.views:pr.clicks;
              const platActs=flActuals[pid]||{};
              const actTot=Object.values(platActs).reduce((s,w)=>s+(w[selMetric]||0),0);
              const delta=planTot>0&&actTot>0?(actTot-planTot)/planTot:null;
              const isOff=delta!=null&&Math.abs(delta)>0.1;
              return(<tr key={pid} style={{background:'#fff',borderBottom:'1px solid var(--border)'}}>
                <td style={{...TD,fontWeight:500,position:'sticky',left:0,background:'#fff',zIndex:1}}>
                  <div style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:6,height:6,borderRadius:'50%',background:typ?.color,display:'inline-block',flexShrink:0}}/>{pr.p.label}</div>
                  <div style={{fontSize:8,color:'var(--text3)'}}>{pr.p.seller}</div>
                </td>
                <td style={{...TDr,color:'var(--text3)',fontSize:9}}>{selMetric==='budget'?fe(planTot):fn(Math.round(planTot||0))}</td>
                {(r.weekly||[]).map((wk,wi)=>{
                  const planW=(planTot||0)*(wk.frac/wFT);
                  const actW=(platActs[wi]||{})[selMetric]||null;
                  const dw=actW!=null&&planW>0?(actW-planW)/planW:null;
                  const isPast=new Date(weekStartDate(wk.week,wk.year||YEAR))<today;
                  return(<td key={wi} style={{...TDc,padding:'2px 3px',background:actW!=null?(dw!=null&&Math.abs(dw)>0.1?(dw>0?'#EAFFE8':'#FFE8E8'):'#F0F8FF'):'transparent'}}>
                    <div style={{fontSize:8,color:'var(--text3)',lineHeight:1.2}}>{selMetric==='budget'?fe(planW):fn(Math.round(planW))}</div>
                    <div style={{fontWeight:actW!=null?700:400,fontSize:9,color:actW!=null?(dw!=null&&Math.abs(dw)>0.1?(dw>0?'var(--green)':'var(--red)'):'var(--text)'):'var(--text3)'}}>
                      {actW!=null?(selMetric==='budget'?fe(actW):fn(Math.round(actW))):(isPast?<span style={{color:'var(--red)',fontSize:8}}>?</span>:'—')}
                    </div>
                  </td>);
                })}
                <td style={{...TDr,fontWeight:700,color:metric.color,fontSize:10}}>{actTot>0?(selMetric==='budget'?fe(actTot):fn(Math.round(actTot))):'—'}</td>
                <td style={{...TDc,fontWeight:700,color:isOff?(delta>0?'var(--green)':'var(--red)'):'var(--text3)',fontSize:10}}>{delta!=null?(delta>=0?'+':'')+((delta*100).toFixed(0))+'%':'—'}</td>
              </tr>);
            })}
          </tbody>
        </table>
      </div>
      <div style={{marginTop:10,fontSize:9,color:'var(--text3)',display:'flex',gap:16,flexWrap:'wrap'}}>
        <span>Each cell: <em>top = plan, bottom = actual</em></span>
        <span style={{color:'var(--green)'}}>■ Green = actual &gt; plan by &gt;10%</span>
        <span style={{color:'var(--red)'}}>■ Red = under-delivering or missing</span>
      </div>
    </>}

    {showUpload&&<UploadModal flight={fl} result={r} onClose={()=>setShowUpload(false)} onImport={handleImport}/>}
  </div>);
}

// ─── EXCEL EXPORT ─────────────────────────────────────────────────────────────
// Client-side XLSX export using SheetJS (loaded dynamically)
async function exportToExcel(flights,allResults,audiences,universe,mode,targetFlightId){
  // Dynamic import of the real xlsx package (fixed 2026-10-05 — see CLAUDE.md)
  const XLSX=await import('xlsx').catch(()=>null);
  if(!XLSX){alert('SheetJS could not be loaded. Try again.');return;}
  const wb=XLSX.utils.book_new();

  function addSheet(name,rows){
    const ws=XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb,ws,name);
    return ws;
  }

  // ── SUMMARY SHEET ──────────────────────────────────────────────────────────
  const brands=[...new Set(flights.map(f=>f.brand||'Brand 1'))];
  const sumRows=[
    ['Platform / Format','Buying Type','Unit','Paid Units (Impr)','CPM / CPC €','Main KPI','KPI abs.','KPI %','Cost per KPI €','Budget €','SHR%'],
  ];
  function buildSummaryRows(bf){
    const groups={};SUMMARY_GROUPS.forEach(g=>{groups[g.key]={label:g.label,types:g.types,rows:{},groupReach:0};});
    let totTrk=0,totAf=0;
    flights.filter(f=>bf==='__TOTAL__'||(f.brand||'Brand 1')===bf).forEach(f=>{
      const r=allResults[f.id];if(!r)return;
      totTrk+=r.tot.trackerCost||0;totAf+=r.tot.agencyFee||0;
      r.platforms.forEach(pr=>{
        const t=pr.p?.type||'banner';
        const gk=t==='video'?'video':t==='sem'?'sem':t==='social'?'social':t==='banner'?'display_std':'display_ns';
        const g=groups[gk];if(!g)return;
        const pid=pr.p.id;
        if(!g.rows[pid])g.rows[pid]={pid,label:pr.p.label,seller:pr.p.seller||'',type:t,budget:0,impr:0,vimpr:0,views:0,clicks:0,reach:0,cpm:pr.cpm||0,frq:pr.frq||0};
        const row=g.rows[pid];row.budget+=pr.bud;row.impr+=pr.impr;row.vimpr+=pr.vimpr;row.views+=pr.views;row.clicks+=pr.clicks;row.reach=sbN([row.reach,pr.reach||0]);
      });
      SUMMARY_GROUPS.forEach(sg=>{const pool=r.platforms.filter(pr=>sg.types.includes(pr.p?.type)).map(pr=>pr.reach||0);groups[sg.key].groupReach=sb2(groups[sg.key].groupReach,sbN(pool));});
    });
    const totBudAll=Object.values(groups).reduce((s,g)=>s+Object.values(g.rows).reduce((ss,r)=>ss+r.budget,0),0);
    const result=[];
    SUMMARY_GROUPS.forEach(sg=>{
      const g=groups[sg.key];const rows=Object.values(g.rows);if(!rows.length)return;
      const gBud=rows.reduce((s,r)=>s+r.budget,0);
      const gImpr=rows.reduce((s,r)=>s+r.impr,0);
      const gVimpr=rows.reduce((s,r)=>s+r.vimpr,0);
      const gViews=rows.reduce((s,r)=>s+r.views,0);
      const gClicks=rows.reduce((s,r)=>s+r.clicks,0);
      const isVideo=sg.key==='video',isSem=sg.key==='sem';
      const mainKpi=isVideo?gViews:isSem?gClicks:gVimpr;
      const kpiLabel=isVideo?'Views':isSem?'Clicks':'Viewable Impr.';
      result.push([g.label,'Mixed','CPM',Math.round(gImpr),gImpr>0?+(gBud/gImpr*1000).toFixed(2):'',kpiLabel,Math.round(mainKpi),(g.groupReach*100).toFixed(1)+'%','',+(gBud).toFixed(2),(totBudAll>0?((gBud/totBudAll)*100).toFixed(1)+'%':'')]);
      rows.forEach(row=>{
        const isV=row.type==='video',isS=row.type==='sem';
        const mkpi=isV?row.views:isS?row.clicks:row.vimpr;
        const mkpiLabel=isV?'Views':isS?'Clicks':'Viewable Impr.';
        const costKpi=mkpi>0?(row.budget/mkpi*1000).toFixed(2):'';
        result.push(['  '+row.label+' ('+row.seller+')',row.buyingType||'Programmatic','CPM',Math.round(row.impr),row.cpm?+row.cpm.toFixed(2):'',mkpiLabel,Math.round(mkpi),(row.impr>0?(mkpi/row.impr*100).toFixed(1)+'%':''),costKpi,+row.budget.toFixed(2),(totBudAll>0?((row.budget/totBudAll)*100).toFixed(1)+'%':'')]);
      });
    });
    result.push(['TOTAL ONLINE','','','','','','','','',+totBudAll.toFixed(2),'100%']);
    if(totTrk>0)result.push(['3rd Party Trackers','','','','','','','','',+totTrk.toFixed(2),'']);
    if(totAf>0)result.push(['Agency Fee','','','','','','','','',+totAf.toFixed(2),'']);
    return result;
  }

  [...brands,'__TOTAL__'].forEach(brand=>{
    const bRows=buildSummaryRows(brand);
    if(!bRows.length)return;
    const sheetRows=[['Brand: '+(brand==='__TOTAL__'?'ALL BRANDS':brand)],[''],...sumRows,...bRows];
    addSheet(brand==='__TOTAL__'?'Summary-ALL':(brand.slice(0,20)+'-Sum'),sheetRows);
  });

  // ── DETAILS SHEETS — one per flight (or selected) ─────────────────────────
  const toExport=targetFlightId?flights.filter(f=>f.id===targetFlightId):flights;
  toExport.forEach(fl=>{
    const r=allResults[fl.id];if(!r||!r.platforms.length)return;
    const wkHeaders=['Platform','Format','Seller','TOTAL',...r.weekly.map(wk=>'W'+wk.week+' '+fdate(weekStartDate(wk.week))),...MONTHS.map((m,mi)=>{const ws=MONTH_WEEKS[mi];return r.weekly.some(wk=>ws.includes(wk.week))?MN[mi]:null;}).filter(Boolean)];
    const wFT=r.weekly.reduce((s,wk)=>s+wk.frac,0)||1;
    const metrics=[
      {label:'IMPRESSIONS',fn:pr=>({tot:pr.impr,weeks:r.weekly.map(wk=>pr.impr*(wk.frac/wFT))})},
      {label:'FRQ (weekly)',fn:pr=>({tot:pr.frq,weeks:r.weekly.map(()=>pr.frq)})},
      {label:'UNIQUE COOKIES',fn:pr=>({tot:pr.uniqueCookies||0,weeks:r.weekly.map(wk=>(pr.uniqueCookies||0)*(wk.frac/wFT))})},
      {label:'TA REAL USERS (cookies/ruc)',fn:pr=>({tot:(pr.uniqueCookies||0)/(pr.p.real_users_coef||2),weeks:r.weekly.map(wk=>((pr.uniqueCookies||0)/(pr.p.real_users_coef||2))*(wk.frac/wFT))})},
      {label:'CLICKS',fn:pr=>({tot:pr.clicks,weeks:r.weekly.map(wk=>pr.clicks*(wk.frac/wFT))})},
      {label:'VIEWS',fn:pr=>({tot:pr.views,weeks:r.weekly.map(wk=>pr.views*(wk.frac/wFT))})},
      {label:'VIEWABLE IMPRESSIONS',fn:pr=>({tot:pr.vimpr,weeks:r.weekly.map(wk=>pr.vimpr*(wk.frac/wFT))})},
    ];
    const detRows=[['Flight: '+fl.brand+' — '+fl.label],['Period: '+fdate(fl.startDate)+' – '+fdate(fl.endDate)],['Budget: '+(fl.budget||0)+' EUR'],['']];
    metrics.forEach(m=>{
      detRows.push([m.label]);
      detRows.push(wkHeaders);
      r.platforms.forEach(pr=>{
        const d=m.fn(pr);
        const monthTots=MONTHS.map((_,mi)=>{const ws=MONTH_WEEKS[mi];const hasData=r.weekly.some(wk=>ws.includes(wk.week));if(!hasData)return null;return r.weekly.filter(wk=>ws.includes(wk.week)).reduce((s,wk)=>s+d.weeks[r.weekly.findIndex(x=>x.week===wk.week)],0);}).filter(v=>v!==null);
        detRows.push([pr.p.label,pr.p.type,pr.p.seller||'',+d.tot.toFixed(1),...d.weeks.map(v=>+v.toFixed(1)),...monthTots.map(v=>+v.toFixed(1))]);
      });
      // total row
      const totTot=r.platforms.reduce((s,pr)=>s+m.fn(pr).tot,0);
      const totWeeks=r.weekly.map((_,wi)=>r.platforms.reduce((s,pr)=>s+m.fn(pr).weeks[wi],0));
      const totMo=MONTHS.map((_,mi)=>{const ws=MONTH_WEEKS[mi];return r.weekly.some(wk=>ws.includes(wk.week))?r.weekly.filter(wk=>ws.includes(wk.week)).reduce((s,wk)=>s+totWeeks[r.weekly.findIndex(x=>x.week===wk.week)],0):null;}).filter(v=>v!==null);
      detRows.push(['TOTAL','','',+totTot.toFixed(1),...totWeeks.map(v=>+v.toFixed(1)),...totMo.map(v=>+v.toFixed(1))]);
      detRows.push(['']);
    });
    // Cumulative reach
    detRows.push(['CUMULATIVE vRCH%']);
    detRows.push(['Week','Date','Days active','Weekly +RCH%','Cumulative RCH%','Est. TA Users reached']);
    let cumPub2=0,cumExt2=0;
    r.weekly.forEach(wk=>{cumPub2=Math.min(sb2(cumPub2,wk.publisherReach||0),r.publisherCap??1);cumExt2=sb2(cumExt2,wk.externalReach||0);const cum=Math.min(0.98,sb2(cumPub2,cumExt2));detRows.push(['W'+wk.week,weekStartDate(wk.week),Math.round(wk.frac*7),(wk.reach*100).toFixed(2)+'%',(cum*100).toFixed(2)+'%',Math.round(cum*universe)]);});
    addSheet((fl.brand.slice(0,10)+'-'+fl.label.slice(0,10)).replace(/[^\w]/g,'_').slice(0,30),detRows);
  });

  XLSX.writeFile(wb,'DigitalMediaplan_'+new Date().toISOString().slice(0,10)+'.xlsx');
}

// ─── M1 HELPERS ───────────────────────────────────────────────────────────────
function fgToType(fg){const m={video:'video',non_standard_display:'nonstandard',sem:'sem',social:'social'};return m[fg]||'banner';}
// Map client override fields (Client.catalogue_overrides' own key names) to frontend catalogue
// field names — shared with CatalogueTable.js's client-scope Catalogue tab (2026-10-04) via
// overrideKeyToFieldKey, so the drawer's client scope and the Client page's Catalogue tab always
// read/write the identical JSON shape.
function _mapClientOvr(ovr){
  if(!ovr)return{};
  const out={};
  Object.entries(ovr).forEach(([k,v])=>{out[overrideKeyToFieldKey(k)]=v;});
  return out;
}

// The 3-layer catalogue merge (global DB -> client overrides -> saved plan snapshot wins for
// whatever fields it has) — extracted 2026-10-05 so Compare (ComparePlansPage.js) can resolve a
// plan's EFFECTIVE catalogue the exact same way the editor's own mount-time useEffect does, not a
// second reimplementation. This matters because a frozen catalogue entry is NOT a complete,
// self-sufficient snapshot in this codebase: layer 3 only overwrites the fields it explicitly
// carries, so any field the frozen entry omits silently falls through to layers 1-2's CURRENT live
// values — found live while verifying Compare (a plan's computed reach disagreed with its own KPI
// bar until this exact merge was reused, because a throwaway test catalogue entry had omitted
// `viewability`, which calcFlight's own inline `||0.7` fallback can't distinguish from "the global
// row's current real viewability happens to be 0.7").
// Returns {catalogue, legacyToDbId} — legacyToDbId is only needed by the live editor's own
// one-time legacy-platform-id flight remap; Compare has no flights to remap and can ignore it.
export function mergePlanCatalogue(globalPlatforms, clientOverrides, frozenCatalogue) {
  frozenCatalogue = frozenCatalogue || [];
  clientOverrides = clientOverrides || {};
  const labelToLegacyId={};
  DEFAULT_PLATFORMS.forEach(dp=>{labelToLegacyId[dp.label.toLowerCase().trim()]=dp.id;});
  const savedById={};
  frozenCatalogue.forEach(p=>{if(p._db_id)savedById[String(p._db_id)]=p;});
  const dbPlats=(globalPlatforms||[]).map(p=>{
    const legacyId=labelToLegacyId[p.name.toLowerCase().trim()]||null;
    // Layer 1: global
    const base={id:'db_'+p.id,_db_id:p.id,_legacy_id:legacyId,label:p.name,seller:p.seller||'',type:p.type||fgToType(p.format_group),format_group:p.format_group||null,base_cpm:p.base_cpm||0,cpc:p.cpc||null,base_discount:p.base_discount??null,viewability:p.viewability||0.7,audience_comp:p.audience_comp||0.65,real_users_coef:p.real_users_coef||2.0,ctr:p.ctr||0.001,vtr:p.vtr||null,engagement_rate:p.engagement_rate??null,weekly_frq:p.weekly_frq||4,monthly_frq:p.monthly_frq||16,reach_pool:p.reach_pool||'display',active:p.active!==false,priority:typePri(fgToType(p.format_group),p.reach_pool||null),seasonal_cpm:p.seasonal_cpm||null,cvr:p.cvr||null,site_id:p.site_id||null,device:p.device||null,format_overlap_group:p.format_overlap_group||null,max_weekly_frequency:p.max_weekly_frequency||null,weekly_impression_cap:p.weekly_impression_cap||null,monthly_impression_cap:p.monthly_impression_cap||null};
    // Layer 2: client overrides
    const cOvr=clientOverrides[String(p.id)]||{};
    const withClient=Object.keys(cOvr).length?{...base,..._mapClientOvr(cOvr),_client_overrides:cOvr}:base;
    // Layer 3: saved plan snapshot wins for existing platforms
    const saved=savedById[String(p.id)];
    if(saved)return{...withClient,...saved,id:'db_'+p.id,_db_id:p.id,_legacy_id:legacyId,_client_overrides:cOvr,_has_plan_override:true};
    return withClient;
  });
  const legacyToDbId={};
  dbPlats.forEach(p=>{if(p._legacy_id)legacyToDbId[p._legacy_id]=p.id;});
  const dbLabelToId={};
  dbPlats.forEach(p=>{dbLabelToId[p.label.toLowerCase().trim()]=p.id;});
  frozenCatalogue.forEach(p=>{
    if(!p._db_id&&p.label){const dbId=dbLabelToId[p.label.toLowerCase().trim()];if(dbId)legacyToDbId[p.id]=dbId;}
  });
  const savedCustom=frozenCatalogue.filter(x=>!x._db_id);
  const dbLabels=new Set(dbPlats.map(p=>p.label.toLowerCase().trim()));
  const trulyCustom=savedCustom.filter(p=>!dbLabels.has((p.label||'').toLowerCase().trim()));
  return {catalogue:[...dbPlats,...trulyCustom], legacyToDbId};
}
// Is `tracker` eligible on the platform whose real DB id is `platformDbId`? Absence of a row
// means not eligible (explicit opt-in, per the admin matrix).
function isTrackerEligible(platformDbId,trackerId,trackerEligibility){
  if(platformDbId==null)return false;
  return(trackerEligibility||[]).some(e=>e.platform_id===platformDbId&&e.tracker_id===trackerId&&e.eligible);
}
// Single source of truth for "which trackers actually apply to this platform-line" — used by
// calcFlight's cost calc, the per-platform exception checkboxes, and the Gemius hook below, so
// none of them can drift out of agreement on what "active" means.
// Master toggle (fl.trackerSettings[t.id].enabled) must be on, the platform must be eligible per
// the admin matrix, AND the platform must not be listed in that tracker's excludedPlatformIds —
// all three, not any one. A platform added to the flight after the tracker was turned on needs no
// entry anywhere to read as included: absence from excludedPlatformIds already means "included".
function activeTrackersForLine(platformDbId,trackerSettings,trackersCatalogue,trackerEligibility){
  if(platformDbId==null)return[];
  const settings=trackerSettings||{};
  return(trackersCatalogue||[]).filter(t=>{
    const s=settings[t.id];
    if(!s||!s.enabled)return false;
    if(!isTrackerEligible(platformDbId,t.id,trackerEligibility))return false;
    return!(s.excludedPlatformIds||[]).includes(platformDbId);
  });
}
// Phase 2 hook point: ZE-code generation (not built yet) triggers off this exact boolean —
// whether Gemius/GDE actually applies to a given platform-line once toggle + eligibility +
// per-platform exclusion have all been resolved. Kept as its own named function, not buried in
// generic tracker-list logic, so Phase 2 has one clear place to call.
// eslint-disable-next-line no-unused-vars
function isGemiusActive(platformDbId,trackerSettings,trackersCatalogue,trackerEligibility){
  const gemius=(trackersCatalogue||[]).find(t=>(t.name||'').toLowerCase().includes('gemius'));
  if(!gemius)return false;
  return activeTrackersForLine(platformDbId,trackerSettings,trackersCatalogue,trackerEligibility).some(t=>t.id===gemius.id);
}
function typePri(t){const P={video:{awareness:9,views:10,traffic:5,conversion:6},banner:{awareness:5,views:1,traffic:7,conversion:4},branding:{awareness:8,views:1,traffic:5,conversion:2},nonstandard:{awareness:7,views:1,traffic:6,conversion:3},social:{awareness:7,views:3,traffic:9,conversion:8},sem:{awareness:1,views:1,traffic:10,conversion:10}};return P[t]||P.banner;}

// ─── MAIN ENGINE ──────────────────────────────────────────────────────────────

function DigitalQuickWizard({onDone,onCancel,catalogue,sitesMap}){
  const[budget,setBudget]=React.useState(50000);
  const[brands,setBrands]=React.useState(1);
  const[objective,setObjective]=React.useState('awareness');
  const[flightMode,setFlightMode]=React.useState('4');
  function generate(){
    const brandNames=Array.from({length:brands},(_,i)=>'Activation '+(i+1));
    const budPerBrand=Math.round(budget/brands);
    const newFlights=[];let idx=0;
    brandNames.forEach(brand=>{
      if(flightMode==='allyear'){
        newFlights.push({...mkFlight(idx++,brand,catalogue,sitesMap),budget:budPerBrand,objective,startWeek:1,weeks:52,startDate:weekStartDate(1),endDate:weekEndDate(52),platforms:smartAllocate(objective,budPerBrand,catalogue,sitesMap,{weeks:52,startDate:weekStartDate(1),endDate:weekEndDate(52)})});
      } else {
        const n=parseInt(flightMode)||1,budPerFlight=Math.round(budPerBrand/n),spacing=Math.floor(52/n);
        for(let fi=0;fi<n;fi++){const sw=fi*spacing+1;const fStart=weekStartDate(sw),fEnd=weekEndDate(sw+3);newFlights.push({...mkFlight(idx++,brand,catalogue,sitesMap),budget:budPerFlight,objective,startWeek:sw,weeks:4,startDate:fStart,endDate:fEnd,platforms:smartAllocate(objective,budPerFlight,catalogue,sitesMap,{weeks:4,startDate:fStart,endDate:fEnd})});}
      }
    });
    onDone(newFlights);
  }
  const IS={width:'100%',padding:'6px 10px',borderRadius:6,border:'1px solid var(--border)',background:'var(--surface2)',fontSize:12,color:'var(--text)',outline:'none'};
  const LS={fontSize:10,color:'var(--text3)',fontWeight:600,textTransform:'uppercase',letterSpacing:'.04em',marginBottom:4,display:'block'};
  return(<div>
    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
      <div><label style={LS}>Total Budget €</label><input type="text" inputMode="numeric" style={IS} value={budget} onFocus={e=>e.target.select()} onChange={e=>setBudget(parseFloat(e.target.value)||0)}/></div>
      <div><label style={LS}>Number of Activations</label><input type="text" inputMode="numeric" style={IS} value={brands} onFocus={e=>e.target.select()} onChange={e=>setBrands(Math.max(1,parseInt(e.target.value)||1))}/></div>
    </div>
    <div style={{marginBottom:12}}><label style={LS}>Objective</label>
      <div style={{display:'flex',gap:6}}>{OBJECTIVES.map(o=><button key={o.id} onClick={()=>setObjective(o.id)} style={{flex:1,padding:'6px 4px',borderRadius:6,border:'1px solid '+(objective===o.id?o.color:'var(--border)'),background:objective===o.id?o.color:'var(--surface2)',color:objective===o.id?'#fff':'var(--text2)',fontSize:10,fontWeight:600,cursor:'pointer'}}>{o.label}</button>)}</div>
    </div>
    <div style={{marginBottom:20}}><label style={LS}>Flights per Activation</label>
      <div style={{display:'flex',gap:6}}>{[['allyear','All Year'],['1','1'],['2','2'],['3','3'],['4','4']].map(([v,l])=><button key={v} onClick={()=>setFlightMode(v)} style={{flex:1,padding:'6px 4px',borderRadius:6,border:'1px solid '+(flightMode===v?'var(--gold)':'var(--border)'),background:flightMode===v?'var(--gold)':'var(--surface2)',color:flightMode===v?'#fff':'var(--text2)',fontSize:10,fontWeight:600,cursor:'pointer'}}>{l}</button>)}</div>
    </div>
    <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
      <button onClick={onCancel} style={{padding:'7px 16px',borderRadius:6,border:'1px solid var(--border)',background:'var(--surface2)',fontSize:12,cursor:'pointer'}}>Cancel</button>
      <button onClick={generate} style={{padding:'7px 20px',borderRadius:6,border:'none',background:'var(--gold)',color:'#fff',fontSize:12,fontWeight:700,cursor:'pointer',display:'inline-flex',alignItems:'center',gap:5}}><Zap size={13}/>Generate</button>
    </div>
  </div>);
}

export default function DigitalPlanningEngine({initialData,onSave,clientId,planStatus,readOnly,planId,brandId,aggregateMode,planName,clientName}){
  const{isAgency,hasSites,isSuperAdmin,canEditCatalogue}=useAuth();
  const navigate=useNavigate();
  const[catalogueViewOpen,setCatalogueViewOpen]=useState(false);
  const[showWizard,setShowWizard]=useState(false);
  const[flights,setFlights]=useState(()=>{if(initialData?.flights?.length)return initialData.flights;return[];});
  const[selId,setSelId]=useState(()=>flights[0]?.id||null);
  const[rightTab,setRightTab]=useState('flowchart');
  const[tabZoom,setTabZoom]=useState(100);
  // Which real calendar year the Flowchart/Details month-grid columns are scoped to —
  // the grid itself (MONTH_WEEKS, week-number matching) is otherwise year-blind, so a
  // plan whose flights span more than one real year needs an explicit selection to
  // disambiguate which year's weeks populate the columns. null until derived from the
  // plan's own flights (see planYears effect below) — never hardcoded to YEAR=2026.
  const[displayYear,setDisplayYear]=useState(null);
  const[saved,setSaved]=useState(false);
  const[audMgrOpen,setAudMgrOpen]=useState(false);
  const[addingBrand,setAddingBrand]=useState(false);
  const[newBrand,setNewBrand]=useState('');
  const[catalogue,setCatalogue]=useState(()=>initialData?.catalogue&&initialData.catalogue.length>0?initialData.catalogue:DEFAULT_PLATFORMS.map(p=>({...p})));
  const[universe,setUniverse]=useState(()=>{const u=initialData?.universe||DEF_UNIVERSE;return u===531192?DEF_UNIVERSE:u;});
  // Refresh-from-catalogue bookkeeping (step 3b, 2026-10-04) — both are written immediately to the
  // DB by their own dedicated routes (PATCH .../catalogue adds to catalogue_deliberate, POST
  // .../catalogue/refresh|undo-refresh write both), independent of the main Save button. Mirrored
  // into this component's own state too, and included in handleSave's payload below, so a normal
  // Save click (which replaces `data` wholesale via the generic PATCH /plans/{id}) never reverts
  // them to whatever this page happened to load with — the exact same risk class `catalogue` itself
  // already has to guard against.
  const[catalogueDeliberate,setCatalogueDeliberate]=useState(()=>initialData?.catalogue_deliberate||{});
  const[lastRefresh,setLastRefresh]=useState(()=>initialData?.last_refresh||null);
  const[audiences,setAudiences]=useState(()=>{const auds=(initialData?.audiences&&initialData.audiences.length>0)?initialData.audiences:DEFAULT_AUDIENCES.map(a=>({...a}));return auds.map(a=>({...a,universe:a.universe===531192?1887226:a.universe}));});
  const[actualsData,setActualsData]=useState(()=>initialData?.actualsData||{});
  // Trackers catalogue: same three-layer merge shape as Platforms (global -> client override ->
  // plan snapshot wins wholesale) but independent code — see the tracker-override useEffect below.
  const[trackersCatalogue,setTrackersCatalogue]=useState(()=>initialData?.trackersCatalogue&&initialData.trackersCatalogue.length>0?initialData.trackersCatalogue:[]);
  const[trackerEligibility,setTrackerEligibility]=useState([]); // [{platform_id, tracker_id, eligible}]
  const[sitesMap,setSitesMap]=useState({});
  const[siteOverlaps,setSiteOverlaps]=useState([]);
  const[catalogueReady,setCatalogueReady]=useState(false);
  const[sitesReady,setSitesReady]=useState(false);
  const[showBriefBanner,setShowBriefBanner]=useState(false);
  const briefAppliedRef=useRef(false);
  const location=useLocation();

  useEffect(()=>{
    // Three-layer merge: Global DB → Client overrides → Plan snapshot
    const globalFetch=api.getPlatforms(false).catch(()=>[]);
    const clientFetch=clientId?api.getClientCatalogueOverrides(clientId).catch(()=>({})):Promise.resolve({});
    // Also load audiences from DB on mount
    const audFetch=api.getAudiences(clientId).catch(()=>[]);

    Promise.all([globalFetch,clientFetch,audFetch]).then(([data,clientOverrides,dbAuds])=>{
      if(dbAuds&&dbAuds.length){
        const dbMapped=dbAuds.map(a=>({id:'db_'+a.id,_db_id:a.id,name:a.name,universe:a.universe,platComp:a.plat_comp||{},scope:a.scope,client_id:a.client_id}));
        const dbByName={};dbMapped.forEach(a=>{dbByName[a.name.toLowerCase().trim()]=a;});
        setAudiences(prev=>{
          // Plan snapshot always wins for universe + platComp — DB provides _db_id and scope
          const merged=dbMapped.map(dbA=>{
            const saved=prev.find(a=>a.name.toLowerCase().trim()===dbA.name.toLowerCase().trim());
            if(saved){
              return{
                ...dbA,                          // DB base (id, _db_id, scope)
                universe:saved.universe??dbA.universe,  // plan universe wins
                platComp:Object.keys(saved.platComp||{}).length>0?saved.platComp:dbA.platComp,  // plan platComp wins if set
                _has_plan_override:saved.universe!==dbA.universe||Object.keys(saved.platComp||{}).length>0,
              };
            }
            return dbA;
          });
          const planOnly=prev.filter(a=>!a._db_id&&!dbByName[a.name.toLowerCase().trim()]);
          return [...merged,...planOnly];
        });
      }
    }).catch(()=>{});

    Promise.all([globalFetch,clientFetch]).then(([data,clientOverrides])=>{
      if(!data||!data.length)return;
      const {catalogue:mergedCatalogue,legacyToDbId}=mergePlanCatalogue(data,clientOverrides,initialData?.catalogue);
      setFlights(prev=>prev.map(f=>({...f,platforms:f.platforms.map(pe=>({...pe,platformId:legacyToDbId[pe.platformId]||pe.platformId}))})));
      setCatalogue(mergedCatalogue);
    }).finally(()=>setCatalogueReady(true));
  },[]);

  useEffect(()=>{
    // Trackers three-layer merge: Global DB -> Client overrides -> Plan snapshot.
    // Independent code from the Platform merge above (not shared) — same shape, per diagnosis.
    const trackersFetch=api.getTrackers(false).catch(()=>[]);
    const trClientFetch=clientId?api.getClientTrackerOverrides(clientId).catch(()=>({})):Promise.resolve({});
    const eligFetch=api.getTrackerEligibility().catch(()=>[]);
    Promise.all([trackersFetch,trClientFetch,eligFetch]).then(([trs,trClientOverrides,elig])=>{
      setTrackerEligibility(elig||[]);
      if(!trs||!trs.length)return;
      const savedById={};
      (initialData?.trackersCatalogue||[]).forEach(t=>{if(t.id!=null)savedById[String(t.id)]=t;});
      const merged=trs.map(t=>{
        // Layer 1: global
        const base={id:t.id,name:t.name,base_cpm:t.base_cpm||0,active:t.active!==false};
        // Layer 2: client override (partial diff)
        const cOvr=trClientOverrides[String(t.id)]||{};
        const withClient=Object.keys(cOvr).length?{...base,...cOvr,_client_overrides:cOvr}:base;
        // Layer 3: saved plan snapshot wins wholesale
        const saved=savedById[String(t.id)];
        if(saved)return{...withClient,...saved,id:t.id,_has_plan_override:true};
        return withClient;
      });
      setTrackersCatalogue(merged);
    });
  },[clientId]);

  useEffect(()=>{
    // eslint-disable-next-line
    const sitesFetch=api.getSites().then(sites=>{
      const m={};
      (sites||[]).forEach(s=>{m[s.id]={monthly_real_users_total:s.monthly_real_users_total||0,monthly_real_users_desktop:s.monthly_real_users_desktop||0,monthly_real_users_mobile:s.monthly_real_users_mobile||0,monthly_real_users_app:s.monthly_real_users_app||0};});
      setSitesMap(m);
    }).catch(()=>{});
    // eslint-disable-next-line
    const overlapsFetch=api.getSiteOverlaps().then(ov=>setSiteOverlaps(ov||[])).catch(()=>{});
    Promise.all([sitesFetch,overlapsFetch]).finally(()=>setSitesReady(true));
  },[]);

  useEffect(()=>{
    if(briefAppliedRef.current)return;
    if(!catalogueReady||!sitesReady)return;
    if(flights.length>0){briefAppliedRef.current=true;return;}
    const params=new URLSearchParams(location.search);
    const fromBrief=params.get('from_brief');
    if(!fromBrief||!planId)return;
    briefAppliedRef.current=true;
    const stored=sessionStorage.getItem(`brief_flights_${planId}`);
    if(!stored)return;
    try{
      const configs=JSON.parse(stored);
      const newFlights=configs.map((cfg,i)=>({
        id:mkId(),label:`Flight ${i+1} — ${cfg.objective}`,brand:'Activation 1',
        color:FLIGHT_COLORS[i%FLIGHT_COLORS.length],
        startDate:cfg.startDate,endDate:cfg.endDate,startWeek:cfg.startWeek,weeks:cfg.weeks,
        budget:cfg.budget,objective:cfg.objective,audienceId:null,
        platforms:smartAllocate(cfg.objective,cfg.budget,catalogue,sitesMap,{weeks:cfg.weeks,startDate:cfg.startDate,endDate:cfg.endDate}),
        weeklyOverrides:{},agency_fee:0,agency_on_top:false
      }));
      setFlights(newFlights);
      setShowBriefBanner(true);
    }catch(e){}
    sessionStorage.removeItem(`brief_flights_${planId}`);
  },[catalogueReady,sitesReady,flights.length,location.search,planId,catalogue,sitesMap]);

  const allResults=useMemo(()=>{
    const res={};
    flights.forEach(f=>{
      const aud=audiences.find(a=>a.id===f.audienceId);
      res[f.id]=calcFlight(f,catalogue,aud?aud.universe:universe,aud||null,sitesMap,siteOverlaps,trackersCatalogue,trackerEligibility);
    });
    return res;
  },[flights,catalogue,universe,audiences,sitesMap,siteOverlaps,trackersCatalogue,trackerEligibility]);

  // Distinct real calendar years actually present across the plan's flights — derived
  // from each flight's own weekly[].year (populated by calcFlight's date-anchored walk),
  // same pattern as ExportMediaPlanModal's monthYearMap. Sorted ascending so [0] is the
  // earliest year any flight starts in.
  const planYears=useMemo(()=>{
    const ys=new Set();
    flights.forEach(f=>{const r=allResults[f.id];if(r&&r.weekly)r.weekly.forEach(wk=>ys.add(wk.year||YEAR));});
    return[...ys].sort((a,b)=>a-b);
  },[flights,allResults]);

  // Default displayYear to the plan's earliest real flight year once the plan's flights
  // have been calculated — mirrors ExportMediaPlanModal's monthIdx-defaulting effect.
  // Guarded on displayYear===null so this only fires once per plan load, not on every
  // recalculation (e.g. a user editing a flight's budget shouldn't silently snap the
  // Flowchart back to the earliest year if they've already picked a different one).
  useEffect(()=>{
    if(displayYear===null&&planYears.length)setDisplayYear(planYears[0]);
  },[planYears]);

  const planTot=useMemo(()=>combineTot(Object.values(allResults)),[allResults]);

  const brands=[...new Set(flights.map(f=>f.brand||'Activation 1'))];

  // Group (Activation) subtotals — same combineTot fold, scoped to each group's own flights.
  const groupTot=useMemo(()=>{
    const out={};
    brands.forEach(b=>{out[b]=combineTot(flights.filter(f=>(f.brand||'Activation 1')===b).map(f=>allResults[f.id]).filter(Boolean));});
    return out;
  },[flights,allResults,brands]);

  function updFlight(id,patch){setFlights(prev=>prev.map(f=>f.id===id?{...f,...patch}:f));setSaved(false);}
  function addFlight(brand){setFlights(prev=>[...prev,mkFlight(prev.length,brand,catalogue,sitesMap)]);setSaved(false);}
  function cloneFlight(id){const f=flights.find(x=>x.id===id);if(!f)return;setFlights(prev=>[...prev,{...f,id:mkId(),label:f.label+' (copy)'}]);setSaved(false);}
  function removeFlight(id){setFlights(prev=>prev.filter(f=>f.id!==id));setSaved(false);}
  function renameBrand(old_,new_){setFlights(prev=>prev.map(f=>f.brand===old_?{...f,brand:new_}:f));setSaved(false);}
  function handleSave(){if(onSave)onSave({flights,actualsData,audiences,catalogue,universe,trackersCatalogue,catalogue_deliberate:catalogueDeliberate,last_refresh:lastRefresh});setSaved(true);setTimeout(()=>setSaved(false),2500);}
  const handleExportExcel=useCallback((flightId)=>{exportToExcel(flights,allResults,audiences,universe,'details',flightId);},[flights,allResults,audiences,universe]);
  const handleExportSummaryExcel=useCallback(()=>{exportToExcel(flights,allResults,audiences,universe,'summary',null);},[flights,allResults,audiences,universe]);
  const handleExportFullPlan=useCallback(()=>{exportFullPlanExcel(flights,allResults,displayYear,planName,clientName);},[flights,allResults,displayYear,planName,clientName]);

  // Full-page Catalogue view (catalogue parity step 3a, 2026-10-04) — a sibling view of the normal
  // plan editor, not a route/drawer: swapped in here (inside this same component, after every hook
  // above has already run) so unsaved flights/mix in this component's own state survive the toggle.
  if(catalogueViewOpen){
    return <PlanCatalogueView planId={planId} planName={planName} planStatus={planStatus} catalogue={catalogue} setCatalogue={setCatalogue}
      canEdit={canEditCatalogue&&!readOnly} hasSites={hasSites} isSuperAdmin={isSuperAdmin} onBack={()=>setCatalogueViewOpen(false)}
      universe={universe} setUniverse={setUniverse}
      deliberate={catalogueDeliberate} setDeliberate={setCatalogueDeliberate}
      lastRefresh={lastRefresh} setLastRefresh={setLastRefresh}
      flights={flights} audiences={audiences} sitesMap={sitesMap} siteOverlaps={siteOverlaps}
      trackersCatalogue={trackersCatalogue} trackerEligibility={trackerEligibility}/>;
  }

  return(<div className="dpe-root">
    {/* TOP BAR */}
    {!aggregateMode&&<div className="dpe-topbar">
      <div style={{flex:1}}/>
      <div style={{display:'flex',gap:6,alignItems:'center'}}>
        <button className="btn btn-ghost btn-sm" style={{display:'inline-flex',alignItems:'center',gap:5}} onClick={()=>setAudMgrOpen(true)}><Users size={14}/>Audiences</button>
        <button className="btn btn-ghost btn-sm" style={{display:'inline-flex',alignItems:'center',gap:5}} onClick={()=>setCatalogueViewOpen(true)}><Table2 size={14}/>Catalogue</button>
        <button className="btn btn-ghost btn-sm" style={{display:'inline-flex',alignItems:'center',gap:5}} onClick={handleExportFullPlan}><Download size={14}/>Export Excel</button>
        <button className={'btn btn-sm'+(saved?' btn-saved':' btn-primary')} onClick={handleSave}>{saved?'✓ Saved':'Save'}</button>
      </div>
    </div>}

    {!aggregateMode&&showBriefBanner&&(
      <div style={{...C.feedback('info'),margin:'10px 16px 0',display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
        <span style={{display:'flex',alignItems:'center',gap:10}}>
          Plan created from Strategic Diagnostic
          {brandId&&<button onClick={()=>navigate(`/diagnostic/${brandId}`)} style={{background:'none',border:'none',color:'inherit',textDecoration:'underline',cursor:'pointer',fontSize:13,padding:0}}>View diagnostic →</button>}
        </span>
        <button onClick={()=>setShowBriefBanner(false)} style={{background:'none',border:'none',color:'inherit',cursor:'pointer',fontSize:16,lineHeight:1,padding:0}}>×</button>
      </div>
    )}

    <div className="dpe-body">
      {/* LEFT PANEL — hidden in aggregate mode */}
      {!aggregateMode&&<div className="dpe-left">
        <div style={{padding:'6px 10px',display:'flex',gap:6,borderBottom:'1px solid var(--border)',alignItems:'center'}}>
          <span style={{fontSize:10,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'.06em',flex:1}}>Flights</span>
          {addingBrand
            ?<div style={{display:'flex',gap:4}}><input autoFocus className="inp" value={newBrand} placeholder="Activation name…" style={{width:110,fontSize:10}} onChange={e=>setNewBrand(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'&&newBrand.trim()){addFlight(newBrand.trim());setNewBrand('');setAddingBrand(false);}if(e.key==='Escape')setAddingBrand(false);}}/><button className="btn btn-xs btn-primary" onClick={()=>{if(newBrand.trim()){addFlight(newBrand.trim());setNewBrand('');setAddingBrand(false);}}}>✓</button><button className="btn btn-xs btn-ghost" onClick={()=>setAddingBrand(false)}>✕</button></div>
            :<><button className="btn btn-xs btn-ghost" onClick={()=>addFlight(brands[0])}>+ Flight</button>
            <button className="btn btn-xs btn-ghost" onClick={()=>{setNewBrand('');setAddingBrand(true);}}>+ Activation</button>
            <button className="btn btn-ghost btn-xs" style={{background:"var(--gold)",color:"#fff",border:"none",display:'inline-flex',alignItems:'center',gap:4}} onClick={()=>setShowWizard(true)}><Zap size={11}/>Quick</button></>
          }
        </div>
        <div style={{overflowY:'auto',flex:1}}>
          {brands.map(brand=>(<div key={brand}>
            <BrandHeader brand={brand} onRename={n=>renameBrand(brand,n)} onAddFlight={()=>addFlight(brand)} groupTot={groupTot[brand]}/>
            {flights.filter(f=>f.brand===brand).map(f=>(
              <FlightCard key={f.id} flight={f} result={allResults[f.id]} isSelected={selId===f.id}
                onSelect={setSelId} onUpdate={p=>updFlight(f.id,p)} onClone={()=>cloneFlight(f.id)} onRemove={()=>removeFlight(f.id)}
                canRemove={flights.length>1} catalogue={catalogue} audiences={audiences} planId={planId} showAgencyFee={isAgency} sitesMap={sitesMap} siteOverlaps={siteOverlaps} trackersCatalogue={trackersCatalogue} trackerEligibility={trackerEligibility}/>
            ))}
          </div>))}
        </div>
      </div>}

      {/* RIGHT PANEL */}
      <div className="dpe-right">
        {/* KPI bar above tabs */}
        <div style={{display:'flex',gap:6,padding:'6px 16px',borderBottom:'1px solid #dce8f0',background:'rgba(255,255,255,0.7)',flexWrap:'wrap',alignItems:'center'}}>
          {[{l:'Budget',v:fe(planTot.budget)},{l:'Impressions',v:fmil(planTot.impressions)},{l:'vImpressions',v:fmil(planTot.vimpressions)},{l:'vRCH%',v:fp(planTot.reach),hi:true},{l:'Views',v:fmil(planTot.views)},{l:'Clicks',v:fmil(planTot.clicks)}].map(k=>(
            <div key={k.l} className={'kpi-chip'+(k.hi?' kpi-hi':'')}><span className="kpi-label">{k.l}</span><span className="kpi-val">{k.v}</span></div>
          ))}
        </div>
        <div className="dpe-tabs" style={{display:'flex',alignItems:'center'}}>
          {[{t:'flowchart',Icon:BarChart2,l:'Flowchart'},{t:'summary',Icon:AlignLeft,l:'Summary'},{t:'details',Icon:Table2,l:'Details'},{t:'actuals',Icon:Activity,l:'Actuals'}]
            .filter(({t})=>!aggregateMode||t!=='details')
            .map(({t,Icon:Ic,l})=>(
            <button key={t} className={'tab-btn'+(rightTab===t?' on':'')} onClick={()=>setRightTab(t)} style={{display:'inline-flex',alignItems:'center',gap:5}}><Ic size={13}/>{l}</button>
          ))}
          <div style={{flex:1}}/>
          {rightTab==='flowchart'&&planYears.length>1&&(()=>{
            const yi=planYears.indexOf(displayYear);
            const atFirst=yi<=0,atLast=yi>=planYears.length-1;
            return(
              <div style={{display:'flex',alignItems:'center',gap:4,marginRight:8}} title="Flowchart grid shows one calendar year at a time — this plan has flights spanning multiple years">
                <button onClick={()=>!atFirst&&setDisplayYear(planYears[yi-1])} disabled={atFirst} style={{width:22,height:22,border:'1px solid var(--border)',borderRadius:4,background:'transparent',cursor:atFirst?'default':'pointer',fontSize:12,color:atFirst?'var(--border)':'var(--text3)',lineHeight:1,padding:0}}>‹</button>
                <span style={{fontSize:11,fontWeight:700,color:'var(--gold-dark)',minWidth:36,textAlign:'center'}}>{displayYear}</span>
                <button onClick={()=>!atLast&&setDisplayYear(planYears[yi+1])} disabled={atLast} style={{width:22,height:22,border:'1px solid var(--border)',borderRadius:4,background:'transparent',cursor:atLast?'default':'pointer',fontSize:12,color:atLast?'var(--border)':'var(--text3)',lineHeight:1,padding:0}}>›</button>
              </div>
            );
          })()}
          <div style={{display:'flex',alignItems:'center',gap:4,marginRight:8}}>
            <button onClick={()=>setTabZoom(z=>Math.max(50,z-25))} style={{width:22,height:22,border:'1px solid var(--border)',borderRadius:4,background:'transparent',cursor:'pointer',fontSize:14,color:'var(--text3)',lineHeight:1,padding:0}}>−</button>
            <span style={{fontSize:11,fontWeight:700,color:tabZoom!==100?'var(--gold-dark)':'var(--text3)',minWidth:36,textAlign:'center'}}>{tabZoom}%</span>
            <button onClick={()=>setTabZoom(z=>Math.min(200,z+25))} style={{width:22,height:22,border:'1px solid var(--border)',borderRadius:4,background:'transparent',cursor:'pointer',fontSize:14,color:'var(--text3)',lineHeight:1,padding:0}}>+</button>
            <button onClick={()=>setTabZoom(100)} title="Reset to 100%" style={{width:22,height:22,border:'1px solid var(--border)',borderRadius:4,background:'transparent',cursor:'pointer',fontSize:10,color:tabZoom!==100?'var(--gold-dark)':'var(--text3)',lineHeight:1,padding:0,fontWeight:700}}>⌂</button>
          </div>
        </div>
        <div style={{flex:1,overflowY:'auto',overflowX:'auto'}}>
          <div style={{zoom:`${tabZoom}%`}}>
            {rightTab==='flowchart'&&<AnnualFlowchart flights={flights} allResults={allResults} displayYear={displayYear}/>}
            {rightTab==='summary'&&<SummaryTab flights={flights} allResults={allResults} onExportExcel={handleExportSummaryExcel}/>}
            {rightTab==='details'&&<DetailsTab flights={flights} allResults={allResults} catalogue={catalogue} onExportExcel={handleExportExcel} displayYear={displayYear}/>}
            {rightTab==='actuals'&&<ActualsTab flights={flights} allResults={allResults} actualsData={actualsData} setActualsData={setActualsData} catalogue={catalogue}/>}
          </div>
        </div>
      </div>
    </div>

    {audMgrOpen&&<AudienceManager audiences={audiences} setAudiences={setAudiences} catalogue={catalogue} onClose={()=>setAudMgrOpen(false)} clientId={clientId}/>}

    {showWizard&&(
      <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.7)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={e=>{if(e.target===e.currentTarget)setShowWizard(false);}}>
        <div style={{background:'var(--surface)',border:'1px solid var(--gold-border)',borderRadius:12,padding:28,width:480,maxWidth:'92vw'}}>
          <div style={{fontWeight:700,fontSize:15,color:'var(--gold)',marginBottom:4,display:'flex',alignItems:'center',gap:6}}><Zap size={14}/>Quick Setup</div>
          <div style={{fontSize:11,color:'var(--text3)',marginBottom:20}}>Auto-generate brands & flights. Existing flights will be replaced.</div>
          <DigitalQuickWizard catalogue={catalogue} sitesMap={sitesMap} onDone={(f)=>{setFlights(f);setShowWizard(false);setSaved(false);}} onCancel={()=>setShowWizard(false)}/>
        </div>
      </div>
    )}
  </div>);
}
